import { LaborCrewStepper, laborOf } from "@/components/labor-crew-stepper";
import { RemovedChoicesWarning } from "@/components/removed-choices-warning";
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { formatAssets } from "@/lib/vocabulary";
import { recordLabel } from "@/lib/human-labels";
import { humanFieldName } from "@/lib/human-labels";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { photoCaption } from "@/lib/photo-label";
import { useWoRegenerate } from "@/lib/use-wo-regenerate";
import { usePageContextReporter } from "@/lib/page-context";
import { ImageLightbox } from "@/components/image-lightbox";
import {
    ChargeCardPanel,
    chargeCardMode,
    type ChargeCardCustomer,
    type ChargeCardInvoice,
} from "@/components/charge-card-panel";
import { JobTaskCornerTab, JobTaskFlagProvider, useJobTaskFlag } from "@/components/job-task-flag";
import { cn, statusColor, formatCurrency } from "@/lib/utils";
import { useTableControls } from "@/lib/use-table-controls";
import { SortHeaderButton } from "@/components/ui/sort-header-button";
import { stripHtml } from "@/lib/strip-html";
import { useUserPreference } from "@/lib/user-preference";
import {
    CheckCircle,
    AlertTriangle,
    Camera,
    FileText,
    ArrowRight,
    Receipt,
    Send,
    ClipboardCheck,
    Loader2,
    Mail,
    DollarSign,
    FileEdit,
    Eye,
    Pencil,
    Trash2,
    Check,
    X,
    Flag,
    Layers,
    PauseCircle,
    RotateCcw,
    MessageSquarePlus,
    ShieldCheck,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    List,
    MoreHorizontal,
    Package,
    Printer,
    Paperclip,
    Wrench,
    MapPin,
    Ban,
    RefreshCw,
    ExternalLink,
    FileCheck,
    CreditCard,
} from "lucide-react";
import { DocumentPreviewDialog } from "@/components/document-preview-dialog";
import { AttachmentImage } from "@/components/attachment-image";
import { AttachmentPreviewDialog } from "@/components/attachment-preview-dialog";
import {
    SendRecipientEditor,
    docsSummary,
    DOC_LABEL,
    type PlanRecipient,
    type PacketDoc,
} from "@/components/send-recipient-editor";
import { DroppedAttachmentsWarning, type DroppedAttachment } from "@/components/dropped-attachments-warning";
import { PacketSizeNotice, type PacketSizePlan } from "@/components/packet-size-notice";
import {
    StaffOnlySendGate,
    noCustomerEmailRefusal,
    type NoCustomerEmailRefusal,
} from "@/components/staff-only-send-gate";
import { SentAnotherWayDialog } from "@/components/sent-another-way-dialog";
import { AttachJobFilesPicker } from "@/components/attach-job-files-picker";
import { SendCustomerDialog } from "@/components/send-customer-dialog";
import { CancelJobDialog } from "@/components/cancel-job-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Dialog } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "@/components/ui/app-dialogs";
import { useWorkflow } from "@/lib/use-workflow";
import { RemovalReviewCard } from "./removal-review-card";
import { OfficeRepairsSection, type ReviewRepairRow } from "./office-repairs-section";
import { UnconfirmedDevicesCard } from "./unconfirmed-devices-card";
import { PartsRiskCard } from "./parts-risk-card";
import { PhotoMatchCard } from "./photo-match-card";
import { useAuth } from "@/lib/auth";
import { packetBlock, usePacketReadiness } from "@/lib/use-packet-readiness";
import { PacketNotReadyNotice } from "@/components/packet-not-ready-notice";
import { formatQueuedAt } from "@/components/resend-packet-dialog";
import { PermitCard } from "@/pages/jobs/detail/permit-card";
import { conversionToast, type ConversionScope } from "@/lib/estimate-conversion";
import { testStateLabel, testStateTone } from "@/lib/test-state";

type Tab = "all" | "invoices" | "estimates" | "issues" | "pricing";

/** Variables for the approve-and-send mutation. Kept at module scope so the
 * CE-1 drop gate can stash and replay the exact send (with the confirm flag). */
interface ApproveAndSendVars {
    jobId: string;
    flags: { photos: boolean; reports: boolean; invoices: boolean; estimates: boolean };
    attachmentIds?: string[];
    /** Auto-riding report attachments the office UNCHECKED (left off the send). */
    excludedAttachmentIds?: string[];
    plan?: Array<{
        value: string;
        source: string;
        contactMethodId?: string | null;
        contactName?: string | null;
        docs: PacketDoc[];
        remember?: boolean;
    }>;
    resend: boolean;
    /** CE-1: set only after the operator confirms the drop warning. */
    confirmDroppedAttachments?: boolean;
    /** Set only after the office ticks "Send to staff only (internal copy)". */
    staffOnlyConfirmed?: boolean;
}

const VALID_TABS: Tab[] = ["all", "invoices", "estimates", "issues", "pricing"];

function parseTab(raw: string | null): Tab {
    return VALID_TABS.includes(raw as Tab) ? (raw as Tab) : "all";
}

// ── Layout tiers ──────────────────────────────────────────────────────
// Measured with a ResizeObserver (not media queries) so browser zoom and
// split windows count: a 1440px window at 125% zoom is ~1152 effective px
// and correctly lands in the medium tier.
//   wide   ≥ 1400px  — queue | context | billing, three panes
//   medium ≥ 1100px  — billing drops below the detail content, full width
//   narrow <  1100px — queue collapses into a slide-over drawer + pager
// We observe the page root's PARENT (the shell content column) because the
// root itself gets full-bleed negative margins at the smaller tiers —
// observing the root would oscillate at a tier boundary.
type LayoutTier = "wide" | "medium" | "narrow";

function tierForWidth(w: number): LayoutTier {
    if (w >= 1400) return "wide";
    if (w >= 1100) return "medium";
    return "narrow";
}

function useLayoutTier(rootRef: RefObject<HTMLDivElement | null>): LayoutTier {
    const [tier, setTier] = useState<LayoutTier>(() =>
        tierForWidth(typeof window === "undefined" ? 1920 : window.innerWidth),
    );
    useEffect(() => {
        const el = rootRef.current?.parentElement;
        if (!el) return;
        const apply = () => setTier(tierForWidth(el.offsetWidth));
        const ro = new ResizeObserver(apply);
        ro.observe(el);
        apply();
        return () => ro.disconnect();
    }, [rootRef]);
    return tier;
}

interface QueueJob {
    id: string;
    title: string;
    status: string;
    kind: string | null;
    company_name: string;
    /** TAX ENGINE V1: true when the customer's tax status is 'unknown'. */
    tax_status_unknown?: boolean;
    /** Charge-card queue awareness: saved card + an open invoice to charge. */
    has_card_on_file?: boolean;
    has_open_invoice?: boolean;
    site_name: string;
    site_address: string;
    trade_name: string;
    tech_name: string;
    job_type_name: string;
    updated_at: string;
    completed_at: string | null;
    test_count: number;
    fail_count: number;
    photo_count: number;
    draft_invoice_id: string | null;
    draft_estimate_id: string | null;
    draft_invoice_total: string | null;
    asset_count: number;
    asset_types: string | null;
    review_priority: number;
    review_reasons: string[];
    confidence: "high" | "medium" | "low";
    seconds_since_completion: number | null;
    // #832 C4: days since the OLDEST open office_review_items row was filed —
    // null when the job has no open items (derived-only reasons carry the
    // job's own age instead).
    oldest_flag_days: number | null;
    review_hold_note: string | null;
    return_note: string | null;
    mapping_warning_count: number;
    mapping_warning_attachment_id: string | null;
    pricing_stale?: boolean;
}

interface ReviewReplacementRow {
    id: string;
    job_id: string;
    old_asset_id: string;
    new_asset_id: string | null;
    old_serial: string | null;
    old_model: string | null;
    new_serial: string | null;
    new_model: string | null;
    pair_role: "main" | "bypass";
    parent_old_asset_id: string | null;
    prework_photos: string[] | null;
    prework_notes: string | null;
    old_bypass_missing: boolean;
    replacement_date: string | null;
    finalized_at: string | null;
    performed_by: string | null;
    performed_by_name: string | null;
    created_at: string;
    updated_at: string;
    old_name: string | null;
    old_assembly_type: string | null;
    new_name: string | null;
    new_assembly_type: string | null;
}

interface TestResult {
    id: string;
    asset_id: string;
    asset_name: string;
    serial_number: string;
    manufacturer: string;
    model: string;
    size_label: string;
    assembly_type: string;
    service_type: string | null;
    overall_result: string;
    recommended_action: string | null;
    failure_reasons: string[] | null;
    performed_at: string;
    visual_condition_notes?: string | null;
    failure_notes?: string | null;
    completion_notes?: string | null;
    remarks?: string | null;
    on_non_potable_supply?: boolean | null;
    /** Helper Mode v2 both-name attribution: who typed the readings and who
     *  reviewed & signed. entered_by is null/absent when the tech entered
     *  the row themselves. */
    entered_by_user_id?: string | null;
    entered_by_name?: string | null;
    reviewed_by_name?: string | null;
    /** Orphan-test resolution (Issue #304): set on rows whose device is no
     *  longer linked to the job. 'relink' = a real on-site device whose link
     *  vanished (restore it); 'void' = a garbage/mis-scanned stray (delete it);
     *  'other_site' = a finished test for a device that lives at another
     *  site (it never holds this job's paperwork; the row links to that site);
     *  'keep'/null = leave it informational. Absent on fire-safety rows. */
    orphanAction?: "relink" | "void" | "other_site" | "keep" | null;
    /** Set when the tested device belongs to a different site than the job. */
    other_site_id?: string | null;
    other_site_name?: string | null;
}

interface DocLine {
    id: string;
    description: string;
    qty: number;
    unit_price: number;
    line_total: number;
    pricebook_item_name?: string | null;
    /** Labor line hours × techs × rate (#1823 S3); null on other lines. */
    labor_hours?: number | string | null;
    labor_techs?: number | null;
    labor_rate?: number | string | null;
}

interface Invoice {
    id: string;
    invoice_number: string | null;
    title: string;
    status: string;
    total_amount: number;
    subtotal: number;
    paid_amount?: string | null;
    last_stripe_payment_ref?: string | null;
    lines: DocLine[] | null;
}

interface Estimate {
    id: string;
    title: string;
    status: string;
    subtotal: number;
    lines: DocLine[] | null;
}

/** Adapt a review-screen invoice / job row for the charge-card panel. */
function toChargeInvoice(i: Invoice): ChargeCardInvoice {
    return {
        id: i.id,
        invoiceNumber: i.invoice_number,
        status: i.status,
        total: Number(i.total_amount ?? 0),
        paid: Number(i.paid_amount ?? 0),
        lastStripeRef: i.last_stripe_payment_ref ?? null,
    };
}

function toChargeCustomer(job: Record<string, any> | null | undefined): ChargeCardCustomer {
    return {
        customerId: job?.customer_id ?? null,
        customerName: job?.company_name ?? null,
        hasCard: !!job?.has_card_on_file,
        brand: job?.card_brand ?? null,
        last4: job?.card_last4 ?? null,
        paymentProfile: job?.payment_profile ?? null,
    };
}

interface Asset {
    id: string;
    serial_number: string | null;
    manufacturer: string | null;
    model: string | null;
    size_label: string | null;
    assembly_type: string | null;
    service_type: string | null;
    name: string | null;
    required_action: string | null;
    sequence_no: number | null;
    link_id: string | null;
    added_by: "office" | "tech" | null;
    gate_required: boolean | null;
    office_review_decision: "pending" | "approved" | "denied" | null;
    decision_note: string | null;
    decision_at: string | null;
    disposition: string | null;
    disposition_reason: string | null;
    test_id: string | null;
    overall_result: string | null;
}

/**
 * Trimmed view of `/backflow/job/:jobId/devices` for the office review
 * "Devices Not Tested" section. Mirrors the field-side `Device` shape
 * but only the columns we render here.
 */
interface ReviewDeviceRow {
    id: string;
    name: string | null;
    serial_number: string | null;
    assembly_type: string | null;
    parent_asset_id: string | null;
    status: string | null;
    job_link_id: string | null;
    added_by: "office" | "tech" | null;
    gate_required: boolean | null;
    disposition: "skipped" | "not_due" | "not_located" | "could_not_test" | null;
    disposition_reason: string | null;
    /** Capture-time reason chip (2026-07-14). device_gone → removal review. */
    disposition_category:
        | "no_access"
        | "manager_unavailable"
        | "water_shutoff"
        | "no_water"
        | "unsafe"
        | "device_gone"
        | "ran_out_of_time"
        | "other"
        | null;
    disposition_set_at: string | null;
    disposition_set_by_name: string | null;
    /** Set while the asset awaits office confirmation that it's gone. */
    removal_review_requested_at: string | null;
    is_in_scope: boolean;
    last_test: { id: string; performed_at: string; overall_result: string } | null;
}

interface TestPhoto {
    assetId: string;
    serialNumber: string;
    slot: string;
    dataUrl: string;
}

interface ReportAttachment {
    id: string;
    file_name: string;
    file_key: string;
    mime_type: string;
    category: "report" | "summary";
    mapping_warnings: {
        missingRequired: string[];
        fieldsFilled: number;
        fieldsTotal: number;
        templateId: string;
        dataWarnings?: string[];
    } | null;
    created_at: string;
}

interface JobDocument {
    id: string;
    file_name: string;
    file_key: string;
    mime_type: string;
    category: string;
    photo_tag: string | null;
    created_at: string;
    /** Closeout WO slot (PR-1): view-then-confirm review gate + upload rows. */
    notes?: string | null;
    reviewed_at?: string | null;
    reviewed_by_name?: string | null;
    uploaded_by_name?: string | null;
}

interface PricingDiffLine {
    lineId: string;
    description: string;
    storedUnitPrice: number;
    currentUnitPrice: number;
    anchorUnitPrice: number;
    delta: number;
    pricebookItemId: string | null;
}

interface Submission {
    id: string;
    asset_id: string | null;
    status: string;
    portal_confirmation: string | null;
    submitted_at: string | null;
    submitted_by: string | null;
    submitted_by_name: string | null;
    rejection_reason: string | null;
    notes: string | null;
    asset_name: string | null;
    serial_number: string | null;
    assembly_type: string | null;
    provider_name: string | null;
    submission_platform: string | null;
    portal_url: string | null;
    is_portal: boolean | null;
}

interface PointsEarnedLine {
    id: string;
    taskKey: string;
    displayLabel: string | null;
    quantity: number;
    points: number;
    status: string;
    assetId: string | null;
    serialNumber: string | null;
    userId: string;
    techName: string | null;
    createdAt: string;
}

interface PointsGap {
    kind: "test" | "repair" | "replace" | "invoice_flat";
    assetId: string | null;
    serialNumber: string | null;
    deviceLabel: string | null;
    suggestedTaskKey: string | null;
    suggestedPoints: number | null;
    suggestedUserId: string | null;
    suggestedUserName: string | null;
    message: string;
}

interface PointsReconciliation {
    earned: PointsEarnedLine[];
    gaps: PointsGap[];
    totalPoints: number;
}

interface ReviewDetail {
    job: Record<string, any>;
    testResults: TestResult[];
    photos: any[];
    testPhotos: TestPhoto[];
    estimates: Estimate[];
    invoices: Invoice[];
    assets: Asset[];
    reportAttachments?: ReportAttachment[];
    documents?: JobDocument[];
    submissions?: Submission[];
    cityEmailDraft?: { recipient: string | null; subject: string; message: string };
    // Choices dropped from this job's draft quotes; Refresh pricing brings them back.
    removedChoices?: string[];
    reviewItems?: ReviewItem[];
    workOrderCompletion?: WorkOrderCompletion | null;
    workOrderAttachments?: JobDocument[];
    // Intake item that created this job — powers "Fix template mapping".
    workOrderIntakeItemId?: string | null;
    pointsReconciliation?: PointsReconciliation | null;
    pricing_anchor_date?: string | null;
    pricing_stale?: boolean;
    pricing_diff?: PricingDiffLine[];
}

interface ReviewItem {
    id: string;
    title: string;
    notes: string | null;
    status: string;
    created_at: string;
}

interface WorkOrderCompletion {
    id: string;
    completed_at: string | null;
    time_in: string | null;
    time_out: string | null;
    tech_count: number | null;
    work_performed: string | null;
    deficiencies_found: boolean | null;
    deficiency_description: string | null;
    confirmation_number: string | null;
    customer_fields: Record<string, unknown> | null;
    /** Tech skipped the customer acknowledgement — the WO paper is
     *  intentionally unsigned; this is why (e.g. "No one on site"). */
    ack_skipped_reason: string | null;
    delivered_at: string | null;
    delivered_to: string | null;
    submitted_at: string | null;
    /** Helper Mode v2 both-name attribution. */
    entered_by_user_id?: string | null;
    entered_by_name?: string | null;
    reviewed_by_name?: string | null;
}

const REPAIR_GAP_REVIEW_PREFIX = "Repair coverage gap: ";

/**
 * A repair estimate is the auto-created `Repair Estimate — …` document
 * generated from failed devices on job completion. We detect it by title so
 * the office-review workflow can guide the user through send → approve →
 * schedule without changing any backend shape.
 */
function isRepairEstimate(est: { title?: string | null }): boolean {
    return !!est.title?.toLowerCase().includes("repair");
}

const CONFIDENCE_STYLES: Record<string, { bg: string; text: string; label: string; gloss: string }> = {
    high: {
        bg: "bg-emerald-50 dark:bg-emerald-900/20",
        text: "text-emerald-700 dark:text-emerald-300",
        label: "High Confidence",
        gloss: "High Confidence — automated checks found nothing wrong; a quick skim should be enough.",
    },
    medium: {
        bg: "bg-amber-50 dark:bg-amber-900/20",
        text: "text-amber-700 dark:text-amber-300",
        label: "Review Needed",
        gloss: "Review Needed — something on this job needs a human look before it goes out.",
    },
    low: {
        bg: "bg-red-50 dark:bg-red-900/20",
        text: "text-red-700 dark:text-red-300",
        label: "Requires Attention",
        gloss: "Requires Attention — blocking problems were found; read the reason pills before approving.",
    },
};

const REASON_LABELS: Record<string, string> = {
    failed_test: "Failed Test",
    cannot_complete: "Cannot Complete",
    missing_photos: "Missing Photos",
    pricing_uncertainty: "Pricing Issue",
    pricing_stale: "Pricing Changed",
    on_hold: "On Hold",
    ready_for_invoicing: "Ready for Invoice",
    missing_pricebook: "Missing Pricebook Item",
    repair_needed: "Repair Needed",
    repair_coverage_gap: "Repair Coverage Gap",
    workflow_flag: "Needs Review",
    missing_form_fields: "Missing Form Fields",
    assignment_approval: "Assignment Approval",
    wo_delivery_failed: "WO Delivery Failed",
    wo_no_return_pdf: "No Return PDF",
};

// H13: per-reason "what to verify" guidance for the sticky review checklist.
// Keyed by review_reason so the checklist tells an untrained office user
// exactly what to look at before approving — the reasons no longer vanish the
// moment the job is opened.
const REASON_GUIDANCE: Record<string, string> = {
    failed_test:
        "A device failed — a repair estimate should exist below; it sends alongside the invoice when you approve.",
    cannot_complete: "Tech couldn't finish — read the reason and decide whether to reschedule, hold, or cancel.",
    missing_photos: "Required photos are missing — check the photo section or return the job to the tech.",
    pricing_uncertainty: "Pricing needs a human check — verify the invoice line items and totals are correct.",
    pricing_stale: "Pricebook prices changed since this was priced — re-check the invoice total.",
    on_hold: "Job is on hold — review the hold note and clear it before approving.",
    missing_pricebook: "A line item isn't mapped to the pricebook — map it so the invoice is accurate.",
    repair_needed:
        "Repair work is needed — confirm the repair estimate line items are right; the repair is scheduled as its own job once the customer approves.",
    repair_coverage_gap: "Failed device has no repair estimate — create one so the repair isn't missed.",
    workflow_flag: "Flagged for manual review — check the job notes for what needs attention.",
    missing_form_fields: "The test form has missing/odd fields — verify the report before sending.",
    assignment_approval: "The tech who did this work needs assignment approval — approve it above.",
    wo_delivery_failed: "The completed work-order PDF failed to email the vendor — retry the send below.",
    wo_no_return_pdf: "No return PDF exists — the vendor's template isn't mapped. Map it, then send manually.",
};

/**
 * Pill color classes per review reason — the single source for both the queue
 * pills and the legend, so the two can never drift apart. Reasons without an
 * entry render as a plain outline badge.
 */
function reasonPillClass(r: string): string {
    return cn(
        (r === "failed_test" || r === "cannot_complete" || r === "wo_delivery_failed") &&
            "border-red-300 text-red-600 dark:border-red-700 dark:text-red-400",
        (r === "missing_photos" ||
            r === "missing_pricebook" ||
            r === "missing_form_fields" ||
            r === "wo_no_return_pdf") &&
            "border-amber-300 text-amber-600 dark:border-amber-700 dark:text-amber-400",
        r === "on_hold" && "border-purple-300 text-purple-600 dark:border-purple-700 dark:text-purple-400",
        (r === "repair_needed" || r === "workflow_flag") &&
            "border-orange-300 text-orange-600 dark:border-orange-700 dark:text-orange-400",
        r === "repair_coverage_gap" &&
            "border-rose-400 text-rose-700 bg-rose-50 font-semibold dark:border-rose-600 dark:text-rose-300 dark:bg-rose-900/20",
        r === "assignment_approval" && "border-blue-300 text-blue-600 dark:border-blue-700 dark:text-blue-400",
    );
}

/**
 * M8 (#834): pill legend for the queue — Closeout next door has a color key;
 * this queue stacks 8+ pill types with none. Every chip is rendered with the
 * exact classes the queue pills use, and hovering a chip gives the reason's
 * plain-English guidance sentence. Collapsible, choice follows the user
 * (server-backed preference, same pattern as the dispatch grid legend).
 */
function PillLegend() {
    const [collapsed, setCollapsed] = useUserPreference<boolean>("office_review_legend_collapsed", false);
    return (
        <div className="border-b border-border/60 bg-secondary/30 px-4 py-2" data-testid="pill-legend">
            <button
                type="button"
                onClick={() => setCollapsed(!collapsed)}
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[.1em] text-foreground/50 hover:text-foreground/80"
                aria-expanded={!collapsed}
            >
                Pill legend
                {collapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
            </button>
            {!collapsed && (
                <>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1">
                        {Object.entries(REASON_LABELS).map(([r, label]) => (
                            <Badge
                                key={r}
                                variant="outline"
                                className={cn("cursor-help text-[9px] px-1.5 py-0", reasonPillClass(r))}
                                title={REASON_GUIDANCE[r]}
                            >
                                {label}
                            </Badge>
                        ))}
                    </div>
                    <p className="mt-1 text-[10px] leading-snug text-foreground/60">
                        Hover a pill for what it means and what to check. Red = failed or undeliverable, amber =
                        something's missing, orange = needs a human decision, rose = a failed device with no repair
                        estimate yet, blue = a tech assignment awaiting approval.
                    </p>
                </>
            )}
        </div>
    );
}

function timeSinceLabel(seconds: number | null): string | null {
    if (!seconds || seconds < 3600) return null;
    const hrs = Math.round(seconds / 3600);
    if (hrs > 48) return `${Math.round(hrs / 24)}d ago`;
    return `${hrs}h ago`;
}

/**
 * Bug 4 / Prompt 3: pre-send summary. One row per EMAIL that will go out —
 * "1 email to alice@… (results + invoice + photos)" — so the office confirms
 * the exact consolidation before anything ships. Legacy (unverified)
 * addresses are flagged in amber.
 */
function SendConfirmSummary({
    snapshot,
    isPending,
    onBack,
    onConfirm,
}: {
    snapshot: PlanRecipient[] | null;
    isPending: boolean;
    onBack: () => void;
    onConfirm: () => void;
}) {
    const recipients = (snapshot ?? []).filter((r) => r.docs.length > 0);
    const hasLegacy = recipients.some((r) => r.tier === "legacy");

    return (
        <>
            <div className="space-y-3">
                <p className="text-sm font-medium">
                    {recipients.length} email{recipients.length === 1 ? "" : "s"} will be sent:
                </p>
                {recipients.length === 0 ? (
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                        No recipients selected — go back and add at least one.
                    </p>
                ) : (
                    <ul className="space-y-1.5">
                        {recipients.map((r) => (
                            <li
                                key={r.value}
                                className={cn(
                                    "rounded-md border px-3 py-2 text-sm",
                                    r.tier === "legacy"
                                        ? "border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10"
                                        : "border-border/60 bg-muted/30",
                                )}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="break-all font-medium">{r.value}</span>
                                    {r.contactName ? (
                                        <span className="text-xs text-foreground/60 whitespace-nowrap">
                                            ({r.contactName})
                                        </span>
                                    ) : null}
                                    {r.tier === "legacy" ? (
                                        <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300">
                                            <AlertTriangle className="h-3 w-3" /> Legacy · unverified
                                        </span>
                                    ) : null}
                                </div>
                                <div className="text-xs text-foreground/60">One email — {docsSummary(r.docs)}</div>
                                {r.remember ? (
                                    <div className="text-[11px] text-muted-foreground">
                                        Routing will be remembered for future sends.
                                    </div>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                )}
                {hasLegacy ? (
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                        One or more recipients is an unverified Legacy address — confirm it&apos;s correct before
                        sending.
                    </p>
                ) : null}
            </div>
            <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={onBack} disabled={isPending}>
                    Back
                </Button>
                <Button
                    className="bg-green-700 hover:bg-green-800 text-white"
                    disabled={isPending || recipients.length === 0}
                    onClick={onConfirm}
                >
                    {isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                    Confirm &amp; Send
                </Button>
            </div>
        </>
    );
}

export default function OfficeReviewPage() {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    useWorkflow();
    // Selection + tab live in the URL so navigating to a job and pressing
    // Back restores the same review workspace (local state is lost on unmount).
    const tab = parseTab(searchParams.get("tab"));
    const selectedJobId = searchParams.get("job");
    // On phones the detail pane sits below the queue; scroll it into view when a
    // job is tapped so the user isn't left staring at the (now-updated) list.
    const detailRef = useRef<HTMLDivElement>(null);
    // Measured layout tier (wide / medium / narrow) — see useLayoutTier.
    const pageRootRef = useRef<HTMLDivElement>(null);
    const tier = useLayoutTier(pageRootRef);
    // Narrow tier: the queue list lives in a slide-over drawer.
    const [queueDrawerOpen, setQueueDrawerOpen] = useState(false);
    const setSelectedJobId = (id: string | null) => {
        // Selection survives ANY navigation away and back (View Job, the job
        // page, sidebar links) — not just browser Back: mirrored to
        // sessionStorage and restored on mount when the URL has no ?job.
        if (id) sessionStorage.setItem("office-review:last-job", id);
        else sessionStorage.removeItem("office-review:last-job");
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (id) next.set("job", id);
                else next.delete("job");
                return next;
            },
            { replace: true },
        );
        if (id && window.matchMedia("(max-width: 767px)").matches) {
            requestAnimationFrame(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
        }
    };
    const setTab = (nextTab: Tab) =>
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set("tab", nextTab);
                next.delete("job");
                return next;
            },
            { replace: true },
        );
    const [pricingDismissed, setPricingDismissed] = useState(false);
    const [checklistCollapsed, setChecklistCollapsed] = useState(false);
    const [noteDialog, setNoteDialog] = useState<{ action: string; jobId: string } | null>(null);
    const [noteText, setNoteText] = useState("");
    const [showSendModal, setShowSendModal] = useState(false);
    // Per-doc compose/preview dialog (recipients + editable subject/body),
    // opened from the invoice/estimate Send buttons in the workspace.
    const [composeSend, setComposeSend] = useState<{
        docType: "invoice" | "estimate";
        id: string;
        customerId: string | null;
    } | null>(null);
    const [previewAttachment, setPreviewAttachment] = useState<{
        id: string;
        fileName: string;
        allowDownload?: boolean;
    } | null>(null);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [showRebuildPricingConfirm, setShowRebuildPricingConfirm] = useState(false);
    const [grantStandingApproval, setGrantStandingApproval] = useState(true);
    const [sendAttachments, setSendAttachments] = useState<{
        photos: boolean;
        reports: boolean;
        invoices: boolean;
        estimates: boolean;
    }>({
        photos: true,
        reports: true,
        invoices: true,
        estimates: true,
    });
    // CE-2: job files the office EXPLICITLY checked in the "Attach job files"
    // picker. An explicit selection overrides category defaults server-side
    // ('document'/'summary' included; signatures never), so — unlike the old
    // H15 upload affordance — these files genuinely ride on the customer
    // email. The ids feed the CE-1 send-preview drop query and the
    // approve-and-send payload.
    const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
    const [excludedFileIds, setExcludedFileIds] = useState<string[]>([]);
    // Prompt 3: latest recipient-centric send plan from the matrix editor.
    // Held in a ref so the editor can update it without re-rendering the
    // (large) office-review tree; read at confirm time.
    const planRef = useRef<PlanRecipient[] | null>(null);
    const handlePlanChange = useCallback((p: PlanRecipient[]) => {
        planRef.current = p;
    }, []);
    // Hard rule: documents in play with no recipient chip anywhere. Send is
    // blocked until this list is empty.
    const [missingDocs, setMissingDocs] = useState<PacketDoc[]>([]);
    const handleMissingDocsChange = useCallback((missing: PacketDoc[]) => {
        setMissingDocs(missing);
    }, []);
    // Bug 4: two-step send — "configure" (pick docs/recipients) then "confirm"
    // (pre-send summary of exactly which emails go out). The snapshot freezes
    // the plan shown on the confirm screen.
    const [sendStep, setSendStep] = useState<"configure" | "confirm">("configure");
    const [sendSnapshot, setSendSnapshot] = useState<PlanRecipient[] | null>(null);
    // CE-1: the drop gate. Non-null when selected files won't ride on the
    // customer email — set proactively from the send-preview drop list before
    // the send, and defensively from a 409 DROPPED_ATTACHMENTS_UNCONFIRMED. It
    // stashes the exact send so "Send anyway" can replay it with the confirm
    // flag. Rendered as its own dialog so both entry points look identical.
    const [dropGate, setDropGate] = useState<{ dropped: DroppedAttachment[]; vars: ApproveAndSendVars } | null>(null);
    const [staffOnlyGate, setStaffOnlyGate] = useState<{
        refusal: NoCustomerEmailRefusal;
        vars: ApproveAndSendVars;
    } | null>(null);
    // "Already sent another way" (2026-10-07), opened from the gate above.
    const [otherWayJobId, setOtherWayJobId] = useState<string | null>(null);

    const queue = useQuery({
        queryKey: ["office-review-queue", tab],
        queryFn: () => api.get<{ jobs: QueueJob[] }>(`/api/office-review/queue?tab=${tab}`),
    });

    const detail = useQuery({
        queryKey: ["office-review-detail", selectedJobId],
        queryFn: () => api.get<ReviewDetail>(`/api/office-review/jobs/${selectedJobId}`),
        enabled: !!selectedJobId,
    });

    // CE-1: ask the send preview which SELECTED attachments won't ride, so the
    // modal can warn BEFORE the send. Only runs while the modal is open and the
    // operator has actually selected ad-hoc files — with none selected there is
    // nothing that could drop. The 409 on approve-and-send is the backstop.
    const selectedAttachmentIds = selectedFileIds;
    const dropPreview = useQuery({
        queryKey: ["send-preview-drops", selectedJobId, selectedAttachmentIds],
        queryFn: () => {
            const qs = selectedAttachmentIds.map((id) => `attachmentIds=${encodeURIComponent(id)}`).join("&");
            return api.get<{ droppedAttachments?: DroppedAttachment[] }>(
                `/api/office-review/jobs/${selectedJobId}/send-preview?${qs}`,
            );
        },
        enabled: !!selectedJobId && showSendModal && selectedAttachmentIds.length > 0,
    });
    const previewDropped: DroppedAttachment[] = dropPreview.data?.droppedAttachments ?? [];
    // Reports as a link when the packet is too big; blank forms / city copies
    // left out (packetSizePlan.ts). One plain line each, nothing to click.
    const sizePlan = useQuery({
        queryKey: ["office-review-send-size", selectedJobId, selectedAttachmentIds, excludedFileIds],
        queryFn: () => {
            const qs = new URLSearchParams();
            if (selectedAttachmentIds.length > 0) qs.set("attachmentIds", selectedAttachmentIds.join(","));
            if (excludedFileIds.length > 0) qs.set("excluded", excludedFileIds.join(","));
            return api.get<PacketSizePlan>(`/api/close-out/jobs/${selectedJobId}/send-size?${qs.toString()}`);
        },
        enabled: !!selectedJobId && showSendModal,
        staleTime: 60_000,
    });

    // Replacements summary fetch — only fires when a job is selected.
    // Drives the "Replacements" section under Test Results, mirroring the
    // panel on the Job Detail page.
    // Repairs readback — who repaired, who typed it, and the no-photos
    // reason when there is one (mockups/repair-without-photos.html).
    const repairsQuery = useQuery({
        queryKey: ["backflow", "job", selectedJobId, "repairs"],
        queryFn: () => api.get<{ repairs: ReviewRepairRow[] }>(`/api/backflow/job/${selectedJobId}/repairs`),
        enabled: !!selectedJobId,
    });

    const replacementsQuery = useQuery({
        queryKey: ["backflow", "job", selectedJobId, "replacements"],
        queryFn: () =>
            api.get<{ replacements: ReviewReplacementRow[] }>(`/api/backflow/job/${selectedJobId}/replacements`),
        enabled: !!selectedJobId,
    });

    // Devices feed (with origin + disposition columns) — drives the
    // "Devices Not Tested" section: skipped + tech-added-untested rows
    // bubble up here so dispatch can audit / unblock them.
    const devicesQuery = useQuery({
        queryKey: ["backflow", "job", selectedJobId, "devices"],
        queryFn: () => api.get<{ devices: ReviewDeviceRow[] }>(`/api/backflow/job/${selectedJobId}/devices`),
        enabled: !!selectedJobId,
    });

    const invalidateAll = () => {
        qc.invalidateQueries({ queryKey: ["office-review-queue"] });
        qc.invalidateQueries({ queryKey: ["office-review-detail", selectedJobId] });
        qc.invalidateQueries({ queryKey: ["work-pulse"] });
        qc.invalidateQueries({ queryKey: ["dashboard"] });
    };

    // Helper Mode close-out failsafe: approve/approve-and-send return 422
    // HELPER_PRESENCE_REQUIRED when the tech's paired helper never clocked in
    // on the job — this prompt collects the required Yes/No before approval
    // can proceed. `retry` re-fires plain Approve automatically after the
    // answer; Approve & Send asks the office to click again (its flags live
    // in the send modal).
    const [helperPresencePrompt, setHelperPresencePrompt] = useState<{
        jobId: string;
        helpers: Array<{ id: string; name: string }>;
        retry: "approve" | null;
    } | null>(null);

    // Packet send blocked because the customer's tax status is 'unknown'. The
    // server returns 422 { error: "TAX_STATUS_UNKNOWN", customerId }; instead of
    // a raw error toast we surface the same amber one-click prompt the send
    // dialog uses, then re-fire the send with the original flags.
    const [taxStatusPrompt, setTaxStatusPrompt] = useState<{
        customerId: string;
        sendArgs: ApproveAndSendVars;
    } | null>(null);

    const catchTaxStatusUnknown = (e: any, vars: ApproveAndSendVars): boolean => {
        if (e?.status === 422 && e?.body?.error === "TAX_STATUS_UNKNOWN") {
            const cid = typeof e?.body?.customerId === "string" ? e.body.customerId : null;
            if (cid) {
                setTaxStatusPrompt({ customerId: cid, sendArgs: vars });
                return true;
            }
        }
        return false;
    };

    const catchHelperPresence = (e: any, jobId: string, retry: "approve" | null): boolean => {
        if (e?.status === 422 && e?.body?.error === "HELPER_PRESENCE_REQUIRED") {
            setHelperPresencePrompt({
                jobId,
                helpers: Array.isArray(e.body.helpers) ? e.body.helpers : [],
                retry,
            });
            return true;
        }
        return false;
    };

    /** #1250 — a permit answer is outstanding; answered here, not elsewhere. */
    const [permitGate, setPermitGate] = useState<{
        jobId: string;
        message: string;
        /** Re-runs the action the office chose — approve, or approve-and-send. */
        retry: () => void;
    } | null>(null);
    // Read-only for anyone the server would 403 rather than inputs that fail.
    const { can: canDo } = useAuth();
    const canManageJobs = canDo("jobs.manage");

    const approve = useMutation({
        mutationFn: (jobId: string) => api.post(`/api/office-review/jobs/${jobId}/approve`),
        onSuccess: () => {
            invalidateAll();
            toast.success("Job approved");
        },
        onError: (e: any, jobId) => {
            if (catchHelperPresence(e, jobId, "approve")) return;
            // #1250 — this is the main approval surface, and the permit message
            // points at a Permit card that does not exist on this page. Show the
            // card here so the office answers and approves without hunting.
            if (e?.status === 422 && e?.body?.reason === "permit_not_answered") {
                setPermitGate({
                    retry: () => approve.mutate(jobId),
                    jobId,
                    message: e?.body?.message ?? "This job still needs a permit answer.",
                });
                return;
            }
            toast.error(`Approve failed: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`);
        },
    });

    const answerHelperPresence = useMutation({
        mutationFn: ({ jobId, answer }: { jobId: string; answer: "yes" | "no" }) =>
            api.post(`/api/office-review/jobs/${jobId}/helper-presence`, { answer }),
        onSuccess: (_r, vars) => {
            const retry = helperPresencePrompt?.retry ?? null;
            setHelperPresencePrompt(null);
            invalidateAll();
            toast.success(
                vars.answer === "yes"
                    ? "Recorded — helper credited on this job."
                    : "Recorded — helper was not on this job.",
            );
            if (retry === "approve") {
                approve.mutate(vars.jobId);
            } else {
                toast.info("Answer saved — run Approve & Send again to finish.");
            }
        },
        onError: (e: any) =>
            toast.error(`Could not record the answer: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`),
    });
    // Clears the "Assignment approval" flag raised when office manually
    // assigned a tech not approved for the job's job type. By default it also
    // grants that tech a standing approval so the same override won't re-flag.
    const approveAssignment = useMutation({
        mutationFn: ({ jobId, grantApproval }: { jobId: string; grantApproval: boolean }) =>
            api.post(`/api/office-review/jobs/${jobId}/approve-assignment`, { grantApproval }),
        onSuccess: (_r, vars) => {
            invalidateAll();
            toast.success(
                vars.grantApproval ? "Assignment approved; tech granted standing approval" : "Assignment approved",
            );
        },
        onError: (e: any) =>
            toast.error(`Approve assignment failed: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`),
    });
    const approveAndSend = useMutation({
        mutationFn: ({
            jobId,
            flags,
            attachmentIds,
            excludedAttachmentIds,
            plan,
            resend,
            confirmDroppedAttachments,
            staffOnlyConfirmed,
        }: ApproveAndSendVars) =>
            api
                .post(`/api/office-review/jobs/${jobId}/approve-and-send`, {
                    ...flags,
                    attachmentIds: attachmentIds ?? [],
                    excludedAttachmentIds: excludedAttachmentIds ?? [],
                    ...(plan && plan.length > 0 ? { plan } : {}),
                    // CE-1: the modal only sets this after the operator confirms
                    // the drop warning ("Send anyway"). Without it the server
                    // 409s DROPPED_ATTACHMENTS_UNCONFIRMED so nothing is lost.
                    ...(confirmDroppedAttachments ? { confirmDroppedAttachments: true } : {}),
                    ...(staffOnlyConfirmed ? { staffOnlyConfirmed: true } : {}),
                })
                .then((r) => ({ ...(r as object), _resend: resend })),
        onSuccess: (r: any, vars: ApproveAndSendVars) => {
            invalidateAll();
            // CE-2: selected files can still fall off AT SEND TIME (read
            // failure, size cap once the real PDFs were weighed). Those never
            // went through the pre-send drop gate, so surface them here —
            // a selected file is either attached or loudly reported, never
            // silently missing. Drops the operator already confirmed via
            // "Send anyway" are not re-toasted (except read failures, which
            // are always unexpected).
            const droppedFiles: DroppedAttachment[] = Array.isArray(r?.dropped_attachments)
                ? r.dropped_attachments
                : [];
            const unexpectedDrops = vars.confirmDroppedAttachments
                ? droppedFiles.filter((d) => d.reason === "download_failed")
                : droppedFiles;
            if (unexpectedDrops.length > 0) {
                toast.warning(
                    `${unexpectedDrops.length} selected file${unexpectedDrops.length === 1 ? " was" : "s were"} not attached: ${unexpectedDrops.map((d) => d.fileName).join(", ")}`,
                );
            }
            const emails: any[] = Array.isArray(r?.emails) ? r.emails : [];
            const emailsSent = emails.filter((e) => e?.status === "sent").length;
            const packetHeld = !!r?.packet_held;
            const sentAny = emailsSent > 0;

            // CLOSEOUT-PACKET-HOLD: the whole packet parked; the card (if on
            // file, auto-charge profile) was already run at approve.
            if (packetHeld) {
                const waiting = r?.packet_waiting ?? null;
                const parts = [
                    waiting?.waitingOnReports
                        ? `${waiting.reportsFiled ?? 0} of ${waiting.reportsTotal ?? 0} reports`
                        : null,
                    waiting?.waitingOnWorkOrder ? "the completed work order" : null,
                ].filter(Boolean);
                const charged = Array.isArray(r?.charge_at_approve)
                    ? r.charge_at_approve.filter((c: any) => c?.charged).length
                    : 0;
                toast.info(
                    `Packet held — waiting on ${parts.join(" and ") || "release"}. ` +
                        (charged > 0 ? "Card charged; the invoice rides as a paid receipt. " : "") +
                        "Everything sends as one email once it's ready.",
                );
            }
            const sendErrors: any[] = Array.isArray(r?.send_errors) ? r.send_errors : [];
            const noRecipient: any[] = Array.isArray(r?.no_recipient) ? r.no_recipient : [];
            const readyToFile = Number(r?.submissions_marked_ready_to_file ?? 0);

            // C3: only claim delivery when something actually emailed. Surface
            // partial failures / no-recipient docs instead of a blanket green.
            if (sentAny && (sendErrors.length > 0 || noRecipient.length > 0)) {
                toast.warning(
                    `Sent ${emailsSent} email${emailsSent === 1 ? "" : "s"}, but ${sendErrors.length + noRecipient.length} item${sendErrors.length + noRecipient.length === 1 ? "" : "s"} could not be delivered. Check recipients and resend.`,
                );
            } else if (sentAny) {
                toast.success(
                    r?._resend
                        ? `Resent — ${emailsSent} email${emailsSent === 1 ? "" : "s"} to the customer`
                        : `Sent — ${emailsSent} email${emailsSent === 1 ? "" : "s"} to the customer`,
                );
            } else {
                // No documents existed to send — the job was approved.
                toast.success("Job approved");
            }

            // C11: nudge the office that legal city filing still remains.
            if (readyToFile > 0) {
                toast.info(`${readyToFile} device${readyToFile === 1 ? "" : "s"} ready to file with the city.`, {
                    action: {
                        label: "View filing queue",
                        onClick: () => navigate("/submissions?status=ready_to_file"),
                    },
                });
            }
        },
        // C3: the server now returns 422 NOTHING_SENT when documents existed but
        // none reached a customer — surface that truthfully instead of a green.
        onError: (e: any, vars: ApproveAndSendVars) => {
            if (catchHelperPresence(e, vars.jobId, null)) return;
            // Unknown tax status → amber one-click prompt, never a raw 422.
            if (catchTaxStatusUnknown(e, vars)) return;
            // #1250 — approve-and-send completes the job too, so it meets the
            // same permit gate. Answer it here rather than pointing at a card
            // that lives on another page.
            if (e?.status === 422 && e?.body?.reason === "permit_not_answered") {
                setPermitGate({
                    // Replay the action the office actually chose. Replaying a
                    // plain approve after Approve & Send left the job approved
                    // and the customer's packet never sent, with a success toast
                    // saying "Job approved".
                    retry: () => approveAndSend.mutate(vars),
                    jobId: vars.jobId,
                    message: e?.body?.message ?? "This job still needs a permit answer.",
                });
                return;
            }
            const code = e?.body?.error;
            // CE-1: the server refused because selected files would silently drop
            // and the operator never confirmed. Render the drop warning (same
            // dialog as the preview path) so we can send anyway or back out —
            // never a raw error toast.
            // Every recipient is one of ours: add the customer, or confirm.
            const staffOnly = noCustomerEmailRefusal(e);
            if (staffOnly) {
                setStaffOnlyGate({ refusal: staffOnly, vars });
                return;
            }
            if (e?.status === 409 && code === "DROPPED_ATTACHMENTS_UNCONFIRMED") {
                const dropped = Array.isArray(e?.body?.droppedAttachments)
                    ? (e.body.droppedAttachments as DroppedAttachment[])
                    : [];
                setDropGate({ dropped, vars });
                return;
            }
            if (code === "NOTHING_SENT" || code === "INVOICE_NOT_BILLABLE" || code === "DOC_WITHOUT_RECIPIENT") {
                toast.error(e?.body?.message ?? "Nothing was sent to the customer.");
            } else {
                toast.error(`Send failed: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`);
            }
        },
    });

    // One-click resolve of an unknown tax status from the packet-send prompt:
    // set the customer taxable/exempt (server recomputes their draft docs),
    // then re-fire the original send with its saved flags.
    const resolveTaxAndResend = useMutation({
        mutationFn: (status: "taxable" | "exempt") =>
            api.patch(`/api/customers/${taxStatusPrompt!.customerId}`, { taxStatus: status }),
        onSuccess: (_r, status) => {
            const prompt = taxStatusPrompt;
            setTaxStatusPrompt(null);
            invalidateAll();
            toast.success(
                status === "taxable" ? "Customer marked taxable — tax applied" : "Customer marked tax exempt",
            );
            if (prompt) approveAndSend.mutate(prompt.sendArgs);
        },
        onError: (e: any) =>
            toast.error(`Could not set tax status: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`),
    });
    const hold = useMutation({
        mutationFn: ({ jobId, note }: { jobId: string; note?: string }) =>
            api.post(`/api/office-review/jobs/${jobId}/hold`, { note }),
        onSuccess: () => {
            invalidateAll();
            toast.success("Job placed on hold");
        },
    });
    const returnToTech = useMutation({
        mutationFn: ({ jobId, note }: { jobId: string; note: string }) =>
            api.post(`/api/office-review/jobs/${jobId}/return`, { note }),
        onSuccess: () => {
            invalidateAll();
            setSelectedJobId(null);
            toast.success("Job returned to technician");
        },
    });
    const requestCorrection = useMutation({
        mutationFn: ({ jobId, note }: { jobId: string; note: string }) =>
            api.post(`/api/office-review/jobs/${jobId}/request-correction`, { note }),
        onSuccess: () => {
            invalidateAll();
            setSelectedJobId(null);
            toast.success("Correction requested");
        },
    });
    const createFollowUp = useMutation({
        mutationFn: (jobId: string) =>
            api.post<{ job: { id: string; title: string } }>(`/api/office-review/jobs/${jobId}/create-followup`),
        onSuccess: (r) => {
            invalidateAll();
            toast.success(`Follow-up created: ${r.job.title}`);
            navigate(`/jobs/${r.job.id}`);
        },
    });
    const closeJob = useMutation({
        mutationFn: (jobId: string) => api.post(`/api/office-review/jobs/${jobId}/close`),
        onSuccess: () => {
            invalidateAll();
            setSelectedJobId(null);
            toast.success("Job closed");
        },
    });
    const sendWorkOrder = useMutation({
        mutationFn: ({ jobId, email, ccCustomer }: { jobId: string; email?: string; ccCustomer?: boolean }) =>
            api.post<{ deliveredTo: string }>(`/api/office-review/jobs/${jobId}/send-work-order`, {
                ...(email ? { email } : {}),
                ...(ccCustomer ? { ccCustomer } : {}),
            }),
        onSuccess: (r) => {
            invalidateAll();
            toast.success(`Work order sent to ${r.deliveredTo}`);
        },
        onError: (e: any) => toast.error(`Send failed: ${e?.body?.error ?? "unknown"}`),
    });
    // Regenerate the filled WO PDF from the stored completion (no tech
    // re-entry) — used after fixing a wrong template match. Never delivers.
    const refillWorkOrder = useWoRegenerate({
        onLandedAfterTimeout: () => invalidateAll(),
        onSuccess: (r) => {
            invalidateAll();
            // 200 with no PDF produced (no locked template) — don't call that
            // a regeneration (Issue #931).
            if (r.generated === false) {
                toast.error(
                    `No PDF was generated — ${r.fillWarnings?.[0] ?? "this job has no locked return-PDF template yet."}`,
                );
            } else if ((r.fillWarnings ?? []).length > 0) {
                toast.warning(`PDF regenerated with warnings: ${r.fillWarnings?.[0]}`);
            } else {
                toast.success("Completed WO PDF regenerated — review before sending");
            }
        },
    });
    // Office override: clear a tech's skip on a scheduled device (e.g.
    // dispatch wants the device tested after all). Hits the same
    // disposition endpoint as the field UI with `disposition: null`.
    const restoreDisposition = useMutation({
        mutationFn: ({ jobId, linkId }: { jobId: string; linkId: string }) =>
            api.post(`/api/jobs/${jobId}/assets/${linkId}/disposition`, {
                disposition: null,
            }),
        onSuccess: () => {
            qc.invalidateQueries({
                queryKey: ["backflow", "job", selectedJobId, "devices"],
            });
            toast.success("Skip cleared");
        },
        onError: (e: any) => toast.error(`Restore failed: ${e?.body?.message ?? "unknown"}`),
    });
    // "Device no longer exists" resolution: confirm retires the asset,
    // reject clears the flag and keeps it active. Never auto-retired.
    const resolveRemoval = useMutation({
        mutationFn: ({ assetId, action }: { assetId: string; action: "confirm" | "reject" }) =>
            api.post(`/api/assets/${assetId}/removal-review`, { action }),
        onSuccess: (_res, vars) => {
            qc.invalidateQueries({ queryKey: ["backflow", "job", selectedJobId, "devices"] });
            toast.success(
                vars.action === "confirm" ? "Asset retired — removed from active lists" : "Kept — flag cleared",
            );
        },
        onError: (e: any) => toast.error(`Removal review failed: ${e?.body?.message ?? "unknown"}`),
    });
    const regenerateReport = useMutation({
        mutationFn: (jobId: string) => api.post(`/api/backflow/job/${jobId}/regenerate-report`),
        onSuccess: () => {
            invalidateAll();
            toast.success("Jurisdiction report regenerated");
        },
        onError: (e: any) => toast.error(`Regenerate failed: ${e?.body?.message ?? "unknown"}`),
    });
    // Approve / deny a tech-added device. Approve pulls it into the
    // invoice/estimate + payroll; deny voids its points and leaves it off
    // the invoice (test row + site asset kept on file).
    const decideDevice = useMutation({
        mutationFn: ({
            jobId,
            linkId,
            decision,
            note,
        }: {
            jobId: string;
            linkId: string;
            decision: "approved" | "denied" | "pending";
            note?: string;
        }) => api.post(`/api/office-review/jobs/${jobId}/assets/${linkId}/decision`, { decision, note }),
        onSuccess: (_r, vars) => {
            invalidateAll();
            if (vars.decision === "approved") toast.success("Device approved — added to invoice");
            else if (vars.decision === "denied") toast.success("Device denied — removed from invoice & payroll");
            else toast.success("Decision reset — device back to pending review");
        },
        onError: (e: any) => toast.error(`Decision failed: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`),
    });

    // Orphan test-row actions (Issue #304): clear a test whose device is no
    // longer linked to the job — delete a garbage/mis-scanned stray, or re-link
    // a real on-site device whose link vanished.
    const voidOrphanTest = useMutation({
        mutationFn: ({ jobId, testId }: { jobId: string; testId: string }) =>
            api.post(`/api/office-review/jobs/${jobId}/tests/${testId}/void`, {}),
        onSuccess: () => {
            invalidateAll();
            toast.success("Stray test row deleted — job can clear closeout");
        },
        onError: (e: any) => toast.error(`Couldn't delete: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`),
    });
    const relinkOrphanTest = useMutation({
        mutationFn: ({ jobId, testId }: { jobId: string; testId: string }) =>
            api.post(`/api/office-review/jobs/${jobId}/tests/${testId}/relink`, {}),
        onSuccess: () => {
            invalidateAll();
            toast.success("Device re-linked — its test is back in review");
        },
        onError: (e: any) => toast.error(`Couldn't re-link: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`),
    });

    const convertToInvoice = useMutation({
        mutationFn: (estimateId: string) =>
            api.post<{ invoice: { id: string } }>(`/api/estimates/${estimateId}/convert-to-invoice`, {}),
        onSuccess: (r) => {
            invalidateAll();
            toast.success("Converted to invoice");
            navigate(`/invoices/${r.invoice.id}`);
        },
        onError: (e: any) => toast.error(`Convert failed: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`),
    });
    // Problem 2: turn an approved repair estimate into a scheduled repair job
    // without leaving office review. Uses the existing follow-up-job endpoint
    // and refreshes the dispatch backlog so the new job appears immediately.
    const createRepairJob = useMutation({
        // Optional WO/PO # (Issue #634): when the office knows the approved
        // repair's work-order / PO number, send it so the new job carries it in
        // external_ref / customer_po — the columns the WO-intake dedup + auto-
        // link guards key on, so a later scan links here instead of duplicating.
        mutationFn: ({ estimateId, workOrderNumber }: { estimateId: string; workOrderNumber?: string }) => {
            const wo = workOrderNumber?.trim();
            return api.post<ConversionScope & { job: { id: string } }>(
                `/api/estimates/${estimateId}/create-followup-job`,
                wo ? { workOrderNumber: wo } : {},
            );
        },
        onSuccess: (data) => {
            invalidateAll();
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            // #1611: say what didn't match instead of a blanket success.
            const t = conversionToast(data);
            if (t.warn)
                toast.warning(`Repair job created — ${t.text.replace(/^Job created — /, "")}`, { duration: 12000 });
            else toast.success("Repair job created and added to dispatch queue");
        },
        onError: (e: any) =>
            toast.error(`Create repair job failed: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`),
    });
    const applyCurrentPricing = useMutation({
        mutationFn: ({ jobId, invoiceId }: { jobId: string; invoiceId: string }) =>
            api.post(`/api/office-review/jobs/${jobId}/invoices/${invoiceId}/apply-current-pricing`, {}),
        onSuccess: () => {
            invalidateAll();
            setPricingDismissed(false);
            toast.success("Draft invoice updated to current pricing");
        },
        onError: (e: any) => toast.error(`Reprice failed: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`),
    });
    const rebuildPricing = useMutation({
        mutationFn: (jobId: string) => api.post(`/api/office-review/jobs/${jobId}/rebuild-pricing`, {}),
        onSuccess: () => {
            invalidateAll();
            setPricingDismissed(false);
            setShowRebuildPricingConfirm(false);
            toast.success("Invoice updated from current pricebook");
        },
        onError: (e: any) =>
            toast.error(
                e?.body?.error === "INVOICE_NOT_DRAFT"
                    ? "Already reviewed/sent — cannot refresh pricing"
                    : `Refresh failed: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`,
            ),
    });
    const patchInvoiceLine = useMutation({
        mutationFn: ({
            invoiceId,
            lineId,
            ...body
        }: {
            invoiceId: string;
            lineId: string;
            description?: string;
            qty?: number;
            unit_price?: number;
            labor_hours?: number;
            labor_techs?: number;
        }) => api.patch(`/api/invoices/${invoiceId}/lines/${lineId}`, body),
        onSuccess: () => {
            invalidateAll();
            toast.success("Line updated");
        },
        // P0 (CUTOVER-BUGS.md 2026-07-12): a failed line save must never look
        // saved — refetch so the cell shows the stored value, and say so.
        onError: (e: any) => {
            invalidateAll();
            toast.error(`Line NOT saved: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`);
        },
    });
    const deleteInvoiceLine = useMutation({
        mutationFn: ({ invoiceId, lineId }: { invoiceId: string; lineId: string }) =>
            api.delete(`/api/invoices/${invoiceId}/lines/${lineId}`),
        onSuccess: () => {
            invalidateAll();
            toast.success("Line removed");
        },
        onError: (e: any) =>
            toast.error(`Line NOT removed: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`),
    });
    const patchEstimateLine = useMutation({
        mutationFn: ({
            estimateId,
            lineId,
            ...body
        }: {
            estimateId: string;
            lineId: string;
            description?: string;
            qty?: number;
            unit_price?: number;
            labor_hours?: number;
            labor_techs?: number;
        }) => api.patch(`/api/estimates/${estimateId}/lines/${lineId}`, body),
        onSuccess: () => {
            invalidateAll();
            toast.success("Line updated");
        },
        onError: (e: any) => {
            invalidateAll();
            toast.error(`Line NOT saved: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`);
        },
    });
    const deleteEstimateLine = useMutation({
        mutationFn: ({ estimateId, lineId }: { estimateId: string; lineId: string }) =>
            api.delete(`/api/estimates/${estimateId}/lines/${lineId}`),
        onSuccess: () => {
            invalidateAll();
            toast.success("Line removed");
        },
        onError: (e: any) =>
            toast.error(`Line NOT removed: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`),
    });
    const recordPayment = useMutation({
        mutationFn: ({
            invoiceId,
            ...body
        }: {
            invoiceId: string;
            method: string;
            reference?: string;
            amount?: number;
        }) => api.post(`/api/invoices/${invoiceId}/record-payment`, body),
        onSuccess: () => {
            invalidateAll();
            toast.success("Payment recorded");
        },
        onError: (e: any) => toast.error(`Payment failed: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`),
    });

    const jobs = queue.data?.jobs ?? [];

    // Restore the last-viewed job when arriving without a ?job param (deep
    // links keep their own). Runs once on mount — deliberate empty deps.
    useEffect(() => {
        if (!searchParams.get("job")) {
            const last = sessionStorage.getItem("office-review:last-job");
            if (last) setSelectedJobId(last);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // When the selected job leaves the queue (approved/closed elsewhere or by
    // us), advance to the item that FOLLOWED it in the previous ordering —
    // never jump to the top, never clear on a mere refetch. A deep-linked job
    // that was never in this tab's queue is left alone.
    const prevJobsRef = useRef<QueueJob[]>([]);
    useEffect(() => {
        if (queue.isLoading) return;
        const prev = prevJobsRef.current;
        prevJobsRef.current = jobs;
        if (!selectedJobId || jobs.some((j) => j.id === selectedJobId)) return;
        const oldIdx = prev.findIndex((j) => j.id === selectedJobId);
        if (oldIdx === -1) return;
        const candidates = [...prev.slice(oldIdx + 1), ...prev.slice(0, oldIdx)];
        const next = candidates.find((c) => jobs.some((j) => j.id === c.id));
        setSelectedJobId(next ? next.id : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobs, selectedJobId, queue.isLoading]);
    const d = detail.data;

    // Support Copilot page context — Help names the job under review unprompted.
    usePageContextReporter(
        d?.job
            ? {
                  label: `Office Review — Job #${d.job.display_number || String(d.job.id).slice(0, 8)} (${d.job.company_name})`,
                  entity: { type: "jobs", id: String(d.job.id) },
                  activeTab: tab,
                  values: { customer: d.job.company_name, job_title: d.job.title },
              }
            : { label: "Office Review queue", activeTab: tab },
    );

    // Single entry point for picking a job — used by the queue pane, the
    // narrow-tier drawer (which closes on pick), and the header pager.
    const openJob = (id: string) => {
        setSelectedJobId(id);
        setPricingDismissed(false);
        setQueueDrawerOpen(false);
    };
    const selectedIdx = jobs.findIndex((j) => j.id === selectedJobId);
    const selectPrevJob = () => {
        if (selectedIdx > 0) openJob(jobs[selectedIdx - 1].id);
    };
    const selectNextJob = () => {
        if (selectedIdx === -1 && jobs.length > 0) openJob(jobs[0].id);
        else if (selectedIdx >= 0 && selectedIdx < jobs.length - 1) openJob(jobs[selectedIdx + 1].id);
    };

    // Flatten the already-fetched invoice/estimate lines so the Test Results
    // table can surface the resolved pricebook item per device. The detail
    // payload doesn't link a test result to a pricebook item directly, but the
    // billing lines embed the device serial (`SN: <serial>`), so we match on it.
    const docLinePricebookItems = useMemo(() => {
        const lines: { description: string; name: string }[] = [];
        const collect = (docLines: DocLine[] | null | undefined) => {
            for (const line of docLines ?? []) {
                if (line.pricebook_item_name && line.description) {
                    lines.push({ description: line.description, name: line.pricebook_item_name });
                }
            }
        };
        for (const inv of d?.invoices ?? []) collect(inv.lines);
        for (const est of d?.estimates ?? []) collect(est.lines);
        return lines;
    }, [d?.invoices, d?.estimates]);

    const pricebookItemForTest = (t: TestResult): string | null => {
        const serial = t.serial_number?.trim();
        if (!serial) return null;
        const tagged = `SN: ${serial}`;
        return (
            docLinePricebookItems.find((l) => l.description.includes(tagged))?.name ??
            docLinePricebookItems.find((l) => l.description.includes(serial))?.name ??
            null
        );
    };
    const anyActionPending =
        approve.isPending ||
        approveAndSend.isPending ||
        hold.isPending ||
        returnToTech.isPending ||
        requestCorrection.isPending ||
        createFollowUp.isPending ||
        closeJob.isPending ||
        approveAssignment.isPending;

    // Assignment-approval override: surfaced from the selected job's queue row
    // (review_reasons lives on the queue payload, not the detail payload).
    const selectedQueueRow = jobs.find((j) => j.id === selectedJobId);
    const hasAssignmentFlag = selectedQueueRow?.review_reasons?.includes("assignment_approval") ?? false;

    // Repair-estimate workflow guidance. Derived from data the detail query
    // already returns — no backend change. `failedCount` prefers the detail
    // test results and falls back to the queue row's count. A pending repair
    // estimate never warns or blocks: the estimate emails alongside the
    // invoice on Approve & Send, and approval turns it into its own job.
    const failedCount =
        (d?.testResults?.filter((t) => t.overall_result === "fail").length ?? 0) || (selectedQueueRow?.fail_count ?? 0);
    const repairEstimate = d?.estimates?.find(isRepairEstimate);

    // Drive the action-bar buttons off the job's true approval/send state so
    // we stop showing redundant "Approve" once the job is past review and so
    // the green CTA reads "Send" (approved, never sent) or "Resend"
    // (already sent at least once).
    const isApproved = d?.job?.status === "completed";
    const isSent = !!d?.job?.last_customer_send_at;
    const sendLabel = !isApproved ? "Approve & Send" : !isSent ? "Send" : "Resend";

    // Points reconciliation: work happened (test/repair/replacement/invoice
    // evidence) but no matching point line exists. Approving pays the tech
    // short, so ask for an explicit confirm instead of hard-blocking.
    const pointsGaps = d?.pointsReconciliation?.gaps ?? [];
    const confirmPointsGaps = async () =>
        pointsGaps.length === 0 ||
        (await confirmDialog(
            `Tech points appear to be missing on this job:\n\n${pointsGaps
                .map((g) => `• ${g.message}`)
                .join("\n")}\n\nYou can add the missing point lines from the Tech Points panel first. Approve anyway?`,
        ));

    // C2: the job has at least one sendable invoice that is a $0/empty shell.
    // Block Approve & Send (which would email a meaningless bill) until it's
    // priced or removed.
    const hasNonBillableInvoice = (d?.invoices ?? []).some(
        (inv) =>
            !["paid", "void", "cancelled"].includes(inv.status) &&
            (Number(inv.total_amount) <= 0 || !inv.lines || inv.lines.length === 0),
    );

    const issueCount = jobs.filter((j) => j.review_priority <= 2).length;
    const pricingStaleCount = jobs.filter((j) => j.pricing_stale).length;

    // C11: persistent reminder that legal city filing still remains after a
    // job is approved-and-sent. Devices land in the "Ready to File" queue and
    // must be filed on the water authority portal — easy to forget.
    const filingSummary = useQuery({
        queryKey: ["submissions-summary"],
        queryFn: () =>
            api.get<{ summary: { status: string; count: number; oldest_days?: number | null }[] }>(
                "/api/submissions/summary",
            ),
        staleTime: 60_000,
    });
    const readyToFileRow = filingSummary.data?.summary.find((s) => s.status === "ready_to_file");
    const readyToFileCount = readyToFileRow?.count ?? 0;
    // #832 M14: the backlog's age rides with its count — "164 ready" that's a
    // day old and "164 ready" whose oldest row has waited a month are
    // different emergencies.
    const readyToFileOldest = readyToFileRow?.oldest_days ?? null;

    // CLOSEOUT-PACKET-HOLD: held-packet status for the selected job
    // ("packet held — waiting on N of M reports / work order").
    // #1569: the SHARED readiness read — the same hook the job page's Resend
    // dialog uses. It used to be a second, differently-keyed copy of this
    // query living here, which is how two screens end up describing one
    // packet two ways (#832). One key, one cache, one answer.
    const packetStatusQ = usePacketReadiness(selectedJobId ?? undefined);
    const packetStatus = packetStatusQ.data ?? null;
    // What WOULD hold this packet if the office approved right now. Close Out
    // shows it but never blocks on it: parking here is the design, and the
    // approve also finalises the invoice and can charge the card, so refusing
    // would stop billing on any job with a single unfiled device.
    const packetWouldHold = packetBlock(packetStatusQ.data);

    // H13: the reasons this job is in the queue (from the queue row) plus the
    // standard "always verify" items, so the office has a concrete checklist
    // instead of an undifferentiated dump of tests/photos/invoices.
    const reviewReasons = selectedQueueRow?.review_reasons ?? [];
    const checklistItems: string[] = [
        ...reviewReasons.map((r) => REASON_GUIDANCE[r] ?? REASON_LABELS[r] ?? r),
        "Confirm the invoice total and line items match the work performed.",
        hasNonBillableInvoice ? "An invoice is $0 or empty — add pricing or remove it before sending." : "",
        "If an estimate is required, make sure it's sent to the right contact.",
    ].filter(Boolean);

    const tabDefs: { key: Tab; label: string; icon: typeof Layers; count?: number }[] = [
        { key: "all", label: "All Work", icon: Layers, count: jobs.length },
        { key: "invoices", label: "Invoices", icon: DollarSign },
        { key: "estimates", label: "Estimates", icon: FileEdit },
        { key: "pricing", label: "Pricing Changed", icon: AlertTriangle, count: pricingStaleCount || undefined },
        { key: "issues", label: "Issues", icon: Flag, count: issueCount },
    ];

    function handleNoteAction() {
        if (!noteDialog || !noteText.trim()) return;
        if (noteDialog.action === "return") {
            returnToTech.mutate({ jobId: noteDialog.jobId, note: noteText });
        } else if (noteDialog.action === "correction") {
            requestCorrection.mutate({ jobId: noteDialog.jobId, note: noteText });
        } else if (noteDialog.action === "hold") {
            hold.mutate({ jobId: noteDialog.jobId, note: noteText });
        }
        setNoteDialog(null);
        setNoteText("");
    }

    // ── Decision buttons ────────────────────────────────────────────────
    // One source of truth for the review actions, rendered both in the
    // pinned action bar and in the end-of-review terminal band. The visible
    // set shrinks with the layout tier so the row never wraps into a ragged
    // second line — lower-frequency actions collapse into "More actions":
    //   wide:          View Job · Approve only · Approve & Send · Hold · Return · More(Follow-Up, Cancel)
    //   medium/narrow: Approve only · Approve & Send · More(View Job, Hold, Return, Follow-Up, Cancel)
    const renderDecisionButtons = () => {
        if (!d || !selectedJobId) return null;
        return (
            <>
                {tier === "wide" && (
                    <Button size="sm" variant="outline" onClick={() => navigate(`/jobs/${selectedJobId}`)}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> View Job
                    </Button>
                )}
                {hasAssignmentFlag && (
                    <div className="flex items-center gap-1.5 rounded-md border border-blue-300/60 bg-blue-50/50 px-2 py-1 dark:border-blue-700/60 dark:bg-blue-900/15">
                        <label
                            className="flex items-center gap-1 text-[10px] text-foreground/80"
                            title="Also grant this tech a standing approval for this job type"
                        >
                            <input
                                type="checkbox"
                                className="h-3 w-3"
                                checked={grantStandingApproval}
                                onChange={(e) => setGrantStandingApproval(e.target.checked)}
                            />
                            Grant standing approval
                        </label>
                        <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            disabled={anyActionPending}
                            onClick={() =>
                                approveAssignment.mutate({
                                    jobId: selectedJobId,
                                    grantApproval: grantStandingApproval,
                                })
                            }
                        >
                            {approveAssignment.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                            ) : (
                                <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                            )}
                            Approve assignment
                        </Button>
                    </div>
                )}
                {!isApproved && (
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={anyActionPending}
                        title="Marks the job complete without emailing the customer. Use Approve & Send to also email the checked PDFs."
                        onClick={async () => {
                            if (await confirmPointsGaps()) approve.mutate(selectedJobId);
                        }}
                    >
                        {approve.isPending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                        ) : (
                            <CheckCircle className="h-3.5 w-3.5 mr-1" />
                        )}
                        Approve only (no email)
                    </Button>
                )}
                <Button
                    size="sm"
                    className="bg-green-700 hover:bg-green-800 text-white"
                    disabled={anyActionPending || hasNonBillableInvoice}
                    title={
                        hasNonBillableInvoice
                            ? "This job has a $0 or empty invoice. Add pricing or remove it before sending."
                            : isApproved
                              ? "Email the checked PDFs to the customer."
                              : "Marks the job complete and emails the checked PDFs to the customer."
                    }
                    onClick={async () => {
                        if (await confirmPointsGaps()) setShowSendModal(true);
                    }}
                >
                    {approveAndSend.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                    ) : (
                        <Send className="h-3.5 w-3.5 mr-1" />
                    )}
                    {sendLabel}
                </Button>
                {tier === "wide" && (
                    <>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={anyActionPending}
                            onClick={() => {
                                setNoteText("");
                                setNoteDialog({ action: "hold", jobId: selectedJobId });
                            }}
                        >
                            <PauseCircle className="h-3.5 w-3.5 mr-1" /> Hold
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={anyActionPending}
                            onClick={() => {
                                setNoteText("");
                                setNoteDialog({ action: "return", jobId: selectedJobId });
                            }}
                        >
                            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Return
                        </Button>
                    </>
                )}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" disabled={anyActionPending} aria-label="More actions">
                            <MoreHorizontal className="h-3.5 w-3.5 mr-1" /> More
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {tier !== "wide" && (
                            <DropdownMenuItem onClick={() => navigate(`/jobs/${selectedJobId}`)}>
                                <Eye className="h-3.5 w-3.5 mr-2" /> View Job
                            </DropdownMenuItem>
                        )}
                        {tier !== "wide" && (
                            <>
                                <DropdownMenuItem
                                    onClick={() => {
                                        setNoteText("");
                                        setNoteDialog({ action: "hold", jobId: selectedJobId });
                                    }}
                                >
                                    <PauseCircle className="h-3.5 w-3.5 mr-2" /> Hold
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => {
                                        setNoteText("");
                                        setNoteDialog({ action: "return", jobId: selectedJobId });
                                    }}
                                >
                                    <RotateCcw className="h-3.5 w-3.5 mr-2" /> Return
                                </DropdownMenuItem>
                            </>
                        )}
                        <DropdownMenuItem onClick={() => createFollowUp.mutate(selectedJobId)}>
                            <MessageSquarePlus className="h-3.5 w-3.5 mr-2" /> Follow-Up
                        </DropdownMenuItem>
                        {d.job.status !== "cancelled" && (
                            <DropdownMenuItem
                                className="text-red-700 dark:text-red-300"
                                onClick={() => setShowCancelDialog(true)}
                            >
                                <Ban className="h-3.5 w-3.5 mr-2" /> Cancel Job
                            </DropdownMenuItem>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </>
        );
    };

    // Queue list body — shared between the docked pane (wide/medium) and the
    // narrow-tier slide-over drawer.
    const queueListBody = queue.isLoading ? (
        <div className="p-8 text-center text-foreground/80">
            <Loader2 className="mx-auto h-6 w-6 animate-spin mb-2" />
            <span className="text-sm">Loading queue...</span>
        </div>
    ) : jobs.length === 0 ? (
        <div className="p-10 text-center text-foreground/80">
            <CheckCircle className="mx-auto mb-3 h-10 w-10 text-emerald-500/80" />
            <p className="font-semibold text-sm">No jobs waiting for review</p>
            <p className="text-xs mt-1.5 leading-relaxed">
                {tab === "issues"
                    ? "No flagged or failed items right now."
                    : "Completed jobs will appear here for approval."}
            </p>
        </div>
    ) : (
        <>
            {/* M8 (#834): the pill color key — shared by the docked pane and
                the narrow-tier drawer because it lives in the queue body. */}
            <PillLegend />
            <div className="divide-y divide-border/50">
                {/* Work Inbox V2: one batched flag fetch for the whole queue. */}
                <JobTaskFlagProvider jobIds={jobs.map((j) => j.id)}>
                    {jobs.map((j) => (
                        <QueueItem
                            key={j.id}
                            job={j}
                            selected={selectedJobId === j.id}
                            onSelect={() => openJob(j.id)}
                        />
                    ))}
                </JobTaskFlagProvider>
            </div>
        </>
    );

    return (
        <div
            ref={pageRootRef}
            className={cn(
                "flex flex-col h-auto md:h-[calc(100vh-9rem)]",
                // Below the wide tier the review workspace goes full-bleed —
                // the shell's dark side gutters waste width the detail needs.
                tier !== "wide" && "-mx-4 sm:-mx-6 lg:-mx-8",
            )}
        >
            {/* FIELD RELIABILITY — bulk removal review of tech-flagged
                "device gone" assets (ST-imported ghosts). Renders only
                while something is pending. */}
            <RemovalReviewCard />
            {/* #1453 — devices nobody has confirmed on site, detector mains
                with no bypass, and the field's "not legible" / "no bypass"
                exceptions. Renders only while something is pending. */}
            <UnconfirmedDevicesCard />
            {/* Device check phase 0 (11/2) — records that could order the
                wrong parts, devices on an open PO / quote first. Read-only;
                renders only while something is listed. */}
            <PartsRiskCard />
            {/* Device check phase 5 (11/2) — "Can't read the tag?" devices the
                office matches from the photos. Renders only while waiting. */}
            <PhotoMatchCard />
            {readyToFileCount > 0 && (
                <button
                    type="button"
                    onClick={() => navigate("/submissions?status=ready_to_file")}
                    className="flex items-center gap-2 border-b border-purple-300/60 bg-purple-50 px-4 py-2 text-left text-sm text-purple-800 hover:bg-purple-100 dark:border-purple-500/40 dark:bg-purple-950/30 dark:text-purple-200"
                >
                    <FileCheck className="h-4 w-4 shrink-0" />
                    <span>
                        <span className="font-semibold">{readyToFileCount}</span> device
                        {readyToFileCount === 1 ? "" : "s"} ready to file with the city
                        {readyToFileOldest != null && readyToFileOldest > 0 && (
                            <span
                                className={cn(
                                    "font-semibold",
                                    readyToFileOldest >= 7 && "text-red-700 dark:text-red-300",
                                )}
                            >
                                {" "}
                                · oldest waiting {readyToFileOldest}d
                            </span>
                        )}{" "}
                        — these aren&apos;t done until they&apos;re filed on the water authority portal.
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1 font-medium underline">
                        Open filing queue <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                </button>
            )}
            {/* Tab bar — sticky so the queue tabs stay reachable when the
                shell scrolls (phone layout, h-auto). */}
            {/* eslint-disable-next-line no-restricted-syntax -- D1: sticky header — content scrolls beneath it; the blur is load-bearing */}
            <div className="sticky top-0 z-30 flex items-center gap-1 border-b border-border/70 bg-card/95 backdrop-blur-sm px-4 pt-3 pb-0">
                {tabDefs.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={cn(
                                "relative px-4 py-2.5 text-sm font-semibold transition-colors rounded-t-lg flex items-center gap-1.5",
                                tab === t.key
                                    ? "text-primary bg-background border border-b-0 border-border/70"
                                    : "text-foreground/80 hover:text-foreground hover:bg-secondary/60",
                            )}
                        >
                            <Icon className="h-4 w-4" />
                            {t.label}
                            {t.count != null && t.count > 0 && (
                                <span
                                    className={cn(
                                        "ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-md px-1.5 text-[10px] font-bold",
                                        t.key === "issues"
                                            ? "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300"
                                            : "bg-primary/10 text-primary",
                                    )}
                                >
                                    {t.count}
                                </span>
                            )}
                        </button>
                    );
                })}
                {tier === "narrow" ? (
                    /* Narrow tier: the queue pane is hidden, so the header
                       carries a drawer button + prev/next pager instead of
                       the passive item count. Hidden below md where the
                       stacked phone queue is still rendered inline. */
                    <div className="ml-auto hidden md:flex items-center gap-1 pb-1">
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-7"
                            onClick={() => setQueueDrawerOpen(true)}
                            data-testid="queue-drawer-button"
                        >
                            <List className="h-3.5 w-3.5 mr-1" /> Queue ({jobs.length})
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            aria-label="Previous job in queue"
                            disabled={selectedIdx <= 0}
                            onClick={selectPrevJob}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            aria-label="Next job in queue"
                            disabled={jobs.length === 0 || selectedIdx >= jobs.length - 1}
                            onClick={selectNextJob}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                ) : (
                    <span className="ml-auto text-xs font-medium text-foreground/80 pr-2 tabular-nums">
                        {queue.isLoading ? "Loading…" : `${jobs.length} item${jobs.length !== 1 ? "s" : ""} in queue`}
                    </span>
                )}
            </div>

            <div className="flex flex-col md:flex-row flex-1 min-h-0 gap-0">
                {/* ── Left Panel: Queue ───────────────────────────────────
                    Docked at wide/medium; at the narrow tier it hides (md+)
                    and the slide-over drawer + header pager take over. Below
                    md the stacked phone queue keeps rendering inline. */}
                <div
                    className={cn(
                        "w-full md:w-[340px] md:shrink-0 max-h-[45vh] md:max-h-none overflow-y-auto border-r border-border/60 bg-card",
                        tier === "narrow" && "md:hidden",
                    )}
                >
                    {queueListBody}
                </div>

                {/* ── Right Panel: Workspace ────────────────────────────── */}
                <div
                    ref={detailRef}
                    className={cn(
                        "flex-1 overflow-hidden bg-background",
                        // Hidden on phones until a job is picked so the queue gets
                        // the full width; always visible from md up.
                        !selectedJobId && "hidden md:block",
                    )}
                >
                    {!selectedJobId ? (
                        queue.isLoading ? (
                            <div className="flex h-full items-center justify-center text-foreground/80">
                                <Loader2 className="h-6 w-6 animate-spin" />
                            </div>
                        ) : (
                            <QueueSummary
                                jobs={jobs}
                                onStartReview={() => {
                                    if (jobs.length > 0) setSelectedJobId(jobs[0].id);
                                }}
                            />
                        )
                    ) : detail.isLoading ? (
                        <div className="flex h-full items-center justify-center text-foreground/80">
                            <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                    ) : d ? (
                        <div className="flex h-full flex-col">
                            {/* ── Action Bar (pinned above the scroll) ── */}
                            <div className="border-b border-border/60 bg-card px-5 py-3 shrink-0 space-y-2">
                                <div className="flex flex-wrap items-center justify-between gap-2 md:flex-nowrap">
                                    <div className="min-w-[240px] flex-1" data-testid="job-header-text">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <h2
                                                className="min-w-[240px] max-w-full truncate text-base font-bold tracking-tight text-foreground"
                                                title={d.job.title}
                                            >
                                                {d.job.title}
                                            </h2>
                                            {(d.job.display_number || d.job.id) && (
                                                <span className="text-xs font-mono text-foreground/70 shrink-0">
                                                    Job #{d.job.display_number || String(d.job.id).slice(0, 8)}
                                                </span>
                                            )}
                                        </div>
                                        {/* Long names truncate with the full value on hover
                                            instead of micro-wrapping one word per line. */}
                                        <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-foreground/80">
                                            <span className="min-w-0 max-w-full truncate" title={d.job.company_name}>
                                                {d.job.company_name}
                                            </span>
                                            <span aria-hidden>&middot;</span>
                                            <span className="min-w-0 max-w-full truncate" title={d.job.site_name}>
                                                {d.job.site_name}
                                            </span>
                                            <span aria-hidden>&middot;</span>
                                            <span className="whitespace-nowrap">Tech: {d.job.tech_name}</span>
                                            {d.job.trade_name && (
                                                <Badge variant="outline" className="text-[10px] shrink-0">
                                                    {d.job.trade_name}
                                                </Badge>
                                            )}
                                            {d.job.job_type_name && (
                                                <Badge variant="outline" className="text-[10px] shrink-0">
                                                    {d.job.job_type_name}
                                                </Badge>
                                            )}
                                        </div>
                                        {(() => {
                                            const addr = [
                                                d.job.site_address,
                                                d.job.site_city,
                                                d.job.site_region,
                                                d.job.site_postal_code,
                                            ]
                                                .map((p: unknown) => (typeof p === "string" ? p.trim() : ""))
                                                .filter(Boolean)
                                                .join(", ");
                                            return addr ? (
                                                <div className="flex items-center gap-1 text-xs text-foreground/70 mt-0.5 min-w-0">
                                                    <MapPin className="h-3 w-3 shrink-0" />
                                                    <span className="truncate" title={addr}>
                                                        {addr}
                                                    </span>
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>
                                    <div className="flex shrink-0 items-center gap-1.5" data-testid="decision-buttons">
                                        {renderDecisionButtons()}
                                    </div>
                                </div>
                                {/* CLOSEOUT-PACKET-HOLD: the whole customer
                                    packet is held until every device's report
                                    (and the completed WO, when applicable) is
                                    attached.

                                    #1569 — this used to render ONLY once the
                                    packet was already parked (`packetStatus.held`),
                                    so the office found out after the press,
                                    never before it. It now shows the same
                                    notice as soon as something WOULD hold it,
                                    and names the devices. The decision buttons
                                    above stay live either way: parking is the
                                    intended close-out flow, and the approve
                                    also finalises the invoice and can charge
                                    the card on file. */}
                                {packetWouldHold && (
                                    <div className="w-full" data-testid="packet-held-chip">
                                        <PacketNotReadyNotice
                                            block={packetWouldHold}
                                            jobId={selectedJobId ?? ""}
                                            consequence="will-park"
                                            queued={
                                                packetStatus?.held
                                                    ? { at: formatQueuedAt(packetStatus.parked_at), by: null }
                                                    : null
                                            }
                                        />
                                    </div>
                                )}
                            </div>

                            {/* ── ONE scroll context ─────────────────────
                                Everything below the action bar flows in a single
                                vertical scroll — no nested scrollboxes. Only the
                                action bar above and the queue tabs stay pinned. */}
                            <div className="flex-1 min-h-0 overflow-y-auto" data-testid="review-scroll">
                                {/* H13: review checklist — surfaces the queue's
                                reasons (which otherwise vanish once a job is opened)
                                and tells the office what to verify before approving. */}
                                <div className="border-b border-border/60 bg-secondary/40 px-5 py-2.5">
                                    {!checklistCollapsed && reviewReasons.length > 0 && (
                                        <div className="mb-2 flex items-center gap-1.5 flex-wrap">
                                            <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground/60">
                                                Flagged for
                                            </span>
                                            {reviewReasons.map((r) => (
                                                <Badge
                                                    key={r}
                                                    variant="outline"
                                                    className={cn(
                                                        "text-[10px] px-1.5 py-0",
                                                        (r === "failed_test" ||
                                                            r === "cannot_complete" ||
                                                            r === "wo_delivery_failed") &&
                                                            "border-red-300 text-red-600 dark:border-red-700 dark:text-red-400",
                                                        (r === "missing_photos" ||
                                                            r === "missing_pricebook" ||
                                                            r === "missing_form_fields" ||
                                                            r === "wo_no_return_pdf") &&
                                                            "border-amber-300 text-amber-600 dark:border-amber-700 dark:text-amber-400",
                                                        r === "on_hold" &&
                                                            "border-purple-300 text-purple-600 dark:border-purple-700 dark:text-purple-400",
                                                        (r === "repair_needed" || r === "workflow_flag") &&
                                                            "border-orange-300 text-orange-600 dark:border-orange-700 dark:text-orange-400",
                                                        r === "repair_coverage_gap" &&
                                                            "border-rose-400 text-rose-700 dark:border-rose-600 dark:text-rose-300",
                                                        r === "assignment_approval" &&
                                                            "border-blue-300 text-blue-600 dark:border-blue-700 dark:text-blue-400",
                                                    )}
                                                >
                                                    {REASON_LABELS[r] || r}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                    <div className="flex items-start gap-1.5">
                                        <ClipboardCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
                                        <div className="flex-1">
                                            <button
                                                type="button"
                                                onClick={() => setChecklistCollapsed((c) => !c)}
                                                className="flex w-full items-center justify-between gap-2 text-left"
                                                aria-expanded={!checklistCollapsed}
                                            >
                                                <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground/60">
                                                    Before you approve
                                                </span>
                                                {checklistCollapsed ? (
                                                    <ChevronDown className="h-3.5 w-3.5 shrink-0 text-foreground/50" />
                                                ) : (
                                                    <ChevronUp className="h-3.5 w-3.5 shrink-0 text-foreground/50" />
                                                )}
                                            </button>
                                            {!checklistCollapsed && (
                                                <ul className="mt-1 space-y-0.5">
                                                    {checklistItems.map((item, i) => (
                                                        <li
                                                            key={i}
                                                            className="flex items-start gap-1.5 text-xs text-foreground/80"
                                                        >
                                                            <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-foreground/40" />
                                                            <span>{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Announce the auto-created repair estimate (or prompt to
                                create one) so the workflow is self-guiding. */}
                                {failedCount > 0 && repairEstimate?.status === "draft" && (
                                    <div className="flex items-start gap-2 border-b border-amber-300/60 bg-amber-50 px-5 py-3 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
                                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                                        <span className="flex-1">
                                            Repair estimate ready — {failedCount} device{failedCount === 1 ? "" : "s"}{" "}
                                            failed testing. Review the line items below; it emails alongside the invoice
                                            when you Approve &amp; Send. Once the customer approves it, the repair
                                            becomes its own job linked to this estimate.
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                document
                                                    .getElementById("office-review-estimate-section")
                                                    ?.scrollIntoView({ behavior: "smooth" })
                                            }
                                            className="shrink-0 inline-flex items-center gap-1 font-medium underline hover:no-underline"
                                        >
                                            Go to estimate <ArrowRight className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                )}
                                {failedCount > 0 && !repairEstimate && (
                                    <div className="flex items-start gap-2 border-b border-amber-300/60 bg-amber-50 px-5 py-3 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
                                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                                        <span className="flex-1">
                                            {failedCount} device{failedCount === 1 ? "" : "s"} failed testing. Create a
                                            repair estimate for the customer.
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                navigate(
                                                    `/commercial/new?jobId=${selectedJobId}&customerId=${d.job?.customer_id ?? ""}&kind=estimate`,
                                                )
                                            }
                                            className="shrink-0 inline-flex items-center gap-1 font-medium underline hover:no-underline"
                                        >
                                            Create estimate <ArrowRight className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                )}

                                {/* ── Content Area — tiered ──────────────────
                                wide:   job context | billing, two columns sharing
                                        the single scroll.
                                medium/narrow: one column — Compliance Filing,
                                        then billing full-width, then the rest. */}
                                {(() => {
                                    const compliance =
                                        (d.submissions ?? []).length > 0 ? (
                                            <ComplianceFilingPanel detail={d} onUpdated={invalidateAll} />
                                        ) : null;
                                    const contextStack = (
                                        <>
                                            {/* Tech-Added Devices (approve / deny) */}
                                            {selectedJobId && d.assets && (
                                                <TechAddedReviewSection
                                                    assets={d.assets}
                                                    isPending={decideDevice.isPending}
                                                    onDecide={(linkId, decision, note) =>
                                                        decideDevice.mutate({
                                                            jobId: selectedJobId,
                                                            linkId,
                                                            decision,
                                                            note,
                                                        })
                                                    }
                                                />
                                            )}
                                            {/* Removed-but-tested devices (link deleted after testing) */}
                                            {d.assets && d.testResults && selectedJobId && (
                                                <RemovedDevicesSection
                                                    assets={d.assets}
                                                    testResults={d.testResults}
                                                    jobId={selectedJobId}
                                                    onRelink={(testId) =>
                                                        relinkOrphanTest.mutate({ jobId: selectedJobId, testId })
                                                    }
                                                    onVoid={(testId) =>
                                                        voidOrphanTest.mutate({ jobId: selectedJobId, testId })
                                                    }
                                                    isRelinking={relinkOrphanTest.isPending}
                                                    isVoiding={voidOrphanTest.isPending}
                                                />
                                            )}
                                            {/* Tech points earned + reconciliation gaps */}
                                            {selectedJobId && d.pointsReconciliation && (
                                                <TechPointsPanel
                                                    jobId={selectedJobId}
                                                    reconciliation={d.pointsReconciliation}
                                                    onUpdated={invalidateAll}
                                                />
                                            )}
                                            {/* Assets */}
                                            {d.assets && d.assets.length > 0 && (
                                                <CollapsibleSection
                                                    title={`Assets (${d.assets.length})`}
                                                    icon={<Package className="h-4 w-4" />}
                                                    defaultOpen={false}
                                                >
                                                    <div className="rounded-lg border overflow-x-auto">
                                                        <table className="w-full text-sm">
                                                            <thead className="bg-muted text-left text-xs font-semibold text-foreground">
                                                                <tr>
                                                                    <th className="px-3 py-2">#</th>
                                                                    <th className="px-3 py-2">Device</th>
                                                                    <th className="px-3 py-2">Serial</th>
                                                                    <th className="px-3 py-2">Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y">
                                                                {d.assets.map((a, i) => (
                                                                    <tr key={a.id}>
                                                                        <td className="px-3 py-2 text-xs text-foreground/80">
                                                                            {a.sequence_no ?? i + 1}
                                                                        </td>
                                                                        <td className="px-3 py-2 text-xs">
                                                                            {a.size_label} {a.manufacturer} {a.model}
                                                                            {a.assembly_type && (
                                                                                <Badge
                                                                                    variant="outline"
                                                                                    className="ml-1 text-[9px]"
                                                                                >
                                                                                    {a.assembly_type.toUpperCase()}
                                                                                </Badge>
                                                                            )}
                                                                        </td>
                                                                        <td className="px-3 py-2 font-mono text-[11px]">
                                                                            {a.serial_number ?? "—"}
                                                                        </td>
                                                                        <td className="px-3 py-2 text-xs">
                                                                            {a.required_action ?? "—"}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </CollapsibleSection>
                                            )}

                                            {/* Repair coverage gap — a failed device's
                                        recommended action had no priced repair. */}
                                            {(d.reviewItems ?? []).some((ri) =>
                                                ri.title.startsWith(REPAIR_GAP_REVIEW_PREFIX),
                                            ) && (
                                                <section className="rounded-lg border border-rose-300 bg-rose-50 p-3 dark:border-rose-700 dark:bg-rose-900/20">
                                                    <div className="flex items-start gap-2">
                                                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-rose-600 dark:text-rose-400" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-rose-800 dark:text-rose-200">
                                                                Repair coverage gap
                                                            </p>
                                                            {(d.reviewItems ?? [])
                                                                .filter((ri) =>
                                                                    ri.title.startsWith(REPAIR_GAP_REVIEW_PREFIX),
                                                                )
                                                                .map((ri) => (
                                                                    <p
                                                                        key={ri.id}
                                                                        className="text-xs text-rose-700 dark:text-rose-300 mt-1"
                                                                    >
                                                                        {ri.title.slice(
                                                                            REPAIR_GAP_REVIEW_PREFIX.length,
                                                                        )}
                                                                    </p>
                                                                ))}
                                                            <p className="text-xs text-rose-700/80 dark:text-rose-300/80 mt-1">
                                                                These repairs landed as $0 placeholder lines because no
                                                                pricebook mapping exists. Build the repair so it prices
                                                                correctly before invoicing.
                                                            </p>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="mt-2 h-8 border-rose-400 text-rose-700 hover:bg-rose-100 dark:border-rose-600 dark:text-rose-300"
                                                                onClick={() => navigate("/pricebook/action-map")}
                                                            >
                                                                <Wrench className="h-3.5 w-3.5 mr-1" /> Build repair
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </section>
                                            )}

                                            {/* Form Mapping Warnings */}
                                            {(d.reportAttachments ?? []).some(
                                                (a) =>
                                                    a.mapping_warnings &&
                                                    (a.mapping_warnings.missingRequired.length > 0 ||
                                                        (a.mapping_warnings.dataWarnings?.length ?? 0) > 0),
                                            ) && (
                                                <FormMappingWarningsPanel
                                                    attachments={d.reportAttachments ?? []}
                                                    onRegenerate={() => d.job?.id && regenerateReport.mutate(d.job.id)}
                                                    isRegenerating={regenerateReport.isPending}
                                                    onPreview={(id, fileName) => setPreviewAttachment({ id, fileName })}
                                                />
                                            )}

                                            {/* Vendor work order review */}
                                            {d.job?.kind === "vendor_work_order" && (
                                                <WorkOrderReviewSection
                                                    detail={d}
                                                    onPreview={(id, fileName) => setPreviewAttachment({ id, fileName })}
                                                    onSend={(ccCustomer) =>
                                                        d.job?.id &&
                                                        sendWorkOrder.mutate({ jobId: d.job.id, ccCustomer })
                                                    }
                                                    isSending={sendWorkOrder.isPending}
                                                    onRefill={() =>
                                                        d.job?.id && void refillWorkOrder.start({ jobId: d.job.id })
                                                    }
                                                    isRefilling={refillWorkOrder.isRunning}
                                                    deliveryFailed={reviewReasons.includes("wo_delivery_failed")}
                                                    noReturnPdf={reviewReasons.includes("wo_no_return_pdf")}
                                                />
                                            )}

                                            {/* Technician Notes */}
                                            <TechNotesSection job={d.job} testResults={d.testResults} />

                                            {/* Replacements summary — Old → New pairs,
                                        prework photos, notes, finalize state. */}
                                            {(replacementsQuery.data?.replacements?.length ?? 0) > 0 && (
                                                <OfficeReplacementsSection
                                                    rows={replacementsQuery.data!.replacements}
                                                />
                                            )}

                                            <OfficeRepairsSection rows={repairsQuery.data?.repairs ?? []} />

                                            {/* Devices Not Tested — skipped + tech-added-untested
                                        rows surfaced from the new submit-gate model. */}
                                            {selectedJobId && (
                                                <OfficeNotTestedSection
                                                    jobId={selectedJobId}
                                                    rows={devicesQuery.data?.devices ?? []}
                                                    onRestore={(linkId) =>
                                                        restoreDisposition.mutate({
                                                            jobId: selectedJobId,
                                                            linkId,
                                                        })
                                                    }
                                                    isRestoring={restoreDisposition.isPending}
                                                    onResolveRemoval={(assetId, action) =>
                                                        resolveRemoval.mutate({ assetId, action })
                                                    }
                                                    isResolvingRemoval={resolveRemoval.isPending}
                                                />
                                            )}

                                            {/* Test Results */}
                                            {d.testResults.length > 0 && (
                                                <section>
                                                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-foreground">
                                                        <ClipboardCheck className="h-4 w-4" /> Test Results (
                                                        {d.testResults.length})
                                                    </h3>
                                                    <div className="rounded-lg border overflow-x-auto">
                                                        <table className="w-full text-sm">
                                                            <thead className="bg-muted text-left text-xs font-semibold text-foreground">
                                                                <tr>
                                                                    <th className="px-3 py-2">Device</th>
                                                                    <th className="px-3 py-2">Serial</th>
                                                                    <th className="px-3 py-2">Type</th>
                                                                    <th className="px-3 py-2">Serves</th>
                                                                    <th className="px-3 py-2">Pricebook Item</th>
                                                                    <th className="px-3 py-2">Result</th>
                                                                    <th className="px-3 py-2">Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y">
                                                                {d.testResults.map((t) => (
                                                                    <tr
                                                                        key={t.id}
                                                                        className={cn(
                                                                            t.on_non_potable_supply &&
                                                                                "border-l-4 border-purple-400",
                                                                        )}
                                                                    >
                                                                        <td className="px-3 py-2 text-xs">
                                                                            {t.size_label} {t.manufacturer} {t.model}
                                                                            {t.entered_by_user_id &&
                                                                                t.entered_by_name && (
                                                                                    // Helper Mode v2 both-name attribution
                                                                                    <div className="mt-0.5 text-[11px] text-amber-700 dark:text-amber-400">
                                                                                        Readings: {t.entered_by_name} ·
                                                                                        Reviewed &amp; signed:{" "}
                                                                                        {t.reviewed_by_name ||
                                                                                            d.job.tech_name}
                                                                                    </div>
                                                                                )}
                                                                        </td>
                                                                        <td className="px-3 py-2 font-mono text-[11px]">
                                                                            {t.serial_number ?? "—"}
                                                                        </td>
                                                                        <td className="px-3 py-2">
                                                                            <Badge
                                                                                variant="outline"
                                                                                className="text-[10px]"
                                                                            >
                                                                                {t.assembly_type?.toUpperCase()}
                                                                            </Badge>
                                                                        </td>
                                                                        <td className="px-3 py-2 text-xs">
                                                                            {t.service_type
                                                                                ? t.service_type.startsWith("fireline")
                                                                                    ? "Fireline"
                                                                                    : t.service_type === "irrigation"
                                                                                      ? "Irrigation"
                                                                                      : t.service_type.startsWith(
                                                                                              "domestic",
                                                                                          )
                                                                                        ? "Domestic"
                                                                                        : t.service_type
                                                                                : "—"}
                                                                        </td>
                                                                        <td className="px-3 py-2 text-xs text-foreground/80">
                                                                            {pricebookItemForTest(t) ?? "—"}
                                                                        </td>
                                                                        <td className="px-3 py-2">
                                                                            <span
                                                                                className={cn(
                                                                                    "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                                                                                    testStateTone(t.overall_result) ===
                                                                                        "pass"
                                                                                        ? "bg-green-100 text-green-700"
                                                                                        : testStateTone(
                                                                                                t.overall_result,
                                                                                            ) === "fail"
                                                                                          ? "bg-red-100 text-red-700"
                                                                                          : "bg-muted text-foreground/70",
                                                                                )}
                                                                            >
                                                                                {testStateLabel(t.overall_result)}
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-3 py-2 text-xs">
                                                                            {t.recommended_action ?? "—"}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </section>
                                            )}

                                            {/* Files & Reports — generated report/summary PDFs +
                                        uploaded documents, previewable in-app. */}
                                            <JobFilesSection
                                                reports={d.reportAttachments ?? []}
                                                documents={d.documents ?? []}
                                                onPreview={(id, fileName, allowDownload) =>
                                                    setPreviewAttachment({ id, fileName, allowDownload })
                                                }
                                            />

                                            {/* Images — test-embedded + file attachments. Always
                                        rendered last per office review ordering. */}
                                            <TestPhotosSection
                                                testPhotos={d.testPhotos ?? []}
                                                filePhotos={d.photos ?? []}
                                                onPreview={(id, fileName) => setPreviewAttachment({ id, fileName })}
                                            />

                                            {d.testResults.length === 0 &&
                                                (d.testPhotos ?? []).length === 0 &&
                                                d.photos.length === 0 &&
                                                (!d.assets || d.assets.length === 0) && (
                                                    <div className="flex flex-col items-center justify-center h-40 text-foreground/80">
                                                        <FileText className="h-8 w-8 opacity-40 mb-2" />
                                                        <p className="text-sm">
                                                            No test results, photos, or assets for this job.
                                                        </p>
                                                    </div>
                                                )}
                                        </>
                                    );

                                    const billingStack = (
                                        <>
                                            {d.pricing_stale &&
                                                !pricingDismissed &&
                                                d.pricing_diff &&
                                                d.pricing_diff.length > 0 && (
                                                    <PricingStaleBanner
                                                        anchorDate={d.pricing_anchor_date}
                                                        diff={d.pricing_diff}
                                                        draftInvoiceId={
                                                            d.invoices.find((i) => i.status === "draft")?.id
                                                        }
                                                        jobId={selectedJobId!}
                                                        onKeep={() => setPricingDismissed(true)}
                                                        onConvert={(invoiceId) =>
                                                            applyCurrentPricing.mutate({
                                                                jobId: selectedJobId!,
                                                                invoiceId,
                                                            })
                                                        }
                                                        isConverting={applyCurrentPricing.isPending}
                                                    />
                                                )}
                                            {d.invoices.some((i) => i.status === "draft") && (
                                                <div className="flex justify-end">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        disabled={rebuildPricing.isPending}
                                                        onClick={() => setShowRebuildPricingConfirm(true)}
                                                    >
                                                        {rebuildPricing.isPending ? (
                                                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                                        ) : (
                                                            <RefreshCw className="h-3.5 w-3.5 mr-1" />
                                                        )}
                                                        Refresh pricing
                                                    </Button>
                                                </div>
                                            )}
                                            {/* Collect first, then send: charge the card on
                                                file before Approve & Send emails the package
                                                as a PAID receipt. */}
                                            {d.invoices
                                                .filter(
                                                    (i) =>
                                                        chargeCardMode(toChargeInvoice(i), toChargeCustomer(d.job)) !==
                                                        "hidden",
                                                )
                                                .map((i) => (
                                                    <ChargeCardPanel
                                                        key={`charge-${i.id}`}
                                                        invoice={toChargeInvoice(i)}
                                                        customer={toChargeCustomer(d.job)}
                                                        onSettled={invalidateAll}
                                                    />
                                                ))}
                                            {d.invoices.length > 0 && (
                                                <InvoiceSection
                                                    invoices={d.invoices}
                                                    onSend={(id) =>
                                                        setComposeSend({
                                                            docType: "invoice",
                                                            id,
                                                            customerId: d.job?.customer_id ?? null,
                                                        })
                                                    }
                                                    isSending={false}
                                                    onPatchLine={(invoiceId, lineId, body) =>
                                                        patchInvoiceLine.mutate({ invoiceId, lineId, ...body })
                                                    }
                                                    onDeleteLine={(invoiceId, lineId) =>
                                                        deleteInvoiceLine.mutate({ invoiceId, lineId })
                                                    }
                                                    onRecordPayment={(invoiceId, body) =>
                                                        recordPayment.mutate({ invoiceId, ...body })
                                                    }
                                                    isRecordingPayment={recordPayment.isPending}
                                                />
                                            )}
                                            {d.invoices.length > 0 && d.estimates.length > 0 && (
                                                <div className="border-t-2 border-dashed border-border/70 pt-1" />
                                            )}
                                            {d.estimates.length > 0 && (
                                                <EstimateSection
                                                    estimates={d.estimates}
                                                    onSend={(id) =>
                                                        setComposeSend({
                                                            docType: "estimate",
                                                            id,
                                                            customerId: d.job?.customer_id ?? null,
                                                        })
                                                    }
                                                    isSending={false}
                                                    onPatchLine={(estimateId, lineId, body) =>
                                                        patchEstimateLine.mutate({ estimateId, lineId, ...body })
                                                    }
                                                    onDeleteLine={(estimateId, lineId) =>
                                                        deleteEstimateLine.mutate({ estimateId, lineId })
                                                    }
                                                    onConvert={(id) => convertToInvoice.mutate(id)}
                                                    isConverting={convertToInvoice.isPending}
                                                    onCreateRepairJob={(id, workOrderNumber) =>
                                                        createRepairJob.mutate({ estimateId: id, workOrderNumber })
                                                    }
                                                    isCreatingRepairJob={createRepairJob.isPending}
                                                    onCreateEstimate={() =>
                                                        navigate(
                                                            `/commercial/new?jobId=${selectedJobId}&customerId=${d.job?.customer_id ?? ""}&kind=estimate`,
                                                        )
                                                    }
                                                />
                                            )}
                                            {d.invoices.length === 0 && d.estimates.length === 0 && (
                                                <div className="rounded-lg border border-dashed p-6 text-center text-foreground/80">
                                                    <Receipt className="mx-auto h-8 w-8 mb-2 opacity-40" />
                                                    <p className="text-sm">No invoices or estimates for this job.</p>
                                                    {d.testResults.length > 0 && (
                                                        <>
                                                            <p className="mt-1 text-xs text-foreground/60">
                                                                This job has test results but no billing yet.
                                                            </p>
                                                            <Button
                                                                size="sm"
                                                                className="mt-3"
                                                                disabled={rebuildPricing.isPending}
                                                                onClick={() =>
                                                                    selectedJobId &&
                                                                    rebuildPricing.mutate(selectedJobId)
                                                                }
                                                            >
                                                                {rebuildPricing.isPending ? (
                                                                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                                                ) : (
                                                                    <Receipt className="h-3.5 w-3.5 mr-1" />
                                                                )}
                                                                Generate invoice
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    );

                                    return tier === "wide" ? (
                                        <div className="grid grid-cols-2">
                                            <div className="min-w-0 space-y-5 border-r p-5">
                                                {compliance}
                                                {contextStack}
                                            </div>
                                            <div className="min-w-0 space-y-4 p-5">{billingStack}</div>
                                        </div>
                                    ) : (
                                        <div className="min-w-0 space-y-5 p-5">
                                            {compliance}
                                            <div className="space-y-4">{billingStack}</div>
                                            {contextStack}
                                        </div>
                                    );
                                })()}

                                {/* ── End-of-review terminal band ──────────────
                                Always the LAST node in the scroll: the reviewer
                                knows nothing is hiding below it. */}
                                <div
                                    data-testid="end-of-review"
                                    className="border-t border-border/60 bg-card px-5 py-6"
                                >
                                    <div className="mx-auto flex max-w-2xl flex-col items-center gap-3 text-center">
                                        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                                            <CheckCircle className="h-5 w-5" />
                                            That&apos;s everything on this job
                                        </div>
                                        <p className="text-xs text-foreground/70">
                                            Nothing renders below this line — you&apos;ve seen the whole review.
                                        </p>
                                        <div className="flex flex-wrap items-center justify-center gap-1.5">
                                            {renderDecisionButtons()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Narrow-tier queue drawer — left slide-over so the reviewer can
                jump around the queue without the docked list eating width. */}
            <Sheet open={queueDrawerOpen} onOpenChange={setQueueDrawerOpen}>
                <SheetContent className="left-0 right-auto w-[360px] max-w-[90vw] border-l-0 border-r data-[state=closed]:-translate-x-full">
                    <SheetHeader>
                        <SheetTitle>Queue ({jobs.length})</SheetTitle>
                    </SheetHeader>
                    <div className="flex-1 overflow-y-auto">{queueListBody}</div>
                </SheetContent>
            </Sheet>

            {/* Note Dialog (used for Hold, Return, Correction) */}
            <Dialog
                open={!!noteDialog}
                onClose={() => {
                    setNoteDialog(null);
                    setNoteText("");
                }}
                title={
                    noteDialog?.action === "hold"
                        ? "Hold Job"
                        : noteDialog?.action === "return"
                          ? "Return to Technician"
                          : "Request Correction"
                }
            >
                <div className="space-y-3">
                    <p className="text-sm text-foreground/80">
                        {noteDialog?.action === "hold"
                            ? "Add an optional note explaining why this job is being held."
                            : "Provide a note explaining what needs to be addressed."}
                    </p>
                    <Textarea
                        placeholder={
                            noteDialog?.action === "hold"
                                ? "Waiting for parts pricing..."
                                : noteDialog?.action === "return"
                                  ? "Need to re-test device #3, readings were inconsistent."
                                  : "Photo of device tag is blurry, please retake."
                        }
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        rows={3}
                    />
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <Button
                        variant="outline"
                        onClick={() => {
                            setNoteDialog(null);
                            setNoteText("");
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleNoteAction} disabled={noteDialog?.action !== "hold" && !noteText.trim()}>
                        {noteDialog?.action === "hold"
                            ? "Place on Hold"
                            : noteDialog?.action === "return"
                              ? "Return to Tech"
                              : "Send Correction"}
                    </Button>
                </div>
            </Dialog>

            {/* Helper Mode close-out failsafe — required Yes/No before approval */}
            <Dialog
                open={!!helperPresencePrompt}
                onClose={() => setHelperPresencePrompt(null)}
                title="Was the helper on this job?"
            >
                <div className="space-y-3">
                    <p className="text-sm text-foreground/80">
                        {(() => {
                            const names = (helperPresencePrompt?.helpers ?? [])
                                .map((h) => h.name)
                                .filter(Boolean)
                                .join(" and ");
                            return `${names || "This tech's paired helper"} was paired with this tech, but no helper clock-in was recorded on this job. Approval is blocked until you answer.`;
                        })()}
                    </p>
                    <p className="text-xs text-foreground/60">
                        Answering Yes credits the helper with this job's device points; No records that they weren't on
                        site. Either answer unblocks approval.
                    </p>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <Button
                        variant="outline"
                        disabled={answerHelperPresence.isPending}
                        onClick={() =>
                            helperPresencePrompt &&
                            answerHelperPresence.mutate({ jobId: helperPresencePrompt.jobId, answer: "no" })
                        }
                    >
                        No — not on this job
                    </Button>
                    <Button
                        disabled={answerHelperPresence.isPending}
                        onClick={() =>
                            helperPresencePrompt &&
                            answerHelperPresence.mutate({ jobId: helperPresencePrompt.jobId, answer: "yes" })
                        }
                    >
                        {answerHelperPresence.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            "Yes — they were here"
                        )}
                    </Button>
                </div>
            </Dialog>

            {/* Unknown customer tax status — amber one-click prompt (mirrors the send dialog) */}
            <Dialog open={!!taxStatusPrompt} onClose={() => setTaxStatusPrompt(null)} title="Taxable or exempt?">
                <div className="space-y-2 rounded-md border border-amber-400/60 bg-amber-50 p-3 dark:border-amber-500/50 dark:bg-amber-950/40">
                    <p className="flex items-start gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        Tax status isn&apos;t set for this customer.
                    </p>
                    <p className="text-xs text-amber-800/90 dark:text-amber-300/90">
                        Pick one to update the customer, recalculate their draft invoices, and send in one step.
                    </p>
                </div>
                <div className="flex flex-wrap justify-end gap-2 mt-4">
                    <Button
                        variant="outline"
                        className="border-amber-500/60"
                        disabled={resolveTaxAndResend.isPending}
                        onClick={() => resolveTaxAndResend.mutate("exempt")}
                    >
                        {resolveTaxAndResend.isPending && resolveTaxAndResend.variables === "exempt" && (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        )}
                        Tax exempt — send as-is
                    </Button>
                    <Button
                        className="bg-amber-700 text-white hover:bg-amber-800"
                        disabled={resolveTaxAndResend.isPending}
                        onClick={() => resolveTaxAndResend.mutate("taxable")}
                    >
                        {resolveTaxAndResend.isPending && resolveTaxAndResend.variables === "taxable" && (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        )}
                        Taxable — apply tax &amp; send
                    </Button>
                </div>
            </Dialog>

            {/* Approve & Send / Send / Resend modal with attachment selection */}
            <Dialog
                open={showSendModal}
                onClose={() => {
                    setShowSendModal(false);
                    setSelectedFileIds([]);
                    setExcludedFileIds([]);
                    planRef.current = null;
                    setMissingDocs([]);
                    setSendStep("configure");
                    setSendSnapshot(null);
                }}
                title={sendLabel}
            >
                <div className="space-y-4">
                    {sendStep === "configure" ? (
                        <>
                            <p className="text-sm text-foreground/80">
                                {isSent
                                    ? "Re-send the selected documents to the customer."
                                    : "Choose which documents to include with the customer notification."}
                            </p>
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={sendAttachments.photos}
                                        onChange={(e) =>
                                            setSendAttachments((s) => ({ ...s, photos: e.target.checked }))
                                        }
                                        className="rounded border-input"
                                    />
                                    <Camera className="h-3.5 w-3.5 text-foreground/80" />
                                    Job photos ({d?.testPhotos?.length ?? 0} test photos, {d?.photos?.length ?? 0}{" "}
                                    attachments)
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={sendAttachments.reports}
                                        onChange={(e) =>
                                            setSendAttachments((s) => ({ ...s, reports: e.target.checked }))
                                        }
                                        className="rounded border-input"
                                    />
                                    <FileText className="h-3.5 w-3.5 text-foreground/80" />
                                    Test reports / PDFs
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={sendAttachments.invoices}
                                        onChange={(e) =>
                                            setSendAttachments((s) => ({ ...s, invoices: e.target.checked }))
                                        }
                                        className="rounded border-input"
                                    />
                                    <Receipt className="h-3.5 w-3.5 text-foreground/80" />
                                    Invoices ({d?.invoices?.length ?? 0})
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={sendAttachments.estimates}
                                        onChange={(e) =>
                                            setSendAttachments((s) => ({ ...s, estimates: e.target.checked }))
                                        }
                                        className="rounded border-input"
                                    />
                                    <FileEdit className="h-3.5 w-3.5 text-foreground/80" />
                                    Estimates ({d?.estimates?.length ?? 0})
                                </label>
                            </div>
                            {showSendModal && selectedJobId ? (
                                <SendRecipientEditor
                                    jobId={selectedJobId}
                                    customerId={d?.job?.customer_id ?? null}
                                    include={{
                                        invoices: sendAttachments.invoices,
                                        estimates: sendAttachments.estimates,
                                        reports: sendAttachments.reports,
                                        photos: sendAttachments.photos,
                                    }}
                                    onChange={handlePlanChange}
                                    onMissingDocsChange={handleMissingDocsChange}
                                />
                            ) : null}
                            {/*
                             * CE-2: ad-hoc attachment selection, back for real this time.
                             * (H15 removed the old upload affordance because selected
                             * files were never actually emailed.) Checked files feed
                             * selectedFileIds → the CE-1 send-preview drop query and the
                             * approve-and-send attachmentIds payload — the server now
                             * genuinely attaches explicit selections, category defaults
                             * notwithstanding, so the dropGate dialog covers any warnings
                             * (privacy / size cap / read failures) with no extra UI here.
                             */}
                            {showSendModal && selectedJobId ? (
                                <AttachJobFilesPicker
                                    jobId={selectedJobId}
                                    selectedIds={selectedFileIds}
                                    onChange={setSelectedFileIds}
                                    excludedIds={excludedFileIds}
                                    onExcludedChange={setExcludedFileIds}
                                />
                            ) : null}
                            {showSendModal && sizePlan.data ? (
                                <PacketSizeNotice message={sizePlan.data.message} leftOut={sizePlan.data.leftOut} />
                            ) : null}
                            {missingDocs.length > 0 && (
                                <div className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
                                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                    <span>
                                        No recipient is set to receive:{" "}
                                        {missingDocs.map((doc) => DOC_LABEL[doc]).join(", ")}. Assign the document to a
                                        recipient (or uncheck it) before sending.
                                    </span>
                                </div>
                            )}
                            <div className="flex justify-end gap-2 pt-2">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setShowSendModal(false);
                                        setSelectedFileIds([]);
                                        setExcludedFileIds([]);
                                        setExcludedFileIds([]);
                                        planRef.current = null;
                                        setMissingDocs([]);
                                        setSendStep("configure");
                                        setSendSnapshot(null);
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    className="bg-green-700 hover:bg-green-800 text-white"
                                    disabled={missingDocs.length > 0}
                                    onClick={() => {
                                        // Freeze the current plan and move to the
                                        // pre-send summary so the office confirms the
                                        // exact emails before anything is sent.
                                        setSendSnapshot(planRef.current);
                                        setSendStep("confirm");
                                    }}
                                >
                                    <Send className="h-4 w-4 mr-1" />
                                    Review &amp; Send
                                </Button>
                            </div>
                        </>
                    ) : (
                        <SendConfirmSummary
                            snapshot={sendSnapshot}
                            isPending={approveAndSend.isPending}
                            onBack={() => setSendStep("configure")}
                            onConfirm={() => {
                                if (!selectedJobId) return;
                                const plan = (sendSnapshot ?? [])
                                    .filter((r) => r.docs.length > 0)
                                    .map((r) => ({
                                        value: r.value,
                                        source: r.source,
                                        contactMethodId: r.contactMethodId,
                                        contactName: r.contactName,
                                        docs: r.docs,
                                        remember: r.remember,
                                    }));
                                const vars: ApproveAndSendVars = {
                                    jobId: selectedJobId,
                                    flags: sendAttachments,
                                    attachmentIds: selectedAttachmentIds,
                                    excludedAttachmentIds: excludedFileIds,
                                    plan,
                                    resend: isSent,
                                };
                                // CE-1: if the preview flagged selected files that
                                // won't ride, gate on the drop warning before the
                                // send instead of silently dropping them. Otherwise
                                // send straight away (the 409 is the backstop).
                                if (previewDropped.length > 0) {
                                    setDropGate({ dropped: previewDropped, vars });
                                } else {
                                    approveAndSend.mutate(vars);
                                }
                                setShowSendModal(false);
                                setSelectedFileIds([]);
                                planRef.current = null;
                                setMissingDocs([]);
                                setSendStep("configure");
                                setSendSnapshot(null);
                            }}
                        />
                    )}
                </div>
            </Dialog>

            {/* CE-1: drop warning. Same dialog whether the drop list came from
                the send preview (before the send) or a 409 on approve-and-send
                (never a raw error). "Send anyway" replays the exact send with
                the confirm flag; "Cancel" backs out without sending. */}
            <Dialog open={!!dropGate} onClose={() => setDropGate(null)} title="Some files won't be attached">
                {dropGate ? (
                    <DroppedAttachmentsWarning
                        dropped={dropGate.dropped}
                        isPending={approveAndSend.isPending}
                        onSendAnyway={() => {
                            approveAndSend.mutate({ ...dropGate.vars, confirmDroppedAttachments: true });
                            setDropGate(null);
                        }}
                        onCancel={() => setDropGate(null)}
                    />
                ) : null}
            </Dialog>
            <Dialog open={!!staffOnlyGate} onClose={() => setStaffOnlyGate(null)} title="No customer email on file">
                {staffOnlyGate ? (
                    <StaffOnlySendGate
                        refusal={staffOnlyGate.refusal}
                        isPending={approveAndSend.isPending}
                        onSendStaffOnly={() => {
                            approveAndSend.mutate({ ...staffOnlyGate.vars, staffOnlyConfirmed: true });
                            setStaffOnlyGate(null);
                        }}
                        onSentAnotherWay={() => {
                            setOtherWayJobId(staffOnlyGate.vars.jobId);
                            setStaffOnlyGate(null);
                        }}
                        onCancel={() => setStaffOnlyGate(null)}
                    />
                ) : null}
            </Dialog>
            {otherWayJobId && (
                <SentAnotherWayDialog
                    jobId={otherWayJobId}
                    open
                    onClose={() => setOtherWayJobId(null)}
                    onSaved={() => void qc.invalidateQueries({ queryKey: ["office-review-queue"] })}
                />
            )}

            <AttachmentPreviewDialog
                open={!!previewAttachment}
                onClose={() => setPreviewAttachment(null)}
                attachmentId={previewAttachment?.id ?? null}
                fileName={previewAttachment?.fileName ?? null}
                allowDownload={previewAttachment?.allowDownload ?? true}
            />

            <Dialog
                open={showRebuildPricingConfirm}
                onClose={() => setShowRebuildPricingConfirm(false)}
                title="Refresh pricing?"
            >
                <div className="space-y-4">
                    <p className="text-sm text-foreground/80 leading-relaxed">
                        This rebuilds the draft invoice from the current pricebook, including jurisdiction fees from the
                        site&apos;s water provider. Only draft (unsent) invoices are affected.
                    </p>
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                        Any manual line edits on the draft will be replaced by freshly generated lines.
                    </p>
                    <RemovedChoicesWarning choices={detail.data?.removedChoices} action="Refresh pricing" />
                    <div className="flex justify-end gap-2 pt-1">
                        <Button
                            variant="outline"
                            onClick={() => setShowRebuildPricingConfirm(false)}
                            disabled={rebuildPricing.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={!selectedJobId || rebuildPricing.isPending}
                            onClick={() => selectedJobId && rebuildPricing.mutate(selectedJobId)}
                        >
                            {rebuildPricing.isPending ? (
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                                <RefreshCw className="h-4 w-4 mr-1" />
                            )}
                            Refresh pricing
                        </Button>
                    </div>
                </div>
            </Dialog>

            {selectedJobId && (
                <CancelJobDialog
                    open={showCancelDialog}
                    onClose={() => setShowCancelDialog(false)}
                    jobId={selectedJobId}
                    jobLabel={d?.job ? `Job #${d.job.display_number || String(d.job.id).slice(0, 8)}` : null}
                    onCancelled={() => setSelectedJobId(null)}
                />
            )}

            {/* #1250 — the job can't be approved until every device that needs a
                permit is answered. Rendered here rather than linked to: this is
                the main approval surface, and pointing at a card on another page
                is the dead end this replaces. */}
            <Dialog
                open={!!permitGate}
                onClose={() => setPermitGate(null)}
                title="This job still needs a permit answer"
            >
                {permitGate ? (
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">{permitGate.message}</p>
                        <PermitCard jobId={permitGate.jobId} canManage={canManageJobs} />
                        <div className="flex flex-wrap justify-end gap-2">
                            <Button variant="outline" onClick={() => setPermitGate(null)}>
                                Close
                            </Button>
                            <Button
                                onClick={() => {
                                    const retry = permitGate.retry;
                                    setPermitGate(null);
                                    retry();
                                }}
                            >
                                Answered — carry on
                            </Button>
                        </div>
                    </div>
                ) : null}
            </Dialog>
            {composeSend && (
                <SendCustomerDialog
                    docType={composeSend.docType}
                    id={composeSend.id}
                    customerId={composeSend.customerId}
                    open={!!composeSend}
                    onOpenChange={(o) => !o && setComposeSend(null)}
                    onSent={() => invalidateAll()}
                />
            )}
        </div>
    );
}

// ─── Queue Item ────────────────────────────────────────────────────────
function QueueItem({ job, selected, onSelect }: { job: QueueJob; selected: boolean; onSelect: () => void }) {
    const conf = CONFIDENCE_STYLES[job.confidence] || CONFIDENCE_STYLES.medium;
    const timeLabel = timeSinceLabel(job.seconds_since_completion);
    // Work Inbox V2: ⚑ N tasks corner tab on the card's top edge.
    const taskFlag = useJobTaskFlag(job.id);

    return (
        <button
            onClick={onSelect}
            className={cn(
                "relative w-full px-4 py-3.5 text-left transition-all duration-150",
                selected ? "bg-card border border-primary/40" : "hover:bg-secondary/60 border border-transparent",
            )}
        >
            <JobTaskCornerTab flag={taskFlag} className="top-1 -right-0.5" />
            <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm truncate text-foreground">{job.title}</span>
                <div className="flex items-center gap-1 shrink-0">
                    {job.kind === "vendor_work_order" && (
                        <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[9px] font-bold border border-blue-300 text-blue-600 dark:border-blue-700 dark:text-blue-400">
                            Work Order
                        </span>
                    )}
                    {job.has_card_on_file && job.has_open_invoice && (
                        <span
                            data-testid="queue-card-chip"
                            className="inline-flex items-center gap-0.5 rounded-md border border-emerald-300 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 dark:border-emerald-700 dark:text-emerald-400"
                            title="This customer has a saved card and an open invoice — collect first, then send"
                        >
                            <CreditCard className="h-2.5 w-2.5" /> card on file
                        </span>
                    )}
                    <span
                        className={cn(
                            "inline-flex cursor-help items-center rounded-md px-1.5 py-0.5 text-[9px] font-bold border",
                            conf.bg,
                            conf.text,
                        )}
                        title={conf.gloss}
                    >
                        {conf.label}
                    </span>
                </div>
            </div>
            {/* M10 (#834): five rail entries can share one billing account name
                ("Cintas Fire National Account") — the site line is what tells
                them apart, and it was never rendered here. */}
            {((job.site_name && job.site_name !== job.company_name) || job.site_address) && (
                <p className="text-[11px] text-foreground/70 mt-0.5 truncate">
                    {job.site_name && job.site_name !== job.company_name && <span>{job.site_name}</span>}
                    {job.site_name && job.site_name !== job.company_name && job.site_address && <span> &middot; </span>}
                    {job.site_address && <span>{job.site_address}</span>}
                </p>
            )}
            <p className="text-xs text-foreground/80 mt-0.5 truncate">
                {job.company_name} &middot; {job.tech_name}
                {job.tax_status_unknown && (
                    <span
                        className="ml-1.5 inline-flex items-center rounded-full border border-amber-400/70 bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
                        title="Customer tax status not set — mark taxable or exempt before sending the invoice"
                    >
                        Tax status?
                    </span>
                )}
            </p>
            {/* Level 2 asset hint */}
            {job.asset_count > 0 && (
                <p className="text-[11px] text-foreground/80 mt-0.5">
                    {job.asset_count === 1
                        ? (formatAssets(job.asset_count, job.asset_types) ?? "Device")
                        : `${job.asset_count} devices`}
                </p>
            )}
            <div className="flex items-center gap-2 mt-1 flex-wrap">
                {job.review_reasons.map((r) => (
                    <Badge
                        key={r}
                        variant="outline"
                        className={cn(
                            "text-[9px] px-1.5 py-0",
                            REASON_GUIDANCE[r] && "cursor-help",
                            reasonPillClass(r),
                        )}
                        title={REASON_GUIDANCE[r]}
                    >
                        {REASON_LABELS[r] || r}
                        {r === "missing_form_fields" && job.mapping_warning_count > 0 && (
                            <span className="ml-1 font-semibold">({job.mapping_warning_count})</span>
                        )}
                    </Badge>
                ))}
                {/* #832 C4: a flag that has sat for a week is a stall, not a
                    label — say its age where the pills are. (Walked live: a
                    "Missing Photos" job sat flagged 23 days looking handled.) */}
                {job.oldest_flag_days != null && job.oldest_flag_days >= 7 && (
                    <Badge
                        variant="outline"
                        data-testid="flag-age"
                        className="text-[9px] px-1.5 py-0 border-red-400 bg-red-50 font-semibold text-red-700 dark:border-red-600 dark:bg-red-900/20 dark:text-red-300"
                        title={`The oldest open flag on this job was filed ${job.oldest_flag_days} days ago and is still open`}
                    >
                        flag {job.oldest_flag_days}d old
                    </Badge>
                )}
                <div className="flex items-center gap-2 ml-auto text-[10px] text-foreground/80">
                    <span className="flex items-center gap-0.5">
                        <ClipboardCheck className="h-3 w-3" /> {job.test_count}
                    </span>
                    <span className="flex items-center gap-0.5">
                        <Camera className="h-3 w-3" /> {job.photo_count}
                    </span>
                    {timeLabel && (
                        <span
                            className={cn(
                                job.seconds_since_completion && job.seconds_since_completion > 86400
                                    ? "text-red-500 font-medium"
                                    : job.seconds_since_completion && job.seconds_since_completion > 14400
                                      ? "text-amber-500"
                                      : "",
                            )}
                        >
                            {timeLabel}
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
}

// ─── Fairness banner (workload equity, surfaced before payroll) ───────
interface FairnessOverview {
    techWeeks: Array<{
        techId: string;
        techName: string;
        weekKey: string;
        points: number;
        pointsPerOnSiteHour: number | null;
        teamMedianPerHour: number | null;
        fairnessFlagged: boolean;
    }>;
}

function FairnessBanner() {
    const { data } = useQuery({
        queryKey: ["performance", "overview"],
        queryFn: () => api.get<FairnessOverview>("/api/performance/overview"),
        staleTime: 5 * 60_000,
    });
    const weeks = data?.techWeeks ?? [];
    const latestWeek = weeks.reduce<string | null>(
        (acc, w) => (acc == null || w.weekKey > acc ? w.weekKey : acc),
        null,
    );
    const flagged = weeks.filter((w) => w.weekKey === latestWeek && w.fairnessFlagged);
    if (flagged.length === 0) return null;
    return (
        <div className="rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3 text-left">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" />
                Workload fairness ({latestWeek})
            </p>
            <p className="text-[11px] text-foreground/80 mt-1">
                Running well above the team's points-per-on-site-hour median — review before payroll:
            </p>
            <ul className="mt-1.5 space-y-0.5">
                {flagged.map((w) => (
                    <li key={w.techId} className="text-xs">
                        <span className="font-medium">{w.techName}</span>{" "}
                        <span className="text-foreground/70">
                            {w.pointsPerOnSiteHour} pts/hr vs median {w.teamMedianPerHour} · {w.points} pts
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

// ─── Queue Summary (shown when no job selected) ───────────────────────
function QueueSummary({ jobs, onStartReview }: { jobs: QueueJob[]; onStartReview: () => void }) {
    const highConf = jobs.filter((j) => j.confidence === "high").length;
    const medConf = jobs.filter((j) => j.confidence === "medium").length;
    const lowConf = jobs.filter((j) => j.confidence === "low").length;
    const highPriority = jobs.find((j) => j.review_priority === 1);

    if (jobs.length === 0) {
        return (
            <div className="flex h-full items-center justify-center text-foreground/80">
                <div className="text-center">
                    <ShieldCheck className="mx-auto mb-3 h-12 w-12 text-emerald-500 opacity-60" />
                    <p className="font-medium text-foreground">Review Queue Empty</p>
                    <p className="text-sm mt-1">All jobs have been reviewed. Nice work.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full items-center justify-center">
            <div className="w-full max-w-md space-y-8 px-6">
                <div className="text-center">
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-purple-50 dark:bg-purple-900/20">
                        <ClipboardCheck className="h-7 w-7 text-purple-600" />
                    </div>
                    <h2 className="text-xl font-black tracking-tight">Office Review</h2>
                    <p className="text-sm text-foreground/80 mt-1.5">
                        {jobs.length} job{jobs.length !== 1 ? "s" : ""} waiting for review
                    </p>
                    {/* #832 C3: every queue says what it counts — four screens
                        showed four numbers for "close-out work" with nothing
                        explaining the difference. */}
                    <p className="text-[11px] text-foreground/60 mt-1.5" data-testid="queue-scope-line">
                        Counts jobs flagged for office checks — failed tests, missing photos, holds, and approval items.
                        It overlaps Closeout (paperwork &amp; billing to finish); the Jobs page&apos;s &ldquo;Needs
                        Review&rdquo; stage also counts legacy pending-action jobs, so its number runs higher.
                    </p>
                </div>

                <div className="grid grid-cols-3 gap-3">
                    <Card className="text-center hover:shadow-[var(--shadow-card-hover)] transition-shadow">
                        <CardContent className="p-4">
                            <p className="text-2xl font-bold tabular-nums text-emerald-600">{highConf}</p>
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground/80 mt-1">
                                High Confidence
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="text-center hover:shadow-[var(--shadow-card-hover)] transition-shadow">
                        <CardContent className="p-4">
                            <p className="text-2xl font-bold tabular-nums text-amber-600">{medConf}</p>
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground/80 mt-1">
                                Review Needed
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="text-center hover:shadow-[var(--shadow-card-hover)] transition-shadow">
                        <CardContent className="p-4">
                            <p className="text-2xl font-bold tabular-nums text-red-600">{lowConf}</p>
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground/80 mt-1">
                                Attention Required
                            </p>
                        </CardContent>
                    </Card>
                </div>

                <FairnessBanner />

                {highPriority && (
                    <div className="rounded-xl border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30 p-4">
                        <p className="text-xs font-semibold text-red-700 dark:text-red-300 flex items-center gap-1.5">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            Highest Priority
                        </p>
                        <p className="text-sm font-semibold mt-1.5">{highPriority.title}</p>
                        <p className="text-xs text-foreground/80 mt-0.5">
                            {highPriority.company_name} &middot; {highPriority.tech_name}
                        </p>
                    </div>
                )}

                <Button className="w-full" size="lg" onClick={onStartReview}>
                    Start Review
                    <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
            </div>
        </div>
    );
}

// ─── Collapsible Section ──────────────────────────────────────────────
function CollapsibleSection({
    title,
    icon,
    children,
    defaultOpen = true,
}: {
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    defaultOpen?: boolean;
}) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <section>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-foreground hover:text-primary transition-colors"
            >
                {icon}
                {title}
                {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
            {open && children}
        </section>
    );
}

// ─── Tech Points Panel ───────────────────────────────────────────────
//
// Shows the point lines the job's work earned plus reconciliation gaps —
// evidence of work (test / repair / replacement rows, invoiced pricebook
// items with a legacy flat amount) that has NO matching point line. Each
// specific gap offers a one-click "Add" that records the missing line for
// the tech via the office gap-fill endpoint.
const POINT_STATUS_STYLES: Record<string, string> = {
    provisional: "border-amber-300 text-amber-700 dark:border-amber-600 dark:text-amber-300",
    approved: "border-emerald-300 text-emerald-700 dark:border-emerald-600 dark:text-emerald-300",
    rejected: "border-red-300 text-red-600 dark:border-red-700 dark:text-red-400",
};

const POINTS_EARNED_SORT: Record<string, (e: PointsEarnedLine) => string | number | null> = {
    tech: (e) => e.techName,
    task: (e) => e.displayLabel ?? e.taskKey,
    device: (e) => e.serialNumber,
    points: (e) => e.points,
    status: (e) => e.status,
};

function TechPointsPanel({
    jobId,
    reconciliation,
    onUpdated,
}: {
    jobId: string;
    reconciliation: PointsReconciliation;
    onUpdated: () => void;
}) {
    const fillGap = useMutation({
        mutationFn: (gap: PointsGap) =>
            api.post(`/api/office-review/jobs/${jobId}/point-events`, {
                userId: gap.suggestedUserId,
                assetId: gap.assetId ?? undefined,
                taskKey: gap.suggestedTaskKey,
                quantity: 1,
            }),
        onSuccess: () => {
            toast.success("Point line added");
            onUpdated();
        },
        onError: (e: any) => {
            const code = e?.body?.error;
            if (code === "ALREADY_RECORDED") {
                toast.info("A point line of this type already exists — refreshing.");
                onUpdated();
                return;
            }
            toast.error(`Add point line failed: ${e?.body?.message ?? code ?? "unknown"}`);
        },
    });

    const { gaps, totalPoints } = reconciliation;
    const {
        visible: earned,
        sortKey,
        sortDir,
        toggleSort,
    } = useTableControls(reconciliation.earned, {
        sortId: "office-review.tech-points",
        sortAccessors: POINTS_EARNED_SORT,
        descFirst: ["points"],
    });
    const sortHeader = (key: string, label: string) => (
        <SortHeaderButton label={label} active={sortKey === key} dir={sortDir} onClick={() => toggleSort(key)} />
    );
    if (earned.length === 0 && gaps.length === 0) return null;

    return (
        <CollapsibleSection
            title={`Tech Points (${totalPoints.toFixed(1)} pts${gaps.length > 0 ? ` — ${gaps.length} missing` : ""})`}
            icon={<DollarSign className="h-4 w-4" />}
            defaultOpen={gaps.length > 0}
        >
            <div className="space-y-2">
                {gaps.map((g, i) => (
                    <div
                        key={`${g.kind}-${g.assetId ?? i}`}
                        className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                    >
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span className="flex-1">{g.message}</span>
                        {g.suggestedTaskKey && g.suggestedUserId && (
                            <Button
                                size="sm"
                                variant="outline"
                                className="shrink-0 border-amber-400 text-amber-800 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-500/20"
                                disabled={fillGap.isPending}
                                onClick={() => fillGap.mutate(g)}
                            >
                                {fillGap.isPending ? (
                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                ) : (
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                )}
                                Add {g.suggestedPoints != null ? `${g.suggestedPoints.toFixed(1)} pts` : "points"}
                            </Button>
                        )}
                    </div>
                ))}
                {earned.length > 0 && (
                    <div className="rounded-lg border overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted text-left text-xs font-semibold text-foreground">
                                <tr>
                                    <th className="px-3 py-2">{sortHeader("tech", "Tech")}</th>
                                    <th className="px-3 py-2">{sortHeader("task", "Task")}</th>
                                    <th className="px-3 py-2">{sortHeader("device", "Device")}</th>
                                    <th className="px-3 py-2 text-right">{sortHeader("points", "Points")}</th>
                                    <th className="px-3 py-2">{sortHeader("status", "Status")}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {earned.map((e) => (
                                    <tr key={e.id}>
                                        <td className="px-3 py-2 text-xs">{e.techName ?? "—"}</td>
                                        <td className="px-3 py-2 text-xs">{e.displayLabel ?? e.taskKey}</td>
                                        <td className="px-3 py-2 font-mono text-[11px]">{e.serialNumber ?? "—"}</td>
                                        <td className="px-3 py-2 text-xs text-right font-medium">
                                            {e.points.toFixed(1)}
                                        </td>
                                        <td className="px-3 py-2">
                                            <Badge
                                                variant="outline"
                                                className={cn(
                                                    "text-[9px]",
                                                    POINT_STATUS_STYLES[e.status] ?? "text-foreground/60",
                                                )}
                                            >
                                                {e.status}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </CollapsibleSection>
    );
}

// ─── Compliance / Mark-as-Filed Panel ────────────────────────────────
//
// Surfaces the per-device city-portal submission records on a job so the
// office can file the report with the jurisdiction and record the
// confirmation number — without leaving the review workspace. Reuses the
// existing PATCH /api/submissions/:id "mark as filed" API.
const SUBMISSION_STATUS_LABELS: Record<string, string> = {
    not_submitted: "Not filed",
    ready_to_file: "Ready to file",
    added_to_cart: "In cart",
    awaiting_checkout: "Awaiting checkout",
    submitted: "Filed",
    accepted: "Accepted",
    rejected: "Rejected",
    correction_required: "Correction required",
};

function ComplianceFilingPanel({ detail, onUpdated }: { detail: ReviewDetail; onUpdated: () => void }) {
    const submissions = detail.submissions ?? [];
    const jobId = detail.job?.id as string | undefined;
    const jurisdiction =
        (detail.job?.water_provider_name as string | undefined) || submissions[0]?.provider_name || null;
    const portalUrl =
        (detail.job?.water_provider_portal_url as string | undefined) ||
        submissions.find((s) => s.portal_url)?.portal_url ||
        null;
    // The merged jurisdiction report is what gets emailed to the city.
    const reportAttachment = (detail.reportAttachments ?? []).find((a) => a.category === "report");

    const [emailOpen, setEmailOpen] = useState(false);

    // H25: once the job has been sent to the customer, the only thing left is
    // the legal filing. Highlight the panel if any device is still awaiting it
    // so the office's eye lands here right after Approve & Send.
    const justSent = !!detail.job?.last_customer_send_at;
    const hasUnfiled = submissions.some((s) => s.status === "ready_to_file" || s.status === "not_submitted");
    const highlight = justSent && hasUnfiled;
    const filedCount = submissions.filter((s) => s.status === "submitted" || s.status === "accepted").length;
    const allFiled = submissions.length > 0 && filedCount === submissions.length;

    return (
        <section
            className={cn(
                "rounded-lg border bg-card p-3 space-y-3",
                highlight ? "border-amber-400 ring-2 ring-amber-300/50 dark:border-amber-500/60" : "border-border",
            )}
        >
            <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold flex items-center gap-1.5 text-foreground">
                    <FileCheck className="h-4 w-4" /> Compliance Filing
                </h3>
                <div className="flex items-center gap-3">
                    {jobId && (
                        <button
                            type="button"
                            onClick={() => setEmailOpen(true)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                        >
                            <Mail className="h-3 w-3" /> Email to city
                        </button>
                    )}
                    {portalUrl && (
                        <a
                            href={portalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                        >
                            Open portal <ExternalLink className="h-3 w-3" />
                        </a>
                    )}
                </div>
            </div>
            {jurisdiction && (
                <p className="text-xs text-foreground/70">
                    Jurisdiction: <span className="font-medium text-foreground">{jurisdiction}</span>
                </p>
            )}
            {/* H25: numbered filing workflow so the legal city-filing step is
                self-explanatory, plus a link to the global queue where every
                ready-to-file device across all jobs lives. */}
            <div className="space-y-2">
                <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs font-medium text-foreground/80">
                    <li className="inline-flex items-center gap-1.5">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                            1
                        </span>
                        Open the portal (or email the city)
                    </li>
                    <ChevronRight className="h-4 w-4 text-foreground/40" />
                    <li className="inline-flex items-center gap-1.5">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                            2
                        </span>
                        Submit the report
                    </li>
                    <ChevronRight className="h-4 w-4 text-foreground/40" />
                    <li className="inline-flex items-center gap-1.5">
                        <span
                            className={cn(
                                "inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold",
                                allFiled ? "bg-success/20 text-success" : "bg-primary/15 text-primary",
                            )}
                        >
                            {allFiled ? <Check className="h-3.5 w-3.5" /> : "3"}
                        </span>
                        Record the confirmation # below
                    </li>
                </ol>
                {submissions.length > 0 && (
                    <div className="space-y-1">
                        <div className="flex items-center justify-between text-[11px] font-medium text-foreground/70">
                            <span>
                                {filedCount} of {submissions.length} filed
                            </span>
                            {allFiled && (
                                <span className="inline-flex items-center gap-1 text-success">
                                    <Check className="h-3 w-3" /> All filed
                                </span>
                            )}
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-foreground/10">
                            <div
                                className={cn(
                                    "h-full rounded-full transition-all",
                                    allFiled ? "bg-success" : "bg-primary",
                                )}
                                style={{
                                    width: `${(filedCount / submissions.length) * 100}%`,
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
            <div className="space-y-2">
                {submissions.map((s) => (
                    <SubmissionRow key={s.id} submission={s} onUpdated={onUpdated} />
                ))}
            </div>
            <a
                href="/submissions?status=ready_to_file"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
                View full City Filing Queue <ArrowRight className="h-3 w-3" />
            </a>
            {emailOpen && jobId && (
                <CityEmailDialog
                    jobId={jobId}
                    draft={detail.cityEmailDraft}
                    reportFileName={reportAttachment?.file_name ?? null}
                    onClose={() => setEmailOpen(false)}
                    onSent={() => {
                        setEmailOpen(false);
                        onUpdated();
                    }}
                />
            )}
        </section>
    );
}

// ─── Email-to-city composer ──────────────────────────────────────────
// Prefilled, fully editable filing email for jurisdictions that accept the
// completed report by email. Sending attaches the merged jurisdiction PDF
// and marks the job's pending submissions as filed.
function CityEmailDialog({
    jobId,
    draft,
    reportFileName,
    onClose,
    onSent,
}: {
    jobId: string;
    draft?: { recipient: string | null; subject: string; message: string };
    reportFileName: string | null;
    onClose: () => void;
    onSent: () => void;
}) {
    const [email, setEmail] = useState(draft?.recipient ?? "");
    const [subject, setSubject] = useState(draft?.subject ?? "");
    const [message, setMessage] = useState(draft?.message ?? "");

    const send = useMutation({
        mutationFn: () =>
            api.post<{ ok: boolean; deliveredTo: string; submissionsFiled: number }>(
                `/api/office-review/jobs/${jobId}/email-to-city`,
                {
                    email: email.trim() || undefined,
                    subject: subject.trim() || undefined,
                    message: message.trim() || undefined,
                },
            ),
        onSuccess: (r) => {
            toast.success(`Report emailed to ${r.deliveredTo}`);
            onSent();
        },
        onError: (e: any) => {
            const code = e?.body?.error;
            toast.error(
                code === "NO_CITY_EMAIL"
                    ? "Enter a city email address to send to."
                    : code === "NO_REPORT"
                      ? "No report PDF found — regenerate the jurisdiction report first."
                      : code === "EMAIL_NOT_CONFIGURED"
                        ? "Email is not configured on this server."
                        : "Couldn't send the report — please try again.",
            );
        },
    });

    return (
        <Dialog open onClose={onClose} title="Email report to city">
            <div className="space-y-3">
                <div>
                    <Label className="text-xs">To (city / water authority)</Label>
                    <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="filing@citywater.gov"
                        className="mt-1"
                    />
                </div>
                <div>
                    <Label className="text-xs">Subject</Label>
                    <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" />
                </div>
                <div>
                    <Label className="text-xs">Message</Label>
                    <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={12}
                        className="mt-1 font-mono text-xs"
                    />
                </div>
                <div className="flex items-center gap-1.5 rounded-md border border-border/70 bg-muted/30 px-3 py-2 text-xs text-foreground/80">
                    <Paperclip className="h-3.5 w-3.5 shrink-0" />
                    {reportFileName ? (
                        <span className="truncate">{reportFileName}</span>
                    ) : (
                        <span className="text-amber-700 dark:text-amber-300">
                            No report PDF found — regenerate the jurisdiction report first.
                        </span>
                    )}
                </div>
                <div className="flex justify-end gap-2 pt-1">
                    <Button type="button" size="sm" variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        disabled={send.isPending || !reportFileName}
                        onClick={() => send.mutate()}
                    >
                        {send.isPending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <>
                                <Send className="h-3.5 w-3.5 mr-1" /> Send to city
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}

function SubmissionRow({ submission, onUpdated }: { submission: Submission; onUpdated: () => void }) {
    const [confirmation, setConfirmation] = useState(submission.portal_confirmation ?? "");
    const isFiled = submission.status === "submitted" || submission.status === "accepted";
    const readyWithConfirmation = submission.status === "ready_to_file" && confirmation.trim().length > 0;

    const markFiled = useMutation({
        mutationFn: () =>
            api.patch(`/api/submissions/${submission.id}`, {
                status: "submitted",
                portalConfirmation: confirmation.trim() || undefined,
            }),
        onSuccess: () => {
            toast.success("Marked as filed");
            onUpdated();
        },
        onError: () => toast.error("Couldn't mark as filed — please try again"),
    });

    const deviceLabel =
        [submission.assembly_type?.toUpperCase(), submission.serial_number].filter(Boolean).join(" · ") ||
        submission.asset_name ||
        "Device";

    return (
        <div className="rounded-md border border-border/70 bg-muted/30 px-3 py-2">
            <div className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate text-xs font-medium text-foreground">{deviceLabel}</span>
                <Badge
                    variant="outline"
                    className={cn(
                        "shrink-0 text-[10px]",
                        isFiled && "border-green-400 text-green-700 dark:text-green-300",
                    )}
                >
                    {SUBMISSION_STATUS_LABELS[submission.status] ?? submission.status}
                </Badge>
            </div>
            {isFiled ? (
                <p className="mt-1 text-[11px] text-foreground/70">
                    {submission.portal_confirmation ? `Confirmation: ${submission.portal_confirmation}` : "Filed"}
                    {submission.submitted_by_name ? ` · by ${submission.submitted_by_name}` : ""}
                </p>
            ) : (
                <div className="mt-2 flex items-center gap-1.5">
                    <Input
                        value={confirmation}
                        onChange={(e) => setConfirmation(e.target.value)}
                        placeholder="Confirmation # (optional)"
                        className="h-8 text-xs"
                    />
                    <Button
                        size="sm"
                        variant={readyWithConfirmation ? "success" : "default"}
                        className="h-8 shrink-0"
                        disabled={markFiled.isPending}
                        onClick={() => markFiled.mutate()}
                    >
                        {markFiled.isPending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <>
                                <Check className="h-3.5 w-3.5 mr-1" />
                                {readyWithConfirmation ? "Confirm filed" : "Mark filed"}
                            </>
                        )}
                    </Button>
                </div>
            )}
        </div>
    );
}

// ─── Photos Section (test-embedded + file attachments) ───────────────
const SLOT_LABELS: Record<string, string> = {
    tag: "Tag",
    device: "Device",
    area: "Area",
    general: "General",
};

// ─── Form Mapping Warnings Panel ──────────────────────────────────────
function FormMappingWarningsPanel({
    attachments,
    onRegenerate,
    isRegenerating,
    onPreview,
}: {
    attachments: ReportAttachment[];
    onRegenerate: () => void;
    isRegenerating: boolean;
    onPreview?: (id: string, fileName: string) => void;
}) {
    const withWarnings = attachments.filter(
        (a) =>
            a.mapping_warnings &&
            (a.mapping_warnings.missingRequired.length > 0 || (a.mapping_warnings.dataWarnings?.length ?? 0) > 0),
    );
    if (withWarnings.length === 0) return null;
    const allMissing = new Set<string>();
    const allDataWarnings = new Set<string>();
    let totalFilled = 0;
    let totalFields = 0;
    for (const a of withWarnings) {
        if (!a.mapping_warnings) continue;
        for (const m of a.mapping_warnings.missingRequired) allMissing.add(m);
        for (const w of a.mapping_warnings.dataWarnings ?? []) allDataWarnings.add(w);
        totalFilled += a.mapping_warnings.fieldsFilled;
        totalFields += a.mapping_warnings.fieldsTotal;
    }

    return (
        <section className="rounded-lg border border-amber-300 bg-amber-50/50 dark:bg-amber-900/10 p-3">
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold flex items-center gap-1.5 text-amber-700 dark:text-amber-300">
                        <AlertTriangle className="h-4 w-4" />
                        Form Mapping Warnings
                    </h3>
                    {allMissing.size > 0 && (
                        <>
                            <p className="text-xs text-foreground/80 mt-1">
                                The jurisdiction report was generated but {allMissing.size} required field
                                {allMissing.size === 1 ? "" : "s"} could not be filled. Filled {totalFilled} of{" "}
                                {totalFields} total fields.
                            </p>
                            <ul className="mt-2 flex flex-wrap gap-1">
                                {Array.from(allMissing).map((field) => (
                                    <li
                                        key={field}
                                        className="inline-flex rounded border border-amber-300 bg-white dark:bg-amber-900/30 px-1.5 py-0.5 text-[10px] text-amber-700 dark:text-amber-300"
                                    >
                                        {field}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    {allDataWarnings.size > 0 && (
                        <ul className={cn("space-y-1", allMissing.size > 0 ? "mt-2" : "mt-1")}>
                            {Array.from(allDataWarnings).map((warning) => (
                                <li key={warning} className="text-xs text-amber-700 dark:text-amber-300">
                                    {warning}
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="mt-2 text-xs text-foreground/80">
                        PDF generated — verify the preview looks correct.
                        {onPreview && withWarnings[0] && (
                            <>
                                {" "}
                                <button
                                    type="button"
                                    onClick={() => onPreview(withWarnings[0]!.id, withWarnings[0]!.file_name)}
                                    className="font-medium text-amber-700 underline underline-offset-2 hover:text-amber-800 dark:text-amber-300 dark:hover:text-amber-200"
                                >
                                    Preview PDF to verify
                                </button>
                            </>
                        )}
                    </p>
                </div>
                <button
                    onClick={onRegenerate}
                    disabled={isRegenerating}
                    className="flex-shrink-0 flex items-center gap-1 rounded-lg bg-amber-700 hover:bg-amber-800 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                >
                    {isRegenerating ? "Regenerating..." : "Regenerate Report"}
                </button>
            </div>
        </section>
    );
}

// ─── Technician Notes Section ─────────────────────────────────────────
function TechNotesSection({ job, testResults }: { job: Record<string, any>; testResults: TestResult[] }) {
    const allNotes: Array<{ label: string; text: string }> = [];

    if (job.notes) allNotes.push({ label: "Job Notes", text: job.notes });

    for (const t of testResults) {
        if (t.visual_condition_notes) {
            allNotes.push({
                label: `${t.serial_number ?? t.asset_name ?? "Device"} — Visual Condition`,
                text: t.visual_condition_notes,
            });
        }
        if (t.failure_notes) {
            allNotes.push({
                label: `${t.serial_number ?? t.asset_name ?? "Device"} — Failure Notes`,
                text: t.failure_notes,
            });
        }
        if (t.completion_notes) {
            allNotes.push({
                label: `${t.serial_number ?? t.asset_name ?? "Device"} — Completion Notes`,
                text: t.completion_notes,
            });
        }
        if (t.remarks) {
            allNotes.push({ label: `${t.serial_number ?? t.asset_name ?? "Device"} — Remarks`, text: t.remarks });
        }
    }

    if (allNotes.length === 0) return null;

    return (
        <section>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-foreground">
                <MessageSquarePlus className="h-4 w-4" /> Technician Notes ({allNotes.length})
            </h3>
            <div className="space-y-2">
                {allNotes.map((n, i) => (
                    <div key={i} className="rounded-lg border bg-muted p-3">
                        <p className="text-[10px] font-semibold text-foreground/80 uppercase tracking-wide mb-1">
                            {n.label}
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{n.text}</p>
                    </div>
                ))}
            </div>
        </section>
    );
}

function TestPhotosSection({
    testPhotos,
    filePhotos,
    onPreview,
}: {
    testPhotos: TestPhoto[];
    filePhotos: any[];
    onPreview: (id: string, fileName: string) => void;
}) {
    const totalCount = testPhotos.length + filePhotos.length;
    if (totalCount === 0) return null;

    // Group test photos by device serial number
    const byDevice = new Map<string, TestPhoto[]>();
    for (const tp of testPhotos) {
        const key = tp.serialNumber || "Unknown Device";
        if (!byDevice.has(key)) byDevice.set(key, []);
        byDevice.get(key)!.push(tp);
    }

    return (
        <section>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5 text-foreground">
                <Camera className="h-4 w-4" /> Photos ({totalCount})
            </h3>

            {/* Test-embedded photos grouped by device */}
            {Array.from(byDevice.entries()).map(([serial, photos]) => (
                <div key={serial} className="mb-3">
                    <p className="text-xs font-medium text-foreground/80 mb-1.5">{serial}</p>
                    <div className="grid grid-cols-3 gap-2">
                        {photos.map((tp, idx) => (
                            <div
                                key={`${tp.assetId}-${tp.slot}-${idx}`}
                                className="group relative aspect-square overflow-hidden rounded-lg border bg-muted"
                            >
                                <img
                                    src={tp.dataUrl}
                                    alt={`${serial} ${tp.slot}`}
                                    className="h-full w-full object-cover"
                                />
                                <div className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1 text-[10px] text-white font-medium">
                                    {SLOT_LABELS[tp.slot] ?? tp.slot}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {/* File-based attachments */}
            {filePhotos.length > 0 && (
                <div className={testPhotos.length > 0 ? "mt-3 pt-3 border-t border-border/40" : ""}>
                    {testPhotos.length > 0 && (
                        <p className="text-xs font-medium text-foreground/80 mb-1.5">Uploaded Attachments</p>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                        {filePhotos.map((p: any) => (
                            <button
                                key={p.id}
                                type="button"
                                onClick={() => onPreview(p.id, photoCaption(p))}
                                className="group relative aspect-square overflow-hidden rounded-lg border bg-muted text-left focus:outline-none focus:ring-2 focus:ring-ring"
                                title="Click to preview"
                            >
                                <AttachmentImage
                                    attachmentId={p.id}
                                    alt={photoCaption(p)}
                                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                                    loading="lazy"
                                />
                                <div className="absolute inset-x-0 bottom-0 line-clamp-2 bg-black/60 px-2 py-1 text-[10px] leading-tight text-white font-medium">
                                    {photoCaption(p)}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}

// ─── Files & Reports Section ──────────────────────────────────────────
function JobFilesSection({
    reports,
    documents,
    onPreview,
}: {
    reports: ReportAttachment[];
    documents: JobDocument[];
    onPreview: (id: string, fileName: string, allowDownload?: boolean) => void;
}) {
    // Customer signatures are view-only — they must never be downloadable as a
    // raw file. Identify them by the signature photo_tag or filename.
    const isSignatureDoc = (doc: JobDocument) =>
        doc.photo_tag === "signature" || /^signature\.(png|jpe?g)$/i.test(doc.file_name ?? "");
    const files = [
        ...reports.map((r) => ({
            id: r.id,
            name: r.file_name,
            label: r.category === "summary" ? "Summary" : "Report",
            created_at: r.created_at,
            allowDownload: true,
        })),
        ...documents.map((doc) => ({
            id: doc.id,
            name: doc.file_name,
            label: isSignatureDoc(doc) ? "Signature (view only)" : (doc.photo_tag ?? "Document"),
            created_at: doc.created_at,
            allowDownload: !isSignatureDoc(doc),
        })),
    ];
    if (files.length === 0) return null;

    return (
        <section className="mt-4 pt-4 border-t border-border/40">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5 text-foreground">
                <FileText className="h-4 w-4" /> Files &amp; Reports ({files.length})
            </h3>
            <div className="space-y-1.5">
                {files.map((f) => (
                    <button
                        key={f.id}
                        type="button"
                        onClick={() => onPreview(f.id, f.name, f.allowDownload)}
                        className="w-full flex items-center gap-2 rounded-lg border border-border/60 bg-card px-3 py-2 text-left hover:bg-muted/50 transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                        <Paperclip className="h-4 w-4 shrink-0 text-foreground/60" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate text-foreground">{f.name}</p>
                            <p className="text-[10px] text-foreground/60">{f.label}</p>
                        </div>
                        <Eye className="h-4 w-4 shrink-0 text-foreground/40" />
                    </button>
                ))}
            </div>
        </section>
    );
}

// ─── Vendor Work Order Review Section ─────────────────────────────────
function fmtWoDateTime(iso: string | null | undefined): string {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

// ─── Closeout Work Order slot (CLOSEOUT-PACKET-HOLD PR-1, approved UI
// contract 2026-07-17 — match exactly, deviations are bugs) ─────────────
//
// A strip at the TOP of the Work Order card with three states:
//   1. no WO at all           → large amber-dashed drop zone
//   2. generated, unreviewed  → row + "needs review" tag + view-then-confirm
//                               "Mark reviewed", quiet gray drop zone below
//   3. uploaded present       → green "this is what the customer gets" row;
//                               generated (if any) struck through + one-time
//                               skippable divergence reason chips
const WO_DIVERGENCE_REASONS: Array<{ key: string; label: string }> = [
    { key: "form_was_wrong", label: "Form was wrong" },
    { key: "missing_fields", label: "Missing fields" },
    { key: "faster_on_paper", label: "Faster on paper" },
    { key: "tech_never_used_app", label: "Tech never used the app" },
];

function WorkOrderSlot({
    jobId,
    filled,
    uploaded,
    onPreview,
}: {
    jobId: string;
    filled: JobDocument[];
    uploaded: JobDocument[];
    onPreview: (id: string, fileName: string) => void;
}) {
    const qc = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);
    // View-then-confirm: "Mark reviewed" enables only after View was opened
    // in THIS session (persisted confirmations arrive as reviewed_at).
    const [viewedIds, setViewedIds] = useState<Set<string>>(new Set());
    const divergenceKey = `office-review:wo-divergence:${jobId}`;
    const [divergenceDone, setDivergenceDone] = useState(() => !!localStorage.getItem(divergenceKey));

    const generated = filled[0] ?? null;
    const upload = uploaded[0] ?? null;

    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ["office-review-detail", jobId] });
        qc.invalidateQueries({ queryKey: ["office-review-queue"] });
    };

    const uploadWo = useMutation({
        mutationFn: async (files: File[]) => {
            const fd = new FormData();
            for (const f of files) fd.append("files", f);
            fd.append("jobId", jobId);
            return api.postForm<{ attachment: { id: string }; pages: number }>(
                "/api/attachments/work-order-upload",
                fd,
            );
        },
        onSuccess: (r) => {
            toast.success(
                `Paper work order uploaded (${r.pages} page${r.pages === 1 ? "" : "s"}) — this is what the customer gets.`,
            );
            invalidate();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Upload failed — please try again."),
    });

    const markReviewed = useMutation({
        mutationFn: (attachmentId: string) =>
            api.post(`/api/office-review/attachments/${attachmentId}/wo-reviewed`, {}),
        onSuccess: () => {
            toast.success("Work order marked reviewed");
            invalidate();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Could not mark reviewed."),
    });

    const sendDivergence = useMutation({
        mutationFn: (reason: string) => api.post(`/api/office-review/jobs/${jobId}/wo-divergence`, { reason }),
        onSuccess: () => {
            localStorage.setItem(divergenceKey, "1");
            setDivergenceDone(true);
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Could not record the reason."),
    });

    const takeFiles = (list: FileList | File[] | null) => {
        const files = Array.from(list ?? []);
        if (files.length > 0) uploadWo.mutate(files);
    };
    const dropHandlers = {
        onDragOver: (e: React.DragEvent) => {
            e.preventDefault();
            setDragOver(true);
        },
        onDragLeave: () => setDragOver(false),
        onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            setDragOver(false);
            takeFiles(e.dataTransfer?.files ?? null);
        },
    };
    const viewGenerated = () => {
        if (!generated) return;
        setViewedIds((prev) => new Set(prev).add(generated.id));
        onPreview(generated.id, generated.file_name);
    };

    const fileInput = (
        <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="application/pdf,image/*"
            className="hidden"
            data-testid="wo-slot-file-input"
            onChange={(e) => {
                takeFiles(e.target.files);
                e.target.value = "";
            }}
        />
    );

    // STATE 3: uploaded — green, "this is what the customer gets".
    if (upload) {
        return (
            <div className="space-y-2" data-testid="wo-slot-state-3">
                {fileInput}
                <div className="rounded-lg border border-emerald-400/60 bg-emerald-50/60 dark:bg-emerald-900/15 px-3 py-2.5">
                    <div className="flex items-center gap-2">
                        <FileCheck className="h-4 w-4 shrink-0 text-emerald-600" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                                Paper work order — uploaded ✓ this is what the customer gets
                            </p>
                            <p className="text-[11px] text-foreground/60 truncate">
                                {upload.file_name}
                                {upload.notes ? ` · ${upload.notes}` : ""}
                                {upload.uploaded_by_name ? ` · ${upload.uploaded_by_name}` : ""}
                                {` · ${fmtWoDateTime(upload.created_at)}`}
                            </p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => onPreview(upload.id, upload.file_name)}>
                            <Eye className="h-3.5 w-3.5 mr-1" /> View
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={uploadWo.isPending}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {uploadWo.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                            ) : (
                                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                            )}
                            Replace
                        </Button>
                    </div>
                </div>
                {generated && (
                    <div className="px-1 text-[11px] text-muted-foreground">
                        <button
                            type="button"
                            onClick={viewGenerated}
                            className="line-through hover:text-foreground/70 underline-offset-2"
                            title="Open the generated version for comparison"
                        >
                            Workflow work order (generated) — excluded from the packet, kept on file for comparison
                        </button>
                    </div>
                )}
                {generated && !divergenceDone && (
                    <div className="flex items-center gap-1.5 flex-wrap px-1" data-testid="wo-divergence-chips">
                        <span className="text-[11px] text-foreground/60">Why paper over the generated one?</span>
                        {WO_DIVERGENCE_REASONS.map((r) => (
                            <button
                                key={r.key}
                                type="button"
                                disabled={sendDivergence.isPending}
                                onClick={() => sendDivergence.mutate(r.key)}
                                className="rounded-full border border-border/70 px-2 py-0.5 text-[11px] text-foreground/70 hover:bg-secondary/70 transition-colors"
                            >
                                {r.label}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => {
                                localStorage.setItem(divergenceKey, "1");
                                setDivergenceDone(true);
                            }}
                            className="rounded-full px-2 py-0.5 text-[11px] text-foreground/50 hover:text-foreground/80"
                        >
                            Skip
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // STATE 2: generated exists, no upload — review gate + quiet drop zone.
    if (generated) {
        const reviewed = !!generated.reviewed_at;
        const viewed = viewedIds.has(generated.id);
        return (
            <div className="space-y-2" data-testid="wo-slot-state-2">
                {fileInput}
                <div className="rounded-lg border border-border/70 bg-card px-3 py-2.5">
                    <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 shrink-0 text-foreground/70" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground flex items-center gap-1.5 flex-wrap">
                                Workflow work order (generated)
                                {reviewed ? (
                                    <span className="inline-flex items-center gap-0.5 rounded-md border border-emerald-300 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 dark:border-emerald-700 dark:text-emerald-400">
                                        <Check className="h-2.5 w-2.5" /> reviewed
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 dark:border-amber-600 dark:bg-amber-950/40 dark:text-amber-400">
                                        needs review
                                    </span>
                                )}
                            </p>
                            <p className="text-[11px] text-foreground/60 truncate">
                                {generated.file_name}
                                {reviewed && generated.reviewed_by_name
                                    ? ` · reviewed by ${generated.reviewed_by_name} ${fmtWoDateTime(generated.reviewed_at)}`
                                    : ""}
                            </p>
                        </div>
                        <Button size="sm" variant="outline" onClick={viewGenerated}>
                            <Eye className="h-3.5 w-3.5 mr-1" /> View
                        </Button>
                        {!reviewed && (
                            <Button
                                size="sm"
                                disabled={!viewed || markReviewed.isPending}
                                title={
                                    viewed
                                        ? "Confirm the generated work order is correct for the customer packet."
                                        : "Open View first — review before confirming."
                                }
                                onClick={() => markReviewed.mutate(generated.id)}
                                data-testid="wo-mark-reviewed"
                            >
                                {markReviewed.isPending ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                                ) : (
                                    <Check className="h-3.5 w-3.5 mr-1" />
                                )}
                                Mark reviewed
                            </Button>
                        )}
                    </div>
                </div>
                {!reviewed && (
                    <p className="px-1 text-[11px] text-amber-700 dark:text-amber-400" data-testid="wo-gate-status">
                        Waiting on work order — upload the paper copy or review the generated one.
                    </p>
                )}
                <button
                    type="button"
                    {...dropHandlers}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadWo.isPending}
                    className={cn(
                        "w-full rounded-md border border-dashed px-3 py-2 text-left text-[11px] transition-colors",
                        dragOver
                            ? "border-primary/60 bg-primary/5 text-foreground/80"
                            : "border-border/60 text-foreground/50 hover:text-foreground/70 hover:border-border",
                    )}
                    data-testid="wo-slot-quiet-dropzone"
                >
                    {uploadWo.isPending ? (
                        <span className="inline-flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" /> Uploading…
                        </span>
                    ) : (
                        "Got the paper copy instead? Drop it here — it replaces the generated version on the packet."
                    )}
                </button>
            </div>
        );
    }

    // STATE 1: no WO at all — large amber-dashed drop zone.
    return (
        <div className="space-y-2" data-testid="wo-slot-state-1">
            {fileInput}
            <button
                type="button"
                {...dropHandlers}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadWo.isPending}
                className={cn(
                    "w-full rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors",
                    dragOver
                        ? "border-amber-500 bg-amber-100/60 dark:bg-amber-900/30"
                        : "border-amber-400/70 bg-amber-50/60 hover:bg-amber-100/50 dark:border-amber-600/60 dark:bg-amber-950/20 dark:hover:bg-amber-900/25",
                )}
                data-testid="wo-slot-dropzone"
            >
                {uploadWo.isPending ? (
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-300">
                        <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
                    </span>
                ) : (
                    <>
                        <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                            Drop the tech's completed paper work order here
                        </p>
                        <p className="mt-1 text-[11px] text-amber-700/80 dark:text-amber-400/80">
                            Photo or PDF · multiple photos merge into one document, in order · or browse files
                        </p>
                    </>
                )}
            </button>
            <p className="px-1 text-[11px] text-amber-700 dark:text-amber-400" data-testid="wo-gate-status">
                Waiting on work order — upload the paper copy or review the generated one.
            </p>
        </div>
    );
}

function WorkOrderReviewSection({
    detail,
    onPreview,
    onSend,
    isSending,
    onRefill,
    isRefilling,
    deliveryFailed,
    noReturnPdf,
}: {
    detail: ReviewDetail;
    onPreview: (id: string, fileName: string) => void;
    onSend: (ccCustomer: boolean) => void;
    isSending: boolean;
    onRefill: () => void;
    isRefilling: boolean;
    deliveryFailed: boolean;
    noReturnPdf: boolean;
}) {
    const [ccCustomer, setCcCustomer] = useState(false);
    const job = detail.job ?? {};
    const briefing = (job.work_order_briefing ?? {}) as Record<string, any>;
    const officeOnly = (job.office_only_data ?? {}) as Record<string, any>;
    const comp = detail.workOrderCompletion ?? null;
    const attachments = detail.workOrderAttachments ?? [];
    const filled = attachments.filter((a) => a.category === "work_order_filled");
    const uploaded = attachments.filter((a) => a.category === "work_order_uploaded");
    const originals = attachments.filter((a) => a.category === "document");
    const customerFields = (comp?.customer_fields ?? {}) as Record<string, unknown>;

    return (
        <section className="space-y-3 rounded-lg border border-primary/30 bg-primary/[0.03] p-4">
            <h3 className="text-sm font-semibold flex items-center gap-1.5 text-foreground">
                <ClipboardCheck className="h-4 w-4" /> Work Order
                {briefing.work_order_number && (
                    <span className="ml-1 font-mono text-xs text-foreground/60">#{briefing.work_order_number}</span>
                )}
            </h3>

            {/* WO slot strip (UI contract 2026-07-17) — states 1/2/3. */}
            {job.id && (
                <WorkOrderSlot jobId={String(job.id)} filled={filled} uploaded={uploaded} onPreview={onPreview} />
            )}

            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                {briefing.scope_of_work && (
                    <div className="col-span-2">
                        <span className="text-foreground/60">Scope: </span>
                        {briefing.scope_of_work}
                    </div>
                )}
                <div>
                    <span className="text-foreground/60">IVR in: </span>
                    {fmtWoDateTime(job.ivr_checkin_at)}
                    {job.ivr_checkin_confirmation ? ` (${job.ivr_checkin_confirmation})` : ""}
                </div>
                <div>
                    <span className="text-foreground/60">IVR out: </span>
                    {fmtWoDateTime(job.ivr_checkout_at)}
                    {job.ivr_checkout_confirmation ? ` (${job.ivr_checkout_confirmation})` : ""}
                </div>
                {comp && (
                    <>
                        <div>
                            <span className="text-foreground/60">Techs: </span>
                            {comp.tech_count ?? "—"}
                        </div>
                        <div>
                            <span className="text-foreground/60">Confirmation #: </span>
                            {comp.confirmation_number ?? "—"}
                        </div>
                        {comp.work_performed && (
                            <div className="col-span-2">
                                <span className="text-foreground/60">Work performed: </span>
                                {comp.work_performed}
                            </div>
                        )}
                        {comp.entered_by_user_id && comp.entered_by_name && (
                            // Helper Mode v2 both-name attribution
                            <div className="col-span-2 text-[12px] text-amber-700 dark:text-amber-400">
                                Drafted by: {comp.entered_by_name} · Reviewed &amp; signed:{" "}
                                {comp.reviewed_by_name || job.tech_name || "tech"}
                            </div>
                        )}
                    </>
                )}
            </div>

            {comp?.deficiencies_found && (
                <div className="rounded-md border border-red-400/50 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm">
                    <span className="font-semibold text-red-700 dark:text-red-300 flex items-center gap-1">
                        <AlertTriangle className="h-3.5 w-3.5" /> Deficiencies found
                    </span>
                    {comp.deficiency_description && <div className="mt-0.5">{comp.deficiency_description}</div>}
                </div>
            )}

            {/* Ack skipped (Issue #710): the WO paper is INTENTIONALLY unsigned —
                say so loudly so nobody investigates a blank ack as a mistake.
                The PDF itself prints "Not signed — <reason>" on the signature
                line; this banner is the office-side half of the same flag. */}
            {comp?.ack_skipped_reason && (
                <div className="rounded-md border border-amber-400/50 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm">
                    <span className="font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-1">
                        <AlertTriangle className="h-3.5 w-3.5" /> Customer acknowledgement skipped
                    </span>
                    <div className="mt-0.5">
                        {comp.ack_skipped_reason} — the work order is unsigned by design. Get a signature (revisit or
                        e-sign) before sending, or send with the skip noted.
                    </div>
                </div>
            )}

            {Object.keys(customerFields).length > 0 && (
                <div className="text-xs text-foreground/80">
                    <span className="text-foreground/60">From the customer: </span>
                    {/* Was `${k}: ${v}` straight off Object.entries, so the
                        office read raw column names — "site_contact_phone: …".
                        The keys are open-ended (they come from the form), so
                        there's no label map to write; sentence-casing is
                        legible where the raw key was not (#1201). */}
                    {Object.entries(customerFields)
                        .map(([k, v]) => `${humanFieldName(k)}: ${String(v)}`)
                        .join(" · ")}
                </div>
            )}

            {(officeOnly.nte_amount ||
                officeOnly.corp_authorization ||
                officeOnly.office_notes ||
                (Array.isArray(officeOnly.scope_office_lines) && officeOnly.scope_office_lines.length > 0)) && (
                <div className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs space-y-0.5">
                    <div className="font-semibold text-foreground/70">Office only</div>
                    {officeOnly.nte_amount && <div>NTE: {String(officeOnly.nte_amount)}</div>}
                    {officeOnly.corp_authorization && <div>Corp auth: {String(officeOnly.corp_authorization)}</div>}
                    {officeOnly.office_notes && <div>{String(officeOnly.office_notes)}</div>}
                    {/* Vendor terms the Notes-for-Tech splitter routed away from
                        the tech (pre-scheduling, invoicing, submit deadlines). */}
                    {Array.isArray(officeOnly.scope_office_lines) &&
                        officeOnly.scope_office_lines.map((line: string, i: number) => (
                            <div key={i}>• {String(line)}</div>
                        ))}
                </div>
            )}

            {/* The completed WO (generated or uploaded) renders in the slot
                strip above; only the vendor's ORIGINAL scan lists here. */}
            {originals.length > 0 && (
                <div className="space-y-1.5">
                    {originals.map((a) => (
                        <button
                            key={a.id}
                            type="button"
                            onClick={() => onPreview(a.id, a.file_name)}
                            className="w-full flex items-center gap-2 rounded-lg border border-border/60 bg-card px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                        >
                            <Paperclip className="h-4 w-4 shrink-0 text-foreground/60" />
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium truncate text-foreground">{a.file_name}</p>
                                <p className="text-[10px] text-foreground/60">Original work order</p>
                            </div>
                            <Eye className="h-4 w-4 shrink-0 text-foreground/40" />
                        </button>
                    ))}
                </div>
            )}

            {deliveryFailed && (
                <div className="rounded-md border border-red-400/50 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm">
                    <span className="font-semibold text-red-700 dark:text-red-300 flex items-center gap-1">
                        <AlertTriangle className="h-3.5 w-3.5" /> Delivery failed
                    </span>
                    <div className="mt-0.5 text-xs text-red-700/80 dark:text-red-300/80">
                        The completed work order could not be emailed to the vendor. Retry the send below.
                    </div>
                </div>
            )}
            {noReturnPdf && filled.length === 0 && (
                <div className="rounded-md border border-amber-400/50 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                    No return PDF was generated — the vendor's template isn't mapped.{" "}
                    {detail.workOrderIntakeItemId ? (
                        <>
                            <Link
                                to={`/jobs/work-orders/review/${detail.workOrderIntakeItemId}`}
                                className="font-semibold underline underline-offset-2"
                            >
                                Fix the template mapping
                            </Link>
                            , then regenerate and resend.
                        </>
                    ) : (
                        "Map the template on the WO review page, then resend."
                    )}
                </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-1 flex-wrap">
                <div className="text-xs text-foreground/60">
                    {comp?.delivered_at
                        ? `Sent ${fmtWoDateTime(comp.delivered_at)}${comp.delivered_to ? ` to ${comp.delivered_to}` : ""}`
                        : "Not yet delivered"}
                </div>
                <div className="flex items-center gap-3">
                    {detail.workOrderIntakeItemId && (
                        <Link
                            to={`/jobs/work-orders/review/${detail.workOrderIntakeItemId}`}
                            className="text-xs font-medium text-foreground/70 underline underline-offset-2 hover:text-foreground"
                            title="Reopen the WO field mapper to correct where values stamp on the return PDF, then regenerate."
                        >
                            Fix template mapping
                        </Link>
                    )}
                    {comp?.submitted_at != null && (
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={isRefilling}
                            title="Re-run the PDF fill from the tech's submitted values (e.g. after fixing the template). Does not send anything."
                            onClick={() => onRefill()}
                        >
                            {isRefilling ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                            ) : (
                                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                            )}
                            Regenerate PDF
                        </Button>
                    )}
                    <label className="flex items-center gap-1.5 text-xs text-foreground/70 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            className="h-3.5 w-3.5 accent-primary"
                            checked={ccCustomer}
                            onChange={(e) => setCcCustomer(e.target.checked)}
                        />
                        CC customer
                    </label>
                    <Button
                        size="sm"
                        variant={deliveryFailed ? "destructive" : "default"}
                        disabled={isSending || filled.length === 0}
                        onClick={() => onSend(ccCustomer)}
                    >
                        {isSending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                        ) : (
                            <Send className="h-3.5 w-3.5 mr-1" />
                        )}
                        {deliveryFailed
                            ? "Retry send"
                            : comp?.delivered_at
                              ? "Resend work order"
                              : "Send completed work order"}
                    </Button>
                </div>
            </div>
        </section>
    );
}

// ─── Invoice Section ──────────────────────────────────────────────────
function InvoiceSection({
    invoices,
    onSend,
    isSending,
    onPatchLine,
    onDeleteLine,
    onRecordPayment,
    isRecordingPayment,
}: {
    invoices: Invoice[];
    onSend: (id: string) => void;
    isSending: boolean;
    onPatchLine: (
        invoiceId: string,
        lineId: string,
        body: { description?: string; qty?: number; unit_price?: number; labor_hours?: number; labor_techs?: number },
    ) => void;
    onDeleteLine: (invoiceId: string, lineId: string) => void;
    onRecordPayment: (invoiceId: string, body: { method: string; reference?: string; amount?: number }) => void;
    isRecordingPayment: boolean;
}) {
    const [editingLine, setEditingLine] = useState<{
        invoiceId: string;
        id: string;
        description: string;
        qty: string;
        unit_price: string;
    } | null>(null);
    const [paymentDialog, setPaymentDialog] = useState<{ invoiceId: string; totalAmount: number } | null>(null);
    const [paymentForm, setPaymentForm] = useState({ method: "Check", reference: "", amount: "" });
    const [previewInvoice, setPreviewInvoice] = useState<{ id: string; label: string } | null>(null);

    return (
        <section className="space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-1.5 text-foreground">
                <Receipt className="h-4 w-4" /> Invoices ({invoices.length})
            </h3>
            {invoices.map((inv) => {
                // C2: a shell invoice ($0 total or no line items) must never be
                // emailed to a customer. Flag it and block the per-invoice Send.
                const nonBillable =
                    !["paid", "void", "cancelled"].includes(inv.status) &&
                    (Number(inv.total_amount) <= 0 || !inv.lines || inv.lines.length === 0);
                return (
                    <div key={inv.id} className="rounded-lg border">
                        <div className="flex items-center justify-between border-b bg-muted px-4 py-2.5">
                            <div>
                                <h4 className="text-sm font-semibold flex items-center gap-2">
                                    {inv.invoice_number ?? inv.title}
                                </h4>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <Badge variant="outline" className={cn("text-[10px]", statusColor(inv.status))}>
                                        {inv.status}
                                    </Badge>
                                    <span className="text-base font-bold">${Number(inv.total_amount).toFixed(2)}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <a
                                    href={`/invoices/${inv.id}`}
                                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                                >
                                    Open <ArrowRight className="h-3 w-3" />
                                </a>
                                <button
                                    onClick={() =>
                                        setPreviewInvoice({
                                            id: inv.id,
                                            label: inv.invoice_number ?? inv.title ?? recordLabel("Invoice"),
                                        })
                                    }
                                    className="flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-medium hover:bg-accent"
                                    title="Preview & print PDF"
                                >
                                    <Printer className="h-3 w-3" /> Preview
                                </button>
                                {["open", "sent"].includes(inv.status) && (
                                    <button
                                        onClick={() => {
                                            setPaymentForm({ method: "Check", reference: "", amount: "" });
                                            setPaymentDialog({
                                                invoiceId: inv.id,
                                                totalAmount: Number(inv.total_amount),
                                            });
                                        }}
                                        className="flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-medium hover:bg-accent"
                                    >
                                        <DollarSign className="h-3 w-3" /> Pay
                                    </button>
                                )}
                                {inv.status === "draft" ? (
                                    <button
                                        onClick={() => onSend(inv.id)}
                                        disabled={isSending || nonBillable}
                                        title={nonBillable ? "Add pricing or line items before sending" : undefined}
                                        className="flex items-center gap-1 rounded-lg bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSending ? (
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                            <Send className="h-3 w-3" />
                                        )}{" "}
                                        Send
                                    </button>
                                ) : inv.status === "open" ? (
                                    <button
                                        onClick={() => onSend(inv.id)}
                                        disabled={isSending}
                                        title="Invoice already issued — resend to customer"
                                        className="flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-medium hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSending ? (
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                            <Send className="h-3 w-3" />
                                        )}{" "}
                                        Resend
                                    </button>
                                ) : null}
                            </div>
                        </div>
                        {nonBillable && (
                            <div className="flex items-start gap-2 border-b border-destructive/30 bg-destructive/10 px-4 py-2 text-xs text-destructive">
                                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                <span>
                                    This invoice has {Number(inv.total_amount) <= 0 ? "a $0 total" : "no line items"}{" "}
                                    and cannot be sent. Add pricing or remove the invoice before approving &amp;
                                    sending.
                                </span>
                            </div>
                        )}
                        {inv.lines && inv.lines.length > 0 && (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-left text-xs font-semibold text-foreground">
                                        <tr>
                                            {/* min-width keeps long descriptions wrapping at
                                            phrase width instead of one word per row. */}
                                            <th className="px-3 py-1.5 min-w-[240px]">Description</th>
                                            <th className="px-3 py-1.5">Pricebook Item</th>
                                            <th className="px-3 py-1.5 text-right">Qty</th>
                                            <th className="px-3 py-1.5 text-right">Price</th>
                                            <th className="px-3 py-1.5 text-right">Total</th>
                                            {!["paid", "void"].includes(inv.status) && (
                                                <th className="px-2 py-1.5 w-14" />
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {inv.lines.map((line) => {
                                            const isEditing =
                                                editingLine?.id === line.id && editingLine?.invoiceId === inv.id;
                                            return (
                                                <tr key={line.id}>
                                                    <td
                                                        className="px-3 py-1.5 min-w-[240px]"
                                                        title={stripHtml(line.description)}
                                                    >
                                                        {isEditing ? (
                                                            <Input
                                                                className="h-7 text-sm"
                                                                value={editingLine.description}
                                                                onChange={(e) =>
                                                                    setEditingLine({
                                                                        ...editingLine,
                                                                        description: e.target.value,
                                                                    })
                                                                }
                                                            />
                                                        ) : (
                                                            <>
                                                                {stripHtml(line.description)}
                                                                {/* Labor line: the crew / hours stepper (#1823 S3). */}
                                                                {!["paid", "void"].includes(inv.status) &&
                                                                    laborOf(line) && (
                                                                        <LaborCrewStepper
                                                                            className="mt-1.5"
                                                                            labor={laborOf(line)!}
                                                                            onChange={(next) =>
                                                                                onPatchLine(inv.id, line.id, next)
                                                                            }
                                                                        />
                                                                    )}
                                                            </>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-1.5 text-xs text-foreground/80">
                                                        {line.pricebook_item_name ?? "—"}
                                                    </td>
                                                    <td className="px-3 py-1.5 text-right">
                                                        {isEditing ? (
                                                            <Input
                                                                type="number"
                                                                className="h-7 text-sm w-14 text-right ml-auto"
                                                                value={editingLine.qty}
                                                                onChange={(e) =>
                                                                    setEditingLine({
                                                                        ...editingLine,
                                                                        qty: e.target.value,
                                                                    })
                                                                }
                                                            />
                                                        ) : (
                                                            Number(line.qty)
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-1.5 text-right">
                                                        {isEditing ? (
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                className="h-7 text-sm w-20 text-right ml-auto"
                                                                value={editingLine.unit_price}
                                                                onChange={(e) =>
                                                                    setEditingLine({
                                                                        ...editingLine,
                                                                        unit_price: e.target.value,
                                                                    })
                                                                }
                                                            />
                                                        ) : (
                                                            `$${Number(line.unit_price).toFixed(2)}`
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-1.5 text-right font-medium">
                                                        ${Number(line.line_total).toFixed(2)}
                                                    </td>
                                                    {!["paid", "void"].includes(inv.status) && (
                                                        <td className="px-2 py-1.5">
                                                            {isEditing ? (
                                                                <div className="flex gap-1">
                                                                    <button
                                                                        className="p-0.5 hover:text-green-600"
                                                                        onClick={() => {
                                                                            onPatchLine(inv.id, line.id, {
                                                                                description: editingLine.description,
                                                                                qty: Number(editingLine.qty),
                                                                                unit_price: Number(
                                                                                    editingLine.unit_price,
                                                                                ),
                                                                            });
                                                                            setEditingLine(null);
                                                                        }}
                                                                    >
                                                                        <Check className="h-3.5 w-3.5" />
                                                                    </button>
                                                                    <button
                                                                        className="p-0.5 hover:text-foreground/80"
                                                                        onClick={() => setEditingLine(null)}
                                                                    >
                                                                        <X className="h-3.5 w-3.5" />
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div className="flex gap-1">
                                                                    <button
                                                                        className="p-0.5 hover:text-primary"
                                                                        onClick={() =>
                                                                            setEditingLine({
                                                                                invoiceId: inv.id,
                                                                                id: line.id,
                                                                                description: line.description,
                                                                                qty: String(line.qty),
                                                                                unit_price: String(line.unit_price),
                                                                            })
                                                                        }
                                                                    >
                                                                        <Pencil className="h-3.5 w-3.5" />
                                                                    </button>
                                                                    <button
                                                                        className="p-0.5 hover:text-destructive"
                                                                        onClick={async () => {
                                                                            if (
                                                                                await confirmDialog({
                                                                                    message: "Remove this line?",
                                                                                    destructive: true,
                                                                                    confirmLabel: "Remove",
                                                                                })
                                                                            )
                                                                                onDeleteLine(inv.id, line.id);
                                                                        }}
                                                                    >
                                                                        <Trash2 className="h-3.5 w-3.5" />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })}

            <Dialog open={!!paymentDialog} onClose={() => setPaymentDialog(null)} title="Record Payment">
                <div className="space-y-3">
                    <div>
                        <Label>Payment Method</Label>
                        <Select
                            value={paymentForm.method}
                            onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                        >
                            {["Cash", "Check", "ACH", "Zelle", "Credit Card", "Other"].map((m) => (
                                <option key={m} value={m}>
                                    {m}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <Label>
                            Reference <span className="text-foreground/80 font-normal">(optional)</span>
                        </Label>
                        <Input
                            placeholder="Check #, transaction ID, etc."
                            value={paymentForm.reference}
                            onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label>
                            Amount{" "}
                            <span className="text-foreground/80 font-normal">
                                (defaults to {paymentDialog ? formatCurrency(paymentDialog.totalAmount) : ""})
                            </span>
                        </Label>
                        <Input
                            type="number"
                            step="0.01"
                            placeholder={paymentDialog ? String(paymentDialog.totalAmount) : ""}
                            value={paymentForm.amount}
                            onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={() => setPaymentDialog(null)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (!paymentDialog) return;
                            onRecordPayment(paymentDialog.invoiceId, {
                                method: paymentForm.method,
                                reference: paymentForm.reference || undefined,
                                amount: paymentForm.amount ? Number(paymentForm.amount) : undefined,
                            });
                            setPaymentDialog(null);
                        }}
                        disabled={isRecordingPayment}
                    >
                        {isRecordingPayment ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                        Record Payment
                    </Button>
                </div>
            </Dialog>

            <DocumentPreviewDialog
                open={!!previewInvoice}
                onClose={() => setPreviewInvoice(null)}
                kind="invoice"
                documentId={previewInvoice?.id ?? null}
                label={previewInvoice?.label ?? null}
                suggestedFilename={previewInvoice ? `invoice-${previewInvoice.label}.pdf` : null}
            />
        </section>
    );
}

// ─── Estimate Section ─────────────────────────────────────────────────
function EstimateSection({
    estimates,
    onSend,
    isSending,
    onPatchLine,
    onDeleteLine,
    onConvert,
    isConverting,
    onCreateRepairJob,
    isCreatingRepairJob,
    onCreateEstimate,
}: {
    estimates: Estimate[];
    onSend: (id: string) => void;
    isSending: boolean;
    onPatchLine: (
        estimateId: string,
        lineId: string,
        body: { description?: string; qty?: number; unit_price?: number; labor_hours?: number; labor_techs?: number },
    ) => void;
    onDeleteLine: (estimateId: string, lineId: string) => void;
    onConvert: (id: string) => void;
    isConverting: boolean;
    onCreateRepairJob: (id: string, workOrderNumber?: string) => void;
    isCreatingRepairJob: boolean;
    onCreateEstimate: () => void;
}) {
    const [editingLine, setEditingLine] = useState<{
        estimateId: string;
        id: string;
        description: string;
        qty: string;
        unit_price: string;
    } | null>(null);
    const [previewEstimate, setPreviewEstimate] = useState<{ id: string; label: string } | null>(null);
    // Optional per-estimate WO/PO # captured at repair-job creation (Issue #634).
    const [repairWoRef, setRepairWoRef] = useState<Record<string, string>>({});

    return (
        <section id="office-review-estimate-section" className="space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-1.5 text-foreground">
                <FileEdit className="h-4 w-4" /> Estimates ({estimates.length})
            </h3>
            {estimates.map((est) => (
                <div key={est.id} className="rounded-lg border">
                    <div className="flex items-center justify-between border-b bg-muted px-4 py-2.5">
                        <div>
                            <h4 className="text-sm font-semibold">{est.title}</h4>
                            <div className="flex items-center gap-2 mt-0.5">
                                <Badge variant="outline" className={cn("text-[10px]", statusColor(est.status))}>
                                    {est.status}
                                </Badge>
                                {est.status === "sent" && (
                                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/60 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
                                        <Mail className="h-3 w-3" /> Awaiting customer approval
                                    </span>
                                )}
                                <span className="text-base font-bold">${Number(est.subtotal).toFixed(2)}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <a
                                href={`/commercial/${est.id}?type=estimate`}
                                className="flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                                Edit <ArrowRight className="h-3 w-3" />
                            </a>
                            <button
                                onClick={() =>
                                    setPreviewEstimate({
                                        id: est.id,
                                        label: est.title ?? recordLabel("Estimate"),
                                    })
                                }
                                className="flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-medium hover:bg-accent"
                                title="Preview & print PDF"
                            >
                                <Printer className="h-3 w-3" /> Preview
                            </button>
                            {/*
                             * Convert-to-invoice button is intentionally
                             * scoped to estimates the customer has already
                             * approved. Per PRE-CUTOVER-UX-FEEDBACK.md #34
                             * the owner doesn't want this affordance
                             * present on draft / sent estimates. Don't
                             * relax this gate without re-confirming.
                             */}
                            {est.status === "accepted" && (
                                <button
                                    onClick={() => onConvert(est.id)}
                                    disabled={isConverting}
                                    title="Convert this approved estimate into an invoice"
                                    className="flex items-center gap-1 rounded-lg bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                                >
                                    {isConverting ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                        <Receipt className="h-3 w-3" />
                                    )}{" "}
                                    Invoice
                                </button>
                            )}
                            {est.status === "draft" && !isRepairEstimate(est) && (
                                <button
                                    onClick={() => onSend(est.id)}
                                    disabled={isSending}
                                    className="flex items-center gap-1 rounded-lg bg-purple-600 px-2 py-1 text-xs font-medium text-white hover:bg-purple-700 disabled:opacity-50"
                                >
                                    {isSending ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                        <Mail className="h-3 w-3" />
                                    )}{" "}
                                    Send
                                </button>
                            )}
                        </div>
                    </div>
                    {/* Problem 2: repair estimates get a status-driven CTA so the
                        office is guided through send → approve → schedule without
                        leaving office review. Non-repair estimates are untouched. */}
                    {isRepairEstimate(est) && (
                        <div className="border-b px-4 py-3">
                            {est.status === "draft" && (
                                <div className="space-y-1.5">
                                    <Button
                                        size="sm"
                                        className="bg-purple-600 hover:bg-purple-700 text-white"
                                        disabled={isSending}
                                        onClick={() => onSend(est.id)}
                                    >
                                        {isSending ? (
                                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                        ) : (
                                            <Mail className="h-3.5 w-3.5 mr-1" />
                                        )}
                                        Send Repair Estimate to Customer
                                    </Button>
                                    <p className="text-xs text-foreground/70">
                                        Sending this estimate gives the customer a price for the repair work. Once they
                                        approve it, you can schedule the repair job.
                                    </p>
                                </div>
                            )}
                            {est.status === "sent" && (
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/60 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
                                        <Mail className="h-3.5 w-3.5" /> Repair estimate sent — awaiting customer
                                        approval
                                    </span>
                                    <a
                                        href={`/commercial/${est.id}?type=estimate`}
                                        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                                    >
                                        View estimate <ArrowRight className="h-3 w-3" />
                                    </a>
                                </div>
                            )}
                            {est.status === "accepted" && (
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/60 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200">
                                        <CheckCircle className="h-3.5 w-3.5" /> Customer approved the repair estimate
                                    </span>
                                    {/* Optional WO/PO # (Issue #634): recorded on the repair job so a
                                        later scan of the physical work order links here instead of
                                        creating a duplicate. Leave blank to create as before. */}
                                    <Input
                                        value={repairWoRef[est.id] ?? ""}
                                        onChange={(e) =>
                                            setRepairWoRef((prev) => ({ ...prev, [est.id]: e.target.value }))
                                        }
                                        placeholder="WO / PO # (optional)"
                                        className="h-8 w-40 text-xs"
                                        aria-label="Work order or PO number (optional)"
                                    />
                                    <Button
                                        size="sm"
                                        className="bg-green-700 hover:bg-green-800 text-white"
                                        disabled={isCreatingRepairJob}
                                        onClick={() => onCreateRepairJob(est.id, repairWoRef[est.id])}
                                    >
                                        {isCreatingRepairJob ? (
                                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                        ) : (
                                            <Wrench className="h-3.5 w-3.5 mr-1" />
                                        )}
                                        Create Repair Job
                                        <ArrowRight className="h-3.5 w-3.5 ml-1" />
                                    </Button>
                                </div>
                            )}
                            {est.status === "declined" && (
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-medium text-foreground/70">
                                        <Ban className="h-3.5 w-3.5" /> Customer declined the repair estimate
                                    </span>
                                    <Button size="sm" variant="outline" onClick={onCreateEstimate}>
                                        <FileEdit className="h-3.5 w-3.5 mr-1" /> Create new estimate
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                    {est.lines && est.lines.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-left text-xs font-semibold text-foreground">
                                    <tr>
                                        {/* min-width keeps long descriptions wrapping at
                                        phrase width instead of one word per row. */}
                                        <th className="px-3 py-1.5 min-w-[240px]">Description</th>
                                        <th className="px-3 py-1.5">Pricebook Item</th>
                                        <th className="px-3 py-1.5 text-right">Qty</th>
                                        <th className="px-3 py-1.5 text-right">Price</th>
                                        <th className="px-3 py-1.5 text-right">Total</th>
                                        {est.status === "draft" && <th className="px-2 py-1.5 w-14" />}
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {est.lines.map((line) => {
                                        const isEditing =
                                            editingLine?.id === line.id && editingLine?.estimateId === est.id;
                                        return (
                                            <tr key={line.id}>
                                                <td
                                                    className="px-3 py-1.5 min-w-[240px]"
                                                    title={stripHtml(line.description)}
                                                >
                                                    {isEditing ? (
                                                        <Input
                                                            className="h-7 text-sm"
                                                            value={editingLine.description}
                                                            onChange={(e) =>
                                                                setEditingLine({
                                                                    ...editingLine,
                                                                    description: e.target.value,
                                                                })
                                                            }
                                                        />
                                                    ) : (
                                                        <>
                                                            {stripHtml(line.description)}
                                                            {est.status === "draft" && laborOf(line) && (
                                                                <LaborCrewStepper
                                                                    className="mt-1.5"
                                                                    labor={laborOf(line)!}
                                                                    onChange={(next) =>
                                                                        onPatchLine(est.id, line.id, next)
                                                                    }
                                                                />
                                                            )}
                                                        </>
                                                    )}
                                                </td>
                                                <td className="px-3 py-1.5 text-xs text-foreground/80">
                                                    {line.pricebook_item_name ?? "—"}
                                                </td>
                                                <td className="px-3 py-1.5 text-right">
                                                    {isEditing ? (
                                                        <Input
                                                            type="number"
                                                            className="h-7 text-sm w-14 text-right ml-auto"
                                                            value={editingLine.qty}
                                                            onChange={(e) =>
                                                                setEditingLine({ ...editingLine, qty: e.target.value })
                                                            }
                                                        />
                                                    ) : (
                                                        Number(line.qty)
                                                    )}
                                                </td>
                                                <td className="px-3 py-1.5 text-right">
                                                    {isEditing ? (
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            className="h-7 text-sm w-20 text-right ml-auto"
                                                            value={editingLine.unit_price}
                                                            onChange={(e) =>
                                                                setEditingLine({
                                                                    ...editingLine,
                                                                    unit_price: e.target.value,
                                                                })
                                                            }
                                                        />
                                                    ) : (
                                                        `$${Number(line.unit_price).toFixed(2)}`
                                                    )}
                                                </td>
                                                <td className="px-3 py-1.5 text-right font-medium">
                                                    ${Number(line.line_total).toFixed(2)}
                                                </td>
                                                {est.status === "draft" && (
                                                    <td className="px-2 py-1.5">
                                                        {isEditing ? (
                                                            <div className="flex gap-1">
                                                                <button
                                                                    className="p-0.5 hover:text-green-600"
                                                                    onClick={() => {
                                                                        onPatchLine(est.id, line.id, {
                                                                            description: editingLine.description,
                                                                            qty: Number(editingLine.qty),
                                                                            unit_price: Number(editingLine.unit_price),
                                                                        });
                                                                        setEditingLine(null);
                                                                    }}
                                                                >
                                                                    <Check className="h-3.5 w-3.5" />
                                                                </button>
                                                                <button
                                                                    className="p-0.5 hover:text-foreground/80"
                                                                    onClick={() => setEditingLine(null)}
                                                                >
                                                                    <X className="h-3.5 w-3.5" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex gap-1">
                                                                <button
                                                                    className="p-0.5 hover:text-primary"
                                                                    onClick={() =>
                                                                        setEditingLine({
                                                                            estimateId: est.id,
                                                                            id: line.id,
                                                                            description: line.description,
                                                                            qty: String(line.qty),
                                                                            unit_price: String(line.unit_price),
                                                                        })
                                                                    }
                                                                >
                                                                    <Pencil className="h-3.5 w-3.5" />
                                                                </button>
                                                                <button
                                                                    className="p-0.5 hover:text-destructive"
                                                                    onClick={async () => {
                                                                        if (
                                                                            await confirmDialog({
                                                                                message: "Remove this line?",
                                                                                destructive: true,
                                                                                confirmLabel: "Remove",
                                                                            })
                                                                        )
                                                                            onDeleteLine(est.id, line.id);
                                                                    }}
                                                                >
                                                                    <Trash2 className="h-3.5 w-3.5" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            ))}

            <DocumentPreviewDialog
                open={!!previewEstimate}
                onClose={() => setPreviewEstimate(null)}
                kind="estimate"
                documentId={previewEstimate?.id ?? null}
                label={previewEstimate?.label ?? null}
                suggestedFilename={previewEstimate ? `estimate-${previewEstimate.label}.pdf` : null}
            />
        </section>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Office Review — Replacements section
//
// Mirrors the JobReplacementsPanel on the Job Detail page but slimmed
// for the office review layout. Groups main + bypass rows under the
// parent's old_asset_id so the office sees one Old → New block per
// physical assembly even on detector swaps.
// ──────────────────────────────────────────────────────────────────────────
function OfficeReplacementsSection({ rows }: { rows: ReviewReplacementRow[] }) {
    type Group = { main: ReviewReplacementRow | null; bypass: ReviewReplacementRow | null };
    const groups = new Map<string, Group>();
    for (const r of rows) {
        if (r.pair_role === "main") {
            const g = groups.get(r.old_asset_id) ?? { main: null, bypass: null };
            g.main = r;
            groups.set(r.old_asset_id, g);
        }
    }
    for (const r of rows) {
        if (r.pair_role === "bypass") {
            const parentKey = r.parent_old_asset_id ?? r.old_asset_id;
            const g = groups.get(parentKey) ?? { main: null, bypass: null };
            g.bypass = r;
            groups.set(parentKey, g);
        }
    }
    const ordered = Array.from(groups.values()).sort((a, b) =>
        ((a.main ?? a.bypass)?.created_at ?? "").localeCompare((b.main ?? b.bypass)?.created_at ?? ""),
    );

    return (
        <section>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-foreground">
                <RotateCcw className="h-4 w-4" /> Replacements ({ordered.length})
            </h3>
            <div className="space-y-3">
                {ordered.map((g, idx) => (
                    <OfficeReplacementGroupCard key={(g.main ?? g.bypass)?.id ?? idx} main={g.main} bypass={g.bypass} />
                ))}
            </div>
        </section>
    );
}

function OfficeReplacementGroupCard({
    main,
    bypass,
}: {
    main: ReviewReplacementRow | null;
    bypass: ReviewReplacementRow | null;
}) {
    const primary = main ?? bypass;
    const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
    if (!primary) return null;
    const photos = main?.prework_photos ?? bypass?.prework_photos ?? [];
    const notes = main?.prework_notes ?? bypass?.prework_notes ?? null;
    const bypassMissing = !!main?.old_bypass_missing;
    const finalizedAt = main?.finalized_at ?? bypass?.finalized_at ?? null;
    const performedByName = main?.performed_by_name ?? bypass?.performed_by_name ?? null;

    return (
        <div className="rounded-md border bg-muted/30 p-3 space-y-3">
            {main && <OfficeReplacementPairRow row={main} />}
            {bypass && <OfficeReplacementPairRow row={bypass} />}
            {photos.length > 0 && (
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/60 mb-1.5">
                        Pre-work photos ({photos.length})
                    </p>
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
                        {photos.map((src, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => setLightboxSrc(src)}
                                className="block aspect-square overflow-hidden rounded border bg-muted"
                            >
                                <img src={src} alt={`Pre-work ${i + 1}`} className="h-full w-full object-cover" />
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {notes && (
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/60 mb-1">
                        Replacement notes
                    </p>
                    <p className="text-xs whitespace-pre-wrap">{notes}</p>
                </div>
            )}
            {bypassMissing && (
                <div className="rounded border border-warning/40 bg-warning/10 p-2 text-[11px] text-warning-foreground flex items-start gap-1.5">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>Old bypass asset not found at prework time — verify replacement history.</span>
                </div>
            )}
            <div className="text-[11px] text-foreground/60 flex items-center gap-2 flex-wrap">
                {finalizedAt && <span>Replaced on {new Date(finalizedAt).toLocaleString()}</span>}
                {performedByName && <span>by {performedByName}</span>}
            </div>
            {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
        </div>
    );
}

function OfficeReplacementPairRow({ row }: { row: ReviewReplacementRow }) {
    const finalized = !!row.finalized_at;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-0.5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/60">
                    Old {row.pair_role === "bypass" ? "Bypass" : "Main"}
                    {row.old_assembly_type && (
                        <span className="ml-1 text-muted-foreground">· {row.old_assembly_type.toUpperCase()}</span>
                    )}
                </p>
                <p className="text-sm font-medium">{row.old_name ?? "—"}</p>
                {row.old_serial && <p className="text-xs text-foreground/80">SN {row.old_serial}</p>}
                {row.old_model && <p className="text-xs text-foreground/60">{row.old_model}</p>}
            </div>
            <div className="space-y-0.5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/60 flex items-center gap-1.5">
                    New {row.pair_role === "bypass" ? "Bypass" : "Main"}
                    {row.new_assembly_type && (
                        <span className="text-muted-foreground">· {row.new_assembly_type.toUpperCase()}</span>
                    )}
                    {finalized ? (
                        <Badge variant="success" className="text-[9px] px-1 py-0">
                            Finalized
                        </Badge>
                    ) : (
                        <Badge variant="secondary" className="text-[9px] px-1 py-0">
                            Pending
                        </Badge>
                    )}
                </p>
                <p className="text-sm font-medium">{row.new_name ?? "—"}</p>
                {row.new_serial && <p className="text-xs text-foreground/80">SN {row.new_serial}</p>}
                {row.new_model && <p className="text-xs text-foreground/60">{row.new_model}</p>}
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Office Review — Devices Not Tested
//
// Surfaces three flavors of "scheduled work that didn't get a saved test"
// so dispatch can audit and unblock:
//
//   • Skipped — tech released a gating row with a reason (`disposition`
//     column). Includes who/when/why so office can validate.
//   • Not due — same as skipped but the tech tagged the reason as
//     "device fine, just not on the cycle yet".
//   • Bonus — tech-added (added_by='tech') with no test recorded. Not a
//     gate violation but useful for follow-up.
//
// Office has a Restore button on skipped rows that POSTs disposition=null
// to flip the row back to required-untested (mostly for cases where
// dispatch wants the tech to revisit before invoicing).
// ──────────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────────
// TechAddedReviewSection
//
// Devices the tech added on-site (job_assets.added_by='tech') start
// 'pending': drafted on the invoice/estimate but holding both (#1958), and
// out of payroll, until the office rules. Approve pulls a device into billing + reinstates its
// points; deny voids the points and leaves it off the invoice (the test
// row + the site asset are kept on file). The asset always stays on the
// location record regardless.
// ──────────────────────────────────────────────────────────────────────────
function TechAddedReviewSection({
    assets,
    onDecide,
    isPending,
}: {
    assets: Asset[];
    onDecide: (linkId: string, decision: "approved" | "denied" | "pending", note?: string) => void;
    isPending: boolean;
}) {
    const techAdded = assets.filter((a) => a.added_by === "tech" && a.link_id);
    if (techAdded.length === 0) return null;
    const pendingCount = techAdded.filter((a) => (a.office_review_decision ?? "pending") === "pending").length;

    const decisionBadge = (decision: string) => {
        if (decision === "approved")
            return (
                <Badge className="text-[10px] bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                    Approved
                </Badge>
            );
        if (decision === "denied")
            return (
                <Badge className="text-[10px] bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">
                    Denied
                </Badge>
            );
        return (
            <Badge className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                Pending
            </Badge>
        );
    };

    return (
        <section>
            <h3 className="text-sm font-semibold mb-1 flex items-center gap-1.5 text-foreground">
                <Flag className="h-4 w-4 text-amber-600" /> Tech-Added Devices ({techAdded.length})
                {pendingCount > 0 && (
                    <Badge className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                        {pendingCount} pending
                    </Badge>
                )}
            </h3>
            {pendingCount > 0 && (
                <p
                    className="mb-1 text-sm font-bold text-amber-800 dark:text-amber-200"
                    data-testid="tech-added-needs-ok"
                >
                    {pendingCount === 1
                        ? "1 device the tech added needs your OK"
                        : `${pendingCount} devices the tech added need your OK`}
                </p>
            )}
            <p className="text-xs text-foreground/70 mb-2">
                Added on-site by the tech. It&apos;s already on the draft quote and invoice, and they can&apos;t go out
                until you decide. Approve to keep it and pay points; deny to take it off (kept on file for the
                location).
            </p>
            <div className="space-y-2">
                {techAdded.map((a) => {
                    const decision = a.office_review_decision ?? "pending";
                    const tested = !!a.test_id;
                    const passed = a.overall_result === "pass";
                    return (
                        <div
                            key={a.link_id}
                            className={cn(
                                "rounded-lg border p-3 text-xs space-y-2",
                                decision === "pending"
                                    ? "border-amber-300 bg-amber-50/60 dark:border-amber-600/40 dark:bg-amber-900/10"
                                    : "border-border/70 bg-card",
                            )}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                    <p className="font-medium text-sm truncate">
                                        {[a.size_label, a.manufacturer, a.model].filter(Boolean).join(" ") ||
                                            a.name ||
                                            "Unnamed device"}
                                    </p>
                                    <p className="text-foreground/70">
                                        {[
                                            a.assembly_type?.toUpperCase(),
                                            a.serial_number ? `S/N ${a.serial_number}` : null,
                                        ]
                                            .filter(Boolean)
                                            .join(" · ")}
                                    </p>
                                </div>
                                <div className="flex flex-col items-end gap-1.5 shrink-0">
                                    {decisionBadge(decision)}
                                    {tested ? (
                                        <Badge variant={passed ? "success" : "destructive"} className="text-[10px]">
                                            {passed ? "Test passed" : "Test failed"}
                                        </Badge>
                                    ) : (
                                        <Badge variant="outline" className="text-[10px]">
                                            Untested
                                        </Badge>
                                    )}
                                </div>
                            </div>
                            {/* A failed test the office approves for filing is a
                                legitimate state — spell it out so the Approved +
                                Test failed badges don't read as contradictory. */}
                            {decision === "approved" && tested && !passed && (
                                <p className="text-foreground/70">Failed test approved for filing.</p>
                            )}
                            {a.decision_note && <p className="text-foreground/70 italic">"{a.decision_note}"</p>}
                            {decision === "pending" ? (
                                <div className="flex gap-2 pt-0.5">
                                    <Button
                                        size="sm"
                                        variant="success"
                                        disabled={isPending}
                                        className="h-7 text-xs"
                                        onClick={() => a.link_id && onDecide(a.link_id, "approved")}
                                    >
                                        <Check className="h-3.5 w-3.5 mr-1" /> Approve
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        disabled={isPending}
                                        className="h-7 text-xs"
                                        onClick={async () => {
                                            if (!a.link_id) return;
                                            const note =
                                                (await promptDialog("Reason for denying this device (optional):")) ??
                                                undefined;
                                            onDecide(a.link_id, "denied", note || undefined);
                                        }}
                                    >
                                        <X className="h-3.5 w-3.5 mr-1" /> Deny
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between gap-2 pt-0.5">
                                    <span className="text-foreground/70">
                                        {decision === "approved"
                                            ? "Approved — included on invoice/estimate."
                                            : "Denied — left off the invoice."}
                                    </span>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={isPending}
                                        className="h-7 text-xs"
                                        onClick={() => a.link_id && onDecide(a.link_id, "pending")}
                                    >
                                        <RotateCcw className="h-3.5 w-3.5 mr-1" /> Undo
                                    </Button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

/**
 * Surfaces tests whose device was removed from the job after it was tested
 * (the job_assets link was deleted but the backflow_test_results row persists).
 * These never appear in the Tech-Added approval list, so without this they'd be
 * silently dropped. We show them explicitly with a "test data excluded" note so
 * staff can re-link the device if the removal was a mistake.
 */
function RemovedDevicesSection({
    assets,
    testResults,
    jobId,
    onRelink,
    onVoid,
    isRelinking,
    isVoiding,
}: {
    assets: Asset[];
    testResults: TestResult[];
    jobId: string;
    onRelink: (testId: string) => void;
    onVoid: (testId: string) => void;
    isRelinking: boolean;
    isVoiding: boolean;
}) {
    void jobId; // handlers close over the selected job id
    const liveAssetIds = new Set(assets.map((a) => a.id));
    const orphans = testResults.filter((t) => t.asset_id && !liveAssetIds.has(t.asset_id));
    if (orphans.length === 0) return null;

    return (
        <section>
            <h3 className="text-sm font-semibold mb-1 flex items-center gap-1.5 text-foreground">
                <AlertTriangle className="h-4 w-4 text-amber-600" /> Removed Devices ({orphans.length})
            </h3>
            <p className="text-xs text-foreground/70 mb-2">
                These test rows aren't linked to any device on the job, so their data is excluded from filing and
                approval — but an unlinked row can still hold up closeout. If it's a real device removed by mistake,{" "}
                <strong>re-link</strong> it to bring its test back into review. If it's a stray or mis-scanned row for a
                device that isn't real, <strong>delete</strong> it so the job can clear.
            </p>
            <div className="space-y-2">
                {orphans.map((t) => {
                    const passed = t.overall_result === "pass";
                    const failed = t.overall_result === "fail";
                    const action = t.orphanAction ?? null;
                    return (
                        <div
                            key={t.id}
                            className="rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-xs space-y-2 dark:border-amber-600/40 dark:bg-amber-900/10"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                    <p className="font-medium text-sm truncate">
                                        {[t.size_label, t.manufacturer, t.model].filter(Boolean).join(" ") ||
                                            t.asset_name ||
                                            "Unnamed device"}
                                    </p>
                                    <p className="text-foreground/70">
                                        {[
                                            t.assembly_type?.toUpperCase(),
                                            t.serial_number ? `S/N ${t.serial_number}` : null,
                                        ]
                                            .filter(Boolean)
                                            .join(" · ")}
                                    </p>
                                </div>
                                <Badge
                                    variant={passed ? "success" : failed ? "destructive" : "outline"}
                                    className="text-[10px] shrink-0"
                                >
                                    {passed ? "Test passed" : failed ? "Test failed" : "Incomplete"}
                                </Badge>
                            </div>
                            {action === "relink" ? (
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-amber-800 dark:text-amber-200">
                                        Real device on this site — its link vanished. Re-link to finish testing.
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 text-[11px] shrink-0"
                                        disabled={isRelinking}
                                        onClick={async () => {
                                            const ok = await confirmDialog({
                                                message: `Re-link ${t.serial_number ? `S/N ${t.serial_number}` : "this device"} to the job? Its test goes back into review.`,
                                                confirmLabel: "Re-link",
                                            });
                                            if (ok) onRelink(t.id);
                                        }}
                                    >
                                        <RotateCcw className="h-3 w-3 mr-1" /> Re-link to job
                                    </Button>
                                </div>
                            ) : action === "void" ? (
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-amber-800 dark:text-amber-200">
                                        Stray/mis-scanned row — matches no real device. Safe to delete.
                                    </p>
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        className="h-7 text-[11px] shrink-0"
                                        disabled={isVoiding}
                                        onClick={async () => {
                                            const ok = await confirmDialog({
                                                message: `Delete this stray test row${t.serial_number ? ` (S/N ${t.serial_number})` : ""}? This can't be undone. Only do this for a garbage/mis-scanned row, not a real device.`,
                                                destructive: true,
                                                confirmLabel: "Delete stray",
                                            });
                                            if (ok) onVoid(t.id);
                                        }}
                                    >
                                        <Trash2 className="h-3 w-3 mr-1" /> Delete stray
                                    </Button>
                                </div>
                            ) : action === "other_site" && t.other_site_id ? (
                                <p className="text-amber-800 dark:text-amber-200">
                                    Test for a device at another site —{" "}
                                    <Link to={`/sites/${t.other_site_id}`} className="font-medium underline">
                                        {t.other_site_name || "open that site"}
                                    </Link>
                                    . It doesn't hold this job's paperwork.
                                </p>
                            ) : (
                                <p className="text-amber-800 dark:text-amber-200">
                                    Device removed — test data excluded.
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function OfficeNotTestedSection({
    jobId,
    rows,
    onRestore,
    isRestoring,
    onResolveRemoval,
    isResolvingRemoval,
}: {
    jobId: string;
    rows: ReviewDeviceRow[];
    onRestore: (linkId: string) => void;
    isRestoring: boolean;
    onResolveRemoval: (assetId: string, action: "confirm" | "reject") => void;
    isResolvingRemoval: boolean;
}) {
    void jobId;
    // We only care about in-scope rows (linked via job_assets).
    const inScope = rows.filter((r) => r.is_in_scope !== false);
    const skipped = inScope.filter((r) => !!r.disposition);
    // Bonus / tech-added rows the tech never tested. The session
    // doesn't carry a saved test row — `last_test` will be null OR its
    // performed_at predates this job. We keep the heuristic simple:
    // surface tech-added rows with no last_test or whose status is
    // still 'active' (not 'replaced') at the time of review.
    // Bypass children are tested through their parent's single test row, so a
    // child whose parent has a test must not count as "not tested" even if its
    // own last_test is somehow absent.
    const parentIdsWithTest = new Set(rows.filter((r) => r.last_test).map((r) => r.id));
    const bonusUntested = inScope.filter(
        (r) =>
            r.added_by === "tech" &&
            r.gate_required === false &&
            !r.disposition &&
            !r.last_test &&
            !(r.parent_asset_id && parentIdsWithTest.has(r.parent_asset_id)),
    );
    if (skipped.length === 0 && bonusUntested.length === 0) return null;

    const fmtDate = (iso: string | null) => {
        if (!iso) return "";
        try {
            return new Date(iso).toLocaleString();
        } catch {
            return "";
        }
    };

    return (
        <section>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-foreground">
                <PauseCircle className="h-4 w-4" /> Devices Not Tested ({skipped.length + bonusUntested.length})
            </h3>
            <div className="space-y-2">
                {skipped.map((r) => (
                    <div key={r.id} className="rounded-lg border border-border/70 bg-card p-3 text-xs space-y-1.5">
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                                <p className="font-medium text-sm truncate">{r.name ?? "Unnamed device"}</p>
                                <p className="text-foreground/70">
                                    {[r.assembly_type?.toUpperCase(), r.serial_number ? `S/N ${r.serial_number}` : null]
                                        .filter(Boolean)
                                        .join(" · ")}
                                </p>
                            </div>
                            <div className="flex flex-col items-end gap-1.5 shrink-0">
                                <Badge
                                    variant="outline"
                                    className="text-[10px] border-slate-300 dark:border-slate-500/40 bg-slate-100 dark:bg-slate-500/15 text-slate-700 dark:text-slate-200"
                                >
                                    {r.disposition === "not_due"
                                        ? "Not due"
                                        : r.disposition === "not_located"
                                          ? "Can't find it"
                                          : r.disposition === "could_not_test"
                                            ? "Couldn't get to it"
                                            : "Skipped"}
                                </Badge>
                                {r.job_link_id && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 text-[11px]"
                                        disabled={isRestoring}
                                        onClick={() => onRestore(r.job_link_id!)}
                                    >
                                        <RotateCcw className="h-3 w-3 mr-1" /> Restore
                                    </Button>
                                )}
                            </div>
                        </div>
                        {r.disposition_category && r.disposition_category !== "other" && (
                            <p className="text-[11px] font-medium text-foreground/80">
                                {r.disposition_category === "no_access"
                                    ? "No access (keys/locked)"
                                    : r.disposition_category === "manager_unavailable"
                                      ? "Manager unavailable"
                                      : r.disposition_category === "water_shutoff"
                                        ? "Water can't be shut off"
                                        : r.disposition_category === "no_water"
                                          ? "No water to device"
                                          : r.disposition_category === "unsafe"
                                            ? "Unsafe/blocked"
                                            : r.disposition_category === "ran_out_of_time"
                                              ? "Out of time"
                                              : "Device no longer exists"}
                            </p>
                        )}
                        {r.disposition_reason && <p className="italic text-foreground/85">"{r.disposition_reason}"</p>}
                        <p className="text-[11px] text-foreground/65">
                            {[r.disposition_set_by_name, fmtDate(r.disposition_set_at)].filter(Boolean).join(" · ") ||
                                "Skipped on-site"}
                        </p>
                        {r.disposition_category === "device_gone" && r.removal_review_requested_at && (
                            <div className="flex items-center gap-2 pt-1">
                                <span className="text-[11px] text-amber-700 dark:text-amber-300 font-medium">
                                    Removal review — tech says this device no longer exists.
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-[11px]"
                                    disabled={isResolvingRemoval}
                                    onClick={() => onResolveRemoval(r.id, "confirm")}
                                >
                                    Confirm removal
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-[11px]"
                                    disabled={isResolvingRemoval}
                                    onClick={() => onResolveRemoval(r.id, "reject")}
                                >
                                    Keep asset
                                </Button>
                            </div>
                        )}
                    </div>
                ))}
                {bonusUntested.map((r) => (
                    <div key={r.id} className="rounded-lg border border-border/70 bg-card p-3 text-xs space-y-1.5">
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                                <p className="font-medium text-sm truncate">{r.name ?? "Unnamed device"}</p>
                                <p className="text-foreground/70">
                                    {[r.assembly_type?.toUpperCase(), r.serial_number ? `S/N ${r.serial_number}` : null]
                                        .filter(Boolean)
                                        .join(" · ")}
                                </p>
                            </div>
                            <Badge
                                variant="outline"
                                className="text-[10px] border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-200 shrink-0"
                            >
                                Bonus — untested
                            </Badge>
                        </div>
                        <p className="text-[11px] text-foreground/65">
                            Added on-site by tech. Not on the original work order.
                        </p>
                    </div>
                ))}
            </div>
        </section>
    );
}

function PricingStaleBanner({
    anchorDate,
    diff,
    draftInvoiceId,
    onKeep,
    onConvert,
    isConverting,
}: {
    anchorDate?: string | null;
    diff: PricingDiffLine[];
    draftInvoiceId?: string;
    jobId: string;
    onKeep: () => void;
    onConvert: (invoiceId: string) => void;
    isConverting: boolean;
}) {
    const anchorLabel = anchorDate
        ? new Date(`${anchorDate}T12:00:00`).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
          })
        : "job creation";

    return (
        <div className="rounded-lg border border-amber-300/80 bg-amber-50/90 dark:bg-amber-950/30 dark:border-amber-600/50 p-4 space-y-3">
            <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                        Customer pricing has changed since this job was created
                    </p>
                    <p className="text-xs text-amber-800/90 dark:text-amber-200/90 mt-1 leading-relaxed">
                        This draft invoice was priced under the agreement in effect on {anchorLabel}. Sent or paid
                        invoices are never changed automatically.
                    </p>
                </div>
            </div>
            <div className="overflow-x-auto rounded-md border border-amber-200/70 dark:border-amber-700/40">
                <table className="w-full text-xs">
                    <thead className="bg-amber-100/60 dark:bg-amber-900/30">
                        <tr>
                            <th className="text-left px-2 py-1.5 font-semibold">Line</th>
                            <th className="text-right px-2 py-1.5 font-semibold">Billed</th>
                            <th className="text-right px-2 py-1.5 font-semibold">Current</th>
                            <th className="text-right px-2 py-1.5 font-semibold">Δ</th>
                        </tr>
                    </thead>
                    <tbody>
                        {diff.map((row) => (
                            <tr key={row.lineId} className="border-t border-amber-200/50 dark:border-amber-800/40">
                                <td className="px-2 py-1.5 max-w-[200px] truncate">{stripHtml(row.description)}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums">
                                    {formatCurrency(row.storedUnitPrice)}
                                </td>
                                <td className="px-2 py-1.5 text-right tabular-nums">
                                    {formatCurrency(row.currentUnitPrice)}
                                </td>
                                <td
                                    className={cn(
                                        "px-2 py-1.5 text-right tabular-nums font-medium",
                                        row.delta > 0
                                            ? "text-red-700 dark:text-red-300"
                                            : row.delta < 0
                                              ? "text-emerald-700 dark:text-emerald-300"
                                              : "",
                                    )}
                                >
                                    {row.delta > 0 ? "+" : ""}
                                    {formatCurrency(row.delta)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={onKeep}>
                    Keep original pricing
                </Button>
                <Button
                    size="sm"
                    disabled={!draftInvoiceId || isConverting}
                    onClick={() => draftInvoiceId && onConvert(draftInvoiceId)}
                >
                    {isConverting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                    Convert to current pricing
                </Button>
            </div>
        </div>
    );
}
